import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { NoteBlank, CaretLeft, CaretRight, Calendar, Plus, CaretUp, CaretDown, ShoppingCart, X, Tag, MapPin, PencilSimple, Check, WhatsappLogo, EnvelopeSimple, ArrowSquareOut, DownloadSimple, Eye } from 'phosphor-react';
import Header from '../components/Header';
import SearchField from '../components/SearchField';
import { Tabs, TabsList, TabsTrigger } from '../components/Tabs';
import OrderTile from '../components/OrderTile';
import BoardColumn from '../components/BoardColumn';
import OrderCard from '../components/OrderCard';
import { StatusTag, StatusType } from '../components/StatusTag';
import { useApi } from '../hooks/useApi';
import { SearchableDropdown } from '../components/SearchableDropdown';
import { useOrgSocket } from '../hooks/useOrgSocket';
import { mapBackendStatusToFrontend } from '../services/api';
import { Order as ApiOrder, Conversation, Product, OrderItem, KameoneFicha, KameSyncResult } from '../types/api';
import { Skeleton } from '../components/Skeleton';
import { formatRelativeDate, getOrderDate } from '../utils/dateFormatter';
import { CenterPeekModal } from '../components/CenterPeekModal';
import FileDropzone from '../components/FileDropzone';
import { PDFViewer } from '../components/PDFViewer';
import { ExcelViewer } from '../components/ExcelViewer';
import { OrderSummary } from '../components/OrderSummary';
import { Button } from '../components/Button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from '../components/DropdownMenu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/Dialog';
import { BottomSheet, BottomSheetContent, BottomSheetHeader, BottomSheetTitle, BottomSheetFooter, BottomSheetClose } from '../components/BottomSheet';
import { Loader } from '../components/Loader';
import { Input } from '../components/Input';
import { CustomTooltip } from '../components/CustomTooltip';
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/Tooltip';
import { DeleteButton } from '../components/DeleteButton';
import { QuantityWithUnitInput } from '../components/QuantityWithUnitInput';
import type { QuantityUnit } from '../types/api';
import { PriceSourceDot } from '../components/ProductMatchIndicator';
import { PriceOptionsPopover, PriceOptionSource, PriceOption } from '../components/PriceOptionsPopover';
import { PageContainer } from '../components/PageContainer';
import * as XLSX from 'xlsx';

type OrderOrigin = 'manual' | 'pdf' | 'email' | 'whatsapp';

interface Order {
  orderId: string;
  company: string;
  customer: string;
  productCount: number;
  total: string;
  status: StatusType;
  hasUnread: boolean;
  date?: string;
  updatedAt?: string;
  kameoneFolio?: string;
  documentType?: string | null;
  customerRut?: string | null;
  productCodes?: string[];
  origin?: OrderOrigin;
  addressName?: string | null;
  sourceFileName?: string | null;
  documentDate?: string | null;
  /** 'yellow' = ≥1 item from Kame/catalog; 'green' = all from price list; 'blue' = some from OC, none from Kame */
  priceStatus?: 'yellow' | 'green' | 'blue';
}

type LoadingQuote = { type: 'quote'; author: string; quote: string };
type LoadingTip  = { type: 'tip'; text: string };
type LoadingCard = LoadingQuote | LoadingTip;

const ORDER_MODAL_LOADING_QUOTES: LoadingQuote[] = [
  { type: 'quote', author: 'Platón', quote: 'El comienzo es la parte más importante de la obra.' },
  { type: 'quote', author: 'Aristóteles', quote: 'Somos lo que hacemos repetidamente. La excelencia, entonces, no es un acto, sino un hábito.' },
  { type: 'quote', author: 'Lao Tse', quote: 'Un viaje de mil millas comienza con un solo paso.' },
  { type: 'quote', author: 'Buda', quote: 'No hay camino hacia la felicidad: la felicidad es el camino.' },
  { type: 'quote', author: 'Marco Aurelio', quote: 'La felicidad de tu vida depende de la calidad de tus pensamientos.' },
  { type: 'quote', author: 'Johann Wolfgang von Goethe', quote: 'Lo que no se empieza nunca tendrá un final.' },
  { type: 'quote', author: 'Nelson Mandela', quote: 'Siempre parece imposible hasta que se hace.' },
  { type: 'quote', author: 'Mahatma Gandhi', quote: 'Sé el cambio que quieres ver en el mundo.' },
  { type: 'quote', author: 'Malala Yousafzai', quote: 'Un niño, un profesor, un libro y un lápiz pueden cambiar el mundo.' },
  { type: 'quote', author: 'Dolores Huerta', quote: 'Sí se puede.' },
  { type: 'quote', author: 'Miguel de Cervantes', quote: 'El que lee mucho y anda mucho, ve mucho y sabe mucho.' },
  { type: 'quote', author: 'Jorge Luis Borges', quote: 'Siempre imaginé que el Paraíso sería algún tipo de biblioteca.' },
  { type: 'quote', author: 'Pablo Neruda', quote: 'Podrán cortar todas las flores, pero no podrán detener la primavera.' },
  { type: 'quote', author: 'Albert Camus', quote: 'En medio del invierno aprendí que había en mí un verano invencible.' },
  { type: 'quote', author: 'Oscar Wilde', quote: 'Sé tú mismo; todos los demás ya están ocupados.' },
  { type: 'quote', author: 'Mark Twain', quote: 'Los dos días más importantes de tu vida son el día en que naces y el día en que descubres para qué.' },
  { type: 'quote', author: 'Frida Kahlo', quote: 'Pies, ¿para qué los quiero si tengo alas para volar?' },
  { type: 'quote', author: 'Marie Curie', quote: 'Nada en la vida debe ser temido, solo comprendido.' },
  { type: 'quote', author: 'Ada Lovelace', quote: 'La imaginación es la facultad del descubrimiento.' },
  { type: 'quote', author: 'Albert Einstein', quote: 'La imaginación es más importante que el conocimiento.' },
  { type: 'quote', author: 'Carl Sagan', quote: 'Somos una forma de que el cosmos se conozca a sí mismo.' },
  { type: 'quote', author: 'Stephen Hawking', quote: 'La inteligencia es la capacidad de adaptarse al cambio.' },
  { type: 'quote', author: 'Jane Goodall', quote: 'Lo que haces marca una diferencia, y tienes que decidir qué tipo de diferencia quieres hacer.' },
  { type: 'quote', author: 'Steve Jobs', quote: 'La gente que está lo suficientemente loca como para pensar que puede cambiar el mundo es la que lo cambia.' },
  { type: 'quote', author: 'Coco Chanel', quote: 'Para ser irremplazable, uno debe buscar siempre ser diferente.' },
  { type: 'quote', author: 'Oprah Winfrey', quote: 'Convierte tus heridas en sabiduría.' },
  { type: 'quote', author: 'Richard Branson', quote: 'Las oportunidades de negocio son como los autobuses: siempre viene otro.' },
  { type: 'quote', author: 'Michael Jordan', quote: 'He fallado una y otra vez en mi vida. Por eso he tenido éxito.' },
  { type: 'quote', author: 'Pelé', quote: 'El éxito no es un accidente.' },
  { type: 'quote', author: 'Billie Jean King', quote: 'La presión es un privilegio.' },
  { type: 'quote', author: 'Kobe Bryant', quote: 'Todo lo negativo, la presión y los desafíos son oportunidades para crecer.' },
  { type: 'quote', author: 'Roger Federer', quote: 'No siempre puedes ser el mejor, pero siempre puedes dar lo mejor.' },
  { type: 'quote', author: 'Theodore Roosevelt', quote: 'Haz lo que puedas, con lo que tengas, donde estés.' },
  { type: 'quote', author: 'Barack Obama', quote: 'El cambio no vendrá si esperamos a otra persona o a otro momento.' },
  { type: 'quote', author: 'Václav Havel', quote: 'La esperanza no es la convicción de que algo saldrá bien, sino la certeza de que algo tiene sentido.' },
  { type: 'quote', author: 'Susan B. Anthony', quote: 'La independencia es felicidad.' },
];

const ORDER_MODAL_LOADING_TIPS: LoadingTip[] = [
  { type: 'tip', text: 'Revisa bien la información de cada orden antes de confirmarla.' },
  { type: 'tip', text: 'Recuerda sincronizar productos cuando agregues un nuevo artículo en Kame.' },
  { type: 'tip', text: 'Puedes duplicar una orden si el cliente quiere pedir lo mismo.' },
  { type: 'tip', text: 'Puedes buscar productos por SKU si conoces el código exacto del artículo.' },
  { type: 'tip', text: 'Si el cliente tiene dirección guardada, se autocompletará al seleccionarlo.' },
  { type: 'tip', text: 'Los pedidos con OC adjunta quedan con el número de orden del cliente como referencia.' },
  { type: 'tip', text: 'Puedes editar el folio manualmente si necesitas respetar una numeración específica.' },
  { type: 'tip', text: 'Facturas generadas aparecen directamente en Kameone con el folio asignado.' },
  { type: 'tip', text: 'Ante una duda de cantidad, es mejor consultar al cliente que corregir después.' },
  { type: 'tip', text: 'Presiona A para agregar un producto.' },
  { type: 'tip', text: 'Cuando el precio tenga un punto de color, puedes ver de dónde salió: lista de precios, Kame o documento.' },
  { type: 'tip', text: 'Si un producto no aparece en la lista de precios del cliente, revisa el precio sugerido antes de confirmar.' },
  { type: 'tip', text: 'Puedes cambiar la unidad de cantidad entre unidad, kilos o litros según cómo venga el pedido.' },
  { type: 'tip', text: 'Antes de enviar a Kame, revisa que el tipo de documento sea el correcto: factura o guía de despacho.' },
];

let _tipIndex = 0;

const QUOTE_WEIGHT = 1;
const TIP_WEIGHT = 3.7;

const getRandomLoadingQuote = (): LoadingCard => {
  const total = ORDER_MODAL_LOADING_QUOTES.length * QUOTE_WEIGHT + ORDER_MODAL_LOADING_TIPS.length * TIP_WEIGHT;
  const roll = Math.random() * total;
  if (roll < ORDER_MODAL_LOADING_QUOTES.length * QUOTE_WEIGHT) {
    return ORDER_MODAL_LOADING_QUOTES[Math.floor(Math.random() * ORDER_MODAL_LOADING_QUOTES.length)];
  }
  const tip = ORDER_MODAL_LOADING_TIPS[_tipIndex % ORDER_MODAL_LOADING_TIPS.length];
  _tipIndex++;
  return tip;
};

const parseRateLimitError = (message: string): { isRateLimit: true; retryAt: string } | null => {
  const match = message.match(/Try again after (.+)$/i);
  if (!match) return null;
  const iso = match[1].trim();
  const date = new Date(iso);
  if (isNaN(date.getTime())) return null;
  const retryAt = date.toLocaleTimeString('es-CL', {
    timeZone: 'America/Santiago',
    hour: '2-digit',
    minute: '2-digit',
  });
  return { isRateLimit: true, retryAt };
};

type LineSearchProps = {
  itemId: number;
  searchQ: string;
  results: Product[];
  loadingRow: boolean;
  autoFocus?: boolean;
  onSearchChange: (itemId: number, value: string) => void;
  onSearchClear: (itemId: number) => void;
  onSelectProduct: (itemId: number, product: Product) => void;
  onCreateProduct?: (itemId: number, product: Product) => void;
};

const LineProductSearchResults: React.FC<
  Pick<LineSearchProps, 'itemId' | 'results' | 'loadingRow' | 'onSelectProduct' | 'onCreateProduct' | 'searchQ'>
  & { activeIndex?: number }
> = ({ itemId, results, loadingRow, onSelectProduct, onCreateProduct, searchQ, activeIndex = -1 }) => {
  const api = useApi();
  const [creating, setCreating] = React.useState(false);
  const UNIDADES_DE_NEGOCIO = ['COCINA CALIENTE', 'COCINA FRIA', 'ENSALADAS', 'SANDWICH'] as const;

  const [form, setForm] = React.useState({ name: '', sku: '', price: '', unidadDeNegocio: '' });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Pre-fill name from current search query when form opens
  const openForm = () => {
    setForm({ name: searchQ.trim(), sku: '', price: '', unidadDeNegocio: '' });
    setError(null);
    setCreating(true);
  };

  const handleSave = async () => {
    const price = parseFloat(form.price);
    if (!form.name.trim() || !form.sku.trim() || !price || price <= 0) {
      setError('Nombre, SKU y precio son requeridos');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const created = await api.createProduct(
        { name: form.name.trim().toUpperCase(), sku: form.sku.trim().toLowerCase(), price, unidadDeNegocio: form.unidadDeNegocio || undefined }
      );
      onCreateProduct?.(itemId, created);
      setCreating(false);
    } catch (e: any) {
      setError(e.message || 'Error al crear producto');
    } finally {
      setSaving(false);
    }
  };

  if (loadingRow && results.length === 0) {
    return <p className="p-3 text-sm text-text-subtle text-center">Buscando...</p>;
  }

  if (results.length === 0) {
    if (creating) {
      return (
        <div className="p-3 flex flex-col gap-2">
          <p className="text-xs font-semibold text-text-subtle uppercase tracking-wide">Crear producto</p>
          <input
            autoFocus
            type="text"
            placeholder="Nombre (ej: GOHAN CAMARÓN)"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <input
            type="text"
            placeholder="SKU (ej: prococ354)"
            value={form.sku}
            onChange={e => setForm(f => ({ ...f, sku: e.target.value }))}
            className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <input
            type="number"
            placeholder="Precio (sin IVA)"
            value={form.price}
            onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
            className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <select
            value={form.unidadDeNegocio}
            onChange={e => setForm(f => ({ ...f, unidadDeNegocio: e.target.value }))}
            className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">Unidad de Negocio</option>
            {UNIDADES_DE_NEGOCIO.map(u => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setCreating(false)} className="text-xs text-text-subtle hover:text-text px-2 py-1 rounded transition-colors">Cancelar</button>
            <button type="button" onClick={handleSave} disabled={saving} className="text-xs font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 px-3 py-1 rounded transition-colors">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center py-3 gap-2">
        <p className="text-sm text-text-subtle">Sin resultados</p>
        {onCreateProduct && (
          <button type="button" onClick={openForm} className="flex items-center gap-1 text-xs text-primary hover:underline">
            <Plus size={11} weight="bold" /> Crear producto manualmente
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      {results.map((p, idx) => (
        <button
          key={p.id}
          data-kbd-item
          type="button"
          role="option"
          aria-selected={activeIndex === idx}
          className={`w-full px-3 py-2 text-left border-b border-border last:border-b-0 transition-colors ${activeIndex === idx ? 'bg-muted' : 'hover:bg-muted'}`}
          onMouseDown={(e) => { e.preventDefault(); onSelectProduct(itemId, p); }}
        >
          <p className="text-sm font-medium">{p.name}</p>
          <p className="text-xs text-muted-foreground">
            {p.sku}
          </p>
        </button>
      ))}
    </>
  );
};

const UnassignedLineProductSearch: React.FC<LineSearchProps> = ({
  itemId,
  searchQ,
  results,
  loadingRow,
  autoFocus,
  onSearchChange,
  onSearchClear,
  onSelectProduct,
  onCreateProduct,
}) => {
  const open = searchQ.trim().length > 0;
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const portalDropdownRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  // Reset highlight when results change
  React.useEffect(() => {
    setActiveIndex(-1);
  }, [results]);

  // Scroll active item into view
  React.useEffect(() => {
    if (activeIndex < 0 || !portalDropdownRef.current) return;
    const el = portalDropdownRef.current.querySelectorAll<HTMLElement>('[data-kbd-item]')[activeIndex];
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      // If this component is CSS-hidden (e.g. desktop copy hidden on mobile), ignore
      if (!containerRef.current || containerRef.current.offsetParent === null) return;
      if (containerRef.current.contains(e.target as Node)) return;
      if (portalDropdownRef.current?.contains(e.target as Node)) return;
      onSearchClear(itemId);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, itemId, onSearchClear]);

  const rect = containerRef.current?.getBoundingClientRect();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { e.stopPropagation(); onSearchClear(itemId); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, results.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') {
      setActiveIndex(i => {
        if (i >= 0 && i < results.length) { e.preventDefault(); onSelectProduct(itemId, results[i]); }
        return i;
      });
    }
  };

  return (
    <div ref={containerRef} className="relative w-full min-w-0">
      <Input
        ref={inputRef}
        placeholder="Buscar producto..."
        value={searchQ}
        onChange={(e) => onSearchChange(itemId, e.target.value)}
        onKeyDown={handleKeyDown}
        className="py-1.5"
        data-search-item-id={itemId}
      />
      {open && rect && ReactDOM.createPortal(
        <div
          ref={portalDropdownRef}
          style={{ position: 'fixed', top: rect.bottom + 4, left: rect.left, width: rect.width, zIndex: 9999 }}
          className="max-h-[220px] overflow-y-auto rounded-lg border border-border bg-background shadow-lg"
        >
          <LineProductSearchResults
            itemId={itemId}
            searchQ={searchQ}
            results={results}
            loadingRow={loadingRow}
            onSelectProduct={onSelectProduct}
            onCreateProduct={onCreateProduct}
            activeIndex={activeIndex}
          />
        </div>,
        document.body
      )}
    </div>
  );
};


/** Producto / cantidad / precio inline en el modal "Nuevo pedido" */
const NewOrderProductLineBlock: React.FC<{
  item: OrderItem;
  index: number;
  layout: 'desktop' | 'mobile';
  searchQ: string;
  results: Product[];
  loadingRow: boolean;
  readOnly?: boolean;
  flashPrice?: boolean;
  flashTotal?: boolean;
  onSearchChange: (itemId: number, value: string) => void;
  onSearchClear: (itemId: number) => void;
  onSelectProduct: (itemId: number, product: Product) => void;
  onCreateProduct: (itemId: number, product: Product) => void;
  onClearProduct: (itemId: number) => void;
  onQuantityChange: (itemId: number, raw: string) => void;
  onUnitChange: (itemId: number, unit: QuantityUnit) => void;
  onUnitPriceChange: (itemId: number, value: string) => void;
  onPriceSourceSelect: (itemId: number, price: number, source: PriceOptionSource) => void;
  onDelete: (itemId: number) => void;
  onAddItem?: () => void;
  onDescripcionChange: (itemId: number, value: string) => void;
  onPdfHighlight?: (text: string, page?: number) => void;
  hasError?: boolean;
  saveAttempt?: number;
  priceListId?: number | null;
}> = ({
  item,
  index,
  layout,
  searchQ,
  results,
  loadingRow,
  readOnly = false,
  flashPrice = false,
  flashTotal = false,
  onSearchChange,
  onSearchClear,
  onSelectProduct,
  onCreateProduct,
  onClearProduct,
  onQuantityChange,
  onUnitChange,
  onUnitPriceChange,
  onPriceSourceSelect,
  onDelete,
  onAddItem,
  onDescripcionChange,
  onPdfHighlight,
  hasError = false,
  saveAttempt = 0,
  priceListId = null,
}) => {
  const [editingProduct, setEditingProduct] = React.useState(false);
  const [closingProduct, setClosingProduct] = React.useState(false);
  const [descripcionOpen, setDescripcionOpen] = React.useState(false);
  const [descripcionDraft, setDescripcionDraft] = React.useState('');
  const [popoverPos, setPopoverPos] = React.useState<{ top: number; left: number } | null>(null);
  const descripcionBtnRef = React.useRef<HTMLButtonElement>(null);
  const descripcionPopoverRef = React.useRef<HTMLDivElement>(null);
  const editingProductRef = React.useRef<HTMLDivElement>(null);
  const priceInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!hasError || saveAttempt === 0) return;
    if (errorNoProduct) {
      document.querySelector<HTMLInputElement>(`[data-search-item-id="${id}"]`)?.focus();
    } else if (errorNoQty) {
      document.querySelector<HTMLInputElement>(`[data-qty-item-id="${id}"]`)?.focus();
    } else if (errorNoPrice) {
      priceInputRef.current?.focus();
    }
  }, [saveAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!editingProduct) return;
    const handler = (e: MouseEvent) => {
      if (editingProductRef.current?.contains(e.target as Node)) return;
      setClosingProduct(true);
      setTimeout(() => {
        setEditingProduct(false);
        setClosingProduct(false);
        onSearchClear(item.id);
      }, 60);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editingProduct, onSearchClear, item.id]);

  React.useEffect(() => {
    if (!descripcionOpen) return;
    const btn = descripcionBtnRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setPopoverPos({ top: rect.bottom + 4, left: rect.left });
    }
    const handler = (e: MouseEvent) => {
      if (descripcionPopoverRef.current?.contains(e.target as Node)) return;
      if (descripcionBtnRef.current?.contains(e.target as Node)) return;
      setDescripcionOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [descripcionOpen]);

  const id = item.id;
  const lineNumber = index + 1;
  const minQuantity = item.product?.minOrderQuantity || 1;
  const quantity = item.quantity ?? 0;
  const unitPrice = item.unitPrice ?? '0';

  const errorNoProduct = hasError && !item.product;
  const errorNoQty = hasError && item.product != null && (item.quantity == null || String(item.quantity).trim() === '');
  const errorNoPrice = hasError && item.product != null && (item.unitPrice == null || item.unitPrice === '');
  const parsed = parseFloat(String(unitPrice || '0'));
  const itemTotal = quantity * (Number.isFinite(parsed) ? parsed : 0);


  const productBlock = item.product ? (
    <div
      className={`flex items-start gap-1.5 pr-1 group/product rounded px-2 py-1.5 transition-all ${
        onPdfHighlight && item.pdfLineText ? 'hover:bg-muted dark:hover:bg-muted active:bg-muted/80 dark:active:bg-muted/60 cursor-pointer hover:shadow-sm' : ''
      }`}
      onClick={onPdfHighlight && item.pdfLineText ? (e) => {
        const t = e.target as HTMLElement;
        if (t.closest('button') || t.closest('input') || t.closest('textarea') || t.closest('[data-no-highlight]')) return;
        const div = (e.currentTarget as HTMLElement);
        div?.classList.add('surface-press-active');
        setTimeout(() => div?.classList.remove('surface-press-active'), 120);
        onPdfHighlight(item.pdfLineText!, item.pdfPage);
      } : undefined}
    >
      <span className="text-xs text-muted-foreground/50 font-mono leading-snug mt-[3px] shrink-0 select-none">{lineNumber}</span>
      {editingProduct ? (
        <div
          ref={editingProductRef}
          className="flex-1 min-w-0 flex flex-col gap-1"
          style={{ animation: closingProduct ? 'slideDownFadeOut 0.08s ease both' : undefined }}
        >
          {item.pdfLineText?.trim() && (
            <p
              className="text-xs text-text-subtle leading-snug truncate"
              title={item.pdfLineText}
              style={{
                animation: 'slideDownFadeIn 0.25s ease both',
              }}
            >
              {'"'}{item.pdfLineText}{'"'}
            </p>
          )}
          <UnassignedLineProductSearch
            itemId={id}
            searchQ={searchQ}
            results={results}
            loadingRow={loadingRow}
            autoFocus
            onSearchChange={onSearchChange}
            onSearchClear={(itemId) => { onSearchClear(itemId); setEditingProduct(false); }}
            onSelectProduct={(itemId, product) => { onSelectProduct(itemId, product); onSearchClear(itemId); setEditingProduct(false); }}
            onCreateProduct={(itemId, product) => { onCreateProduct(itemId, product); onSearchClear(itemId); setEditingProduct(false); }}
          />
        </div>
      ) : (
        <>
          <div className="flex-1 min-w-0" style={{ animation: 'slideDownFadeIn 0.2s ease both' }}>
            <div className="flex items-center gap-1">
              <p
                className={`text-sm font-medium text-foreground whitespace-normal break-normal leading-snug${!readOnly ? ' cursor-pointer' : ''}`}
                onClick={!readOnly ? () => setEditingProduct(true) : undefined}
              >
                {item.product.name}
              </p>
              {item.pdfMatchQuality === 'dubious' && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="h-2 w-2 shrink-0 rounded-full bg-yellow-500 cursor-help" aria-hidden="true"></span>
                  </TooltipTrigger>
                  <TooltipContent>Match sujeto a revisión</TooltipContent>
                </Tooltip>
              )}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => setEditingProduct(true)}
                  className="opacity-0 group-hover/product:opacity-100 p-0.5 rounded hover:bg-muted transition-all inline-flex align-middle"
                  aria-label="Cambiar producto"
                >
                  <PencilSimple size={12} weight="bold" className="text-text-subtle" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1">
              <p className="text-xs text-muted-foreground truncate">{item.product.sku}</p>
              {item.pdfLineText?.trim() && (
                <div className="flex items-center gap-1 group/extracted px-2 py-1 bg-muted/40 dark:bg-muted/20 rounded">
                  <span className="text-xs text-text-subtle truncate" title={item.pdfLineText}>
                    {'"'}{item.pdfLineText}{'"'}
                  </span>
                  {onPdfHighlight && !readOnly && (
                    <button
                      type="button"
                      onClick={() => onPdfHighlight(item.pdfLineText!, item.pdfPage)}
                      className="opacity-0 group-hover/extracted:opacity-100 p-0.5 rounded hover:bg-muted/50 transition-all inline-flex align-middle shrink-0"
                      aria-label="Ver en PDF"
                      title="Ver en PDF"
                    >
                      <Eye size={12} weight="bold" className="text-text-subtle" />
                    </button>
                  )}
                </div>
              )}
            </div>
            {descripcionOpen && popoverPos && ReactDOM.createPortal(
              <div
                ref={descripcionPopoverRef}
                style={{ position: 'fixed', top: popoverPos.top, left: popoverPos.left, zIndex: 9999 }}
                className="bg-background border border-border rounded-md shadow-lg p-3 flex flex-col gap-2 w-64"
              >
                <p className="text-xs font-medium text-foreground">Descripción detallada</p>
                <textarea
                  autoFocus
                  value={descripcionDraft}
                  onChange={e => setDescripcionDraft(e.target.value)}
                  rows={3}
                  className="text-xs border border-border rounded px-2 py-1.5 bg-background outline-none focus:border-primary resize-none"
                />
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setDescripcionOpen(false)} className="text-xs px-2 py-1 rounded hover:bg-muted text-muted-foreground">Cancelar</button>
                  <button type="button" onClick={() => { onDescripcionChange(item.id, descripcionDraft); setDescripcionOpen(false); }} className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90">Guardar</button>
                </div>
              </div>,
              document.body
            )}
            {item.descripcionDetallada?.trim() && (
              !readOnly ? (
                <span
                  data-no-highlight
                  className="group/desc inline text-xs text-text-subtle break-words leading-snug mt-0.5 cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => { setDescripcionDraft(item.descripcionDetallada ?? ''); setDescripcionOpen(v => !v); }}
                >
                  <span className="font-medium">Descripción:</span> {item.descripcionDetallada}<PencilSimple size={11} weight="bold" className="inline ml-1 mb-0.5 opacity-0 group-hover/desc:opacity-100 transition-opacity text-text-subtle" />
                </span>
              ) : (
                <p className="text-xs text-text-subtle break-words leading-snug mt-0.5">
                  <span className="font-medium">Descripción:</span> {item.descripcionDetallada}
                </p>
              )
            )}
          </div>
        </>
      )}
    </div>
  ) : (
    <div className="flex flex-col gap-1 min-w-0">
      {item.pdfLineText?.trim() && (
        <p className="text-xs text-text-subtle leading-snug truncate" title={item.pdfLineText}>
          {'"'}{item.pdfLineText}{'"'}
        </p>
      )}
      <UnassignedLineProductSearch
        itemId={id}
        searchQ={searchQ}
        results={results}
        loadingRow={loadingRow}
        autoFocus
        onSearchChange={onSearchChange}
        onSearchClear={onSearchClear}
        onSelectProduct={onSelectProduct}
        onCreateProduct={onCreateProduct}
      />
    </div>
  );

  const [qtyRaw, setQtyRaw] = React.useState(String(item.quantity ?? 1));
  const [priceFocused, setPriceFocused] = React.useState(false);
  const [pricePopoverOpen, setPricePopoverOpen] = React.useState(false);

  // Always show all three price source slots; null price renders as em dash (non-clickable).
  const availablePriceOptions = React.useMemo((): PriceOption[] => {
    if (!item.priceOptions && !item.product) return [];
    const opts = item.priceOptions ?? {};
    return [
      { source: 'price_list', price: opts.priceList ?? null },
      { source: 'kame',       price: opts.kame ?? null },
      { source: 'oc',         price: opts.oc ?? null },
    ];
  }, [item.priceOptions, item.product]);

  // Keep local display in sync when item.quantity changes externally (e.g. product selected)
  React.useEffect(() => {
    setQtyRaw(String(item.quantity || ''));
  }, [item.quantity]);

  const qtyInput = (
    <QuantityWithUnitInput
      value={qtyRaw}
      unit={(['unidad', 'kg', 'lt'].includes(item.quantityUnit as string) ? item.quantityUnit : 'unidad') as QuantityUnit}
      disabled={readOnly}
      hasError={errorNoQty}
      onValueChange={(v) => {
        setQtyRaw(v);
        onQuantityChange(id, v);
      }}
      onUnitChange={(u) => onUnitChange(id, u)}
      min={minQuantity}
      dataItemId={id}
      onKeyDown={(e) => {
        if (e.key === 'Tab') { e.preventDefault(); priceInputRef.current?.focus(); return; }
        if (e.key === 'Enter') { e.currentTarget.blur(); return; }
        if ((e.key === 'a' || e.key === 'A') && layout === 'desktop' && onAddItem) { e.preventDefault(); onAddItem(); }
      }}
    />
  );

  const priceInput = (
    <Input
      ref={priceInputRef}
      aria-label="Precio"
      type="text"
      inputMode="decimal"
      value={priceFocused
        ? String(item.unitPrice ?? '0')
        : parseFloat(String(item.unitPrice ?? '0')).toLocaleString('es-CL')
      }
      onChange={(e) => onUnitPriceChange(id, e.target.value)}
      onFocus={() => {
        setPriceFocused(true);
        if (availablePriceOptions.length > 0) setPricePopoverOpen(true);
      }}
      onBlur={() => {
        setPriceFocused(false);
        setPricePopoverOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') { setPricePopoverOpen(false); priceInputRef.current?.blur(); return; }
        if (e.key === 'Enter') { e.currentTarget.blur(); return; }
        if ((e.key === 'a' || e.key === 'A') && layout === 'desktop' && onAddItem) { e.preventDefault(); onAddItem(); }
      }}
      className={`text-center py-1.5 px-2 ${errorNoPrice ? 'border-destructive focus-visible:ring-destructive' : ''}`}
      style={{ width: '100%' }}
      disabled={!item.product || readOnly}
    />
  );

  const isYellowItem =
    item.pdfMatchQuality === 'dubious' || item.pdfMatchQuality === 'unmatched' ||
    item.pdfPriceSource === 'price_list_missing' || item.pdfPriceSource === 'catalog';

  if (layout === 'desktop') {
    return (
      <tr
        className={`group border-b last:border-b-0 ${(errorNoProduct || errorNoQty || errorNoPrice) ? 'border-destructive bg-destructive/5' : isYellowItem ? 'border-yellow-400/60 bg-yellow-50/40 dark:bg-yellow-900/10' : 'border-border'}`}
        {...(isYellowItem ? { 'data-yellow-item': 'true' } : {})}
      >
        <td className={`py-2 align-top ${(errorNoProduct || errorNoQty || errorNoPrice) ? '[box-shadow:inset_0_0.5px_0_rgb(220_38_38)]' : isYellowItem ? '[box-shadow:inset_0_0.5px_0_rgb(234_179_8_/_0.5)]' : ''}`}>{productBlock}</td>
        <td className={`py-2 align-middle ${(errorNoProduct || errorNoQty || errorNoPrice) ? '[box-shadow:inset_0_0.5px_0_rgb(220_38_38)]' : isYellowItem ? '[box-shadow:inset_0_0.5px_0_rgb(234_179_8_/_0.5)]' : ''}`}>{qtyInput}</td>
        <td className={`py-2 align-middle relative ${(errorNoProduct || errorNoQty || errorNoPrice) ? '[box-shadow:inset_0_0.5px_0_rgb(220_38_38)]' : isYellowItem ? '[box-shadow:inset_0_0.5px_0_rgb(234_179_8_/_0.5)]' : ''}`}>
          {flashPrice && <span className="price-shine-overlay overflow-hidden" />}
          <div className="relative">
            {priceInput}
            <PriceSourceDot priceSource={item.pdfPriceSource} />
            {pricePopoverOpen && availablePriceOptions.length > 0 && (
              <PriceOptionsPopover
                options={availablePriceOptions}
                activeSource={item.priceSource ?? null}
                anchorRef={priceInputRef}
                priceListId={priceListId}
                onSelect={(price, source) => {
                  onPriceSourceSelect(id, price, source);
                  setPricePopoverOpen(false);
                }}
              />
            )}
          </div>
        </td>
        <td className={`py-2 align-middle text-right relative overflow-hidden ${(errorNoProduct || errorNoQty || errorNoPrice) ? '[box-shadow:inset_0_0.5px_0_rgb(220_38_38)]' : isYellowItem ? '[box-shadow:inset_0_0.5px_0_rgb(234_179_8_/_0.5)]' : ''}`}>
          {flashTotal && <span className="price-shine-overlay" />}
          <p className="text-base font-medium text-foreground">
            ${itemTotal.toLocaleString('es-CL')}
          </p>
        </td>
        <td className={`py-2 align-middle text-center ${(errorNoProduct || errorNoQty || errorNoPrice) ? '[box-shadow:inset_0_0.5px_0_rgb(220_38_38)]' : isYellowItem ? '[box-shadow:inset_0_0.5px_0_rgb(234_179_8_/_0.5)]' : ''}`}>
          {!readOnly && (
            <DeleteButton
              onDelete={() => onDelete(id)}
              className="opacity-0 group-hover:opacity-100"
            />
          )}
        </td>
      </tr>
    );
  }

  return (
    <div className={`flex flex-col gap-2 w-full ${isYellowItem ? 'bg-yellow-50/40 dark:bg-yellow-900/10 border-l-2 border-yellow-400/60' : ''}`} {...(isYellowItem ? { 'data-yellow-item': 'true' } : {})}>
      <div className="relative z-10">{productBlock}</div>
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[84px] text-right">
          <span className="text-xs text-text-subtle mb-1 block">Cantidad</span>
          {qtyInput}
        </div>
        <div className="flex-1 min-w-[84px] text-right">
          <span className="text-xs text-text-subtle mb-1 block">Precio</span>
          <div className="relative">
            {priceInput}
            <PriceSourceDot priceSource={item.pdfPriceSource} />
            {pricePopoverOpen && availablePriceOptions.length > 0 && (
              <PriceOptionsPopover
                options={availablePriceOptions}
                activeSource={item.priceSource ?? null}
                anchorRef={priceInputRef}
                onSelect={(price, source) => {
                  onPriceSourceSelect(id, price, source);
                  setPricePopoverOpen(false);
                }}
              />
            )}
          </div>
        </div>
        <div className="flex-1 min-w-[72px] text-right">
          <span className="text-xs text-text-subtle mb-1 block">Total</span>
          <p className="text-sm font-medium text-foreground pt-2">${itemTotal.toLocaleString('es-CL')}</p>
        </div>
        {!readOnly && (
          <DeleteButton
            onDelete={() => onDelete(id)}
            className="shrink-0 mb-0.5"
          />
        )}
      </div>
    </div>
  );
};

// Local "seen orders" tracking (like unread emails)
const SEEN_ORDERS_KEY = 'orders_seen_v1';
const getSeenSet = (): Set<string> => {
  try {
    const raw = localStorage.getItem(SEEN_ORDERS_KEY);
    const arr: string[] = raw ? JSON.parse(raw) : [];
    return new Set(arr);
  } catch {
    return new Set();
  }
};
const markSeen = (id: string) => {
  const set = getSeenSet();
  set.add(id);
  localStorage.setItem(SEEN_ORDERS_KEY, JSON.stringify(Array.from(set)));
};

function downloadColumnXlsx(orders: Order[], columnName: string) {
  const rows = orders.map(o => ({
    'Pedido': o.orderId,
    'Empresa': o.company,
    'Cliente': o.customer,
    'RUT': o.customerRut ?? '',
    'Productos': o.productCount,
    'Total': o.total,
    'Fecha creación': o.date ?? '',
    'Fecha documento': o.documentDate ?? '',
    'Folio Kame': o.kameoneFolio ?? '',
    'Tipo documento': o.documentType ?? '',
    'Dirección': o.addressName ?? '',
    'Origen': o.origin ?? '',
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, columnName);
  const date = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `pedidos-${columnName.toLowerCase().replace(/\s+/g, '-')}-${date}.xlsx`);
}

const USP_CSS_ID = 'usp-btn-styles';
const injectUspStyles = () => {
  if (document.getElementById(USP_CSS_ID)) return;
  const style = document.createElement('style');
  style.id = USP_CSS_ID;
  style.textContent = `
    @keyframes uspRotateBorder { to { transform: translate(-50%,-50%) rotate(1turn); } }
    @keyframes uspShineSweep {
      0%          { transform: translateX(-120%) skewX(-18deg); opacity: 0; }
      20%         { opacity: .9; }
      45%, 100%   { transform: translateX(120%) skewX(-18deg); opacity: 0; }
    }
    @keyframes uspSparkle {
      0%,100% { opacity:0; transform:scale(.2) rotate(0deg);   filter:blur(1px); }
      18%     { opacity:1; transform:scale(1.35) rotate(35deg); filter:blur(0); }
      36%     { opacity:.35; transform:scale(.75) rotate(75deg); }
      54%     { opacity:1; transform:scale(1.8) rotate(120deg); }
      72%     { opacity:0; transform:scale(.25) rotate(180deg); filter:blur(1.5px); }
    }
    .usp-rotate { animation: uspRotateBorder 4s linear infinite; }
    .usp-shine  { animation: uspShineSweep 3.8s ease-in-out infinite; }
    .usp-sp-1   { animation: uspSparkle 4s .1s  infinite ease-in-out; }
    .usp-sp-2   { animation: uspSparkle 4s .8s  infinite ease-in-out; }
    .usp-sp-3   { animation: uspSparkle 4s 1.6s infinite ease-in-out; }
    .usp-sp-4   { animation: uspSparkle 4s .4s  infinite ease-in-out; }
    .usp-sp-5   { animation: uspSparkle 4s 2.1s infinite ease-in-out; }
    .usp-sp-6   { animation: uspSparkle 4s 1.1s infinite ease-in-out; }
  `;
  document.head.appendChild(style);
};

const USP_SPARKLES = [
  { left: '10%', top: '20%', cls: 'usp-sp-1' },
  { left: '28%', top: '75%', cls: 'usp-sp-2' },
  { left: '50%', top: '25%', cls: 'usp-sp-3' },
  { left: '72%', top: '70%', cls: 'usp-sp-4' },
  { left: '85%', top: '20%', cls: 'usp-sp-5' },
  { left: '92%', top: '55%', cls: 'usp-sp-6' },
];

const USP_BG = 'radial-gradient(circle at 20% 20%, rgba(255,255,255,.18), transparent 24%), radial-gradient(circle at 80% 30%, rgba(25,118,237,.22), transparent 28%), radial-gradient(circle at 50% 90%, rgba(89,204,224,.18), transparent 32%), linear-gradient(135deg, #080812, #111122 45%, #07070d)';

const UnSoloPedidoButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const [hovered, setHovered] = React.useState(false);

  React.useEffect(() => { injectUspStyles(); }, []);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative', overflow: 'hidden', cursor: 'pointer',
        borderRadius: 8, border: 'none', padding: '6px 12px',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontFamily: 'inherit', fontSize: 14, fontWeight: 500,
        background: hovered ? 'transparent' : undefined,
        color: hovered ? 'rgba(255,255,255,.9)' : undefined,
        transition: 'color 300ms ease',
      }}
      className={!hovered ? 'bg-primary text-primary-foreground' : ''}
    >
      {/* Dark bg — fades in on hover */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: USP_BG,
        opacity: hovered ? 1 : 0,
        transition: 'opacity 400ms ease',
      }} />

      {/* Rotating border */}
      {hovered && (
        <div style={{ position: 'absolute', inset: 0, borderRadius: 'inherit', overflow: 'hidden', zIndex: 1, pointerEvents: 'none' }}>
          <div className="usp-rotate" style={{
            position: 'absolute', top: '50%', left: '50%',
            width: 999, height: 999,
            transform: 'translate(-50%,-50%) rotate(0deg)',
            background: 'conic-gradient(transparent, transparent, #1976ed, #59cce0, white, #1976ed, transparent 83%)',
          }} />
          <div style={{ position: 'absolute', inset: 2, borderRadius: 6, background: USP_BG, zIndex: 2 }} />
        </div>
      )}

      {/* Shine sweep */}
      {hovered && (
        <div className="usp-shine" style={{
          position: 'absolute', inset: 0, zIndex: 4, pointerEvents: 'none',
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0) 25%, rgba(255,255,255,.55) 50%, rgba(255,255,255,0) 75%, transparent 100%)',
          mixBlendMode: 'screen',
        }} />
      )}

      {/* Sparkles */}
      {hovered && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none' }}>
          {USP_SPARKLES.map((s, i) => (
            <span key={i} className={s.cls} style={{
              position: 'absolute', left: s.left, top: s.top,
              width: 4, height: 4, borderRadius: 999, background: 'white', opacity: 0,
              boxShadow: '0 0 6px white, 0 0 14px rgba(255,255,255,.9), 0 0 26px rgba(89,204,224,.75)',
            }} />
          ))}
        </div>
      )}

      {/* Content */}
      <span style={{ position: 'relative', zIndex: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Plus size={16} weight="bold" />
        Un solo pedido
      </span>
    </button>
  );
};

const ADDRESS_STOPWORDS = new Set([
  'av', 'avda', 'avenida', 'calle', 'pasaje', 'psje', 'pje', 'camino', 'ruta', 'autopista', 'boulevard', 'blvd',
  'local', 'loc', 'oficina', 'of', 'piso', 'bodega', 'depto', 'dto', 'dpto', 'apt', 'block', 'bloque', 'torre', 'casa',
  'santiago', 'providencia', 'las', 'los', 'condes', 'nunoa', 'vitacura', 'maipu', 'pudahuel',
  'barnechea', 'lampa', 'colina', 'centro', 'sur', 'norte', 'oriente', 'poniente',
  'numero', 'num', 'nro', 'sin',
]);

function matchAddressToClientAddresses(
  address: string,
  addressName: string | undefined,
  addrs: import('../services/api').ClientAddress[],
): import('../services/api').ClientAddress | null {
  if (!addrs.length) return null;
  const normalize = (s: string) =>
    s.toLowerCase().replace(/[.,-]/g, ' ').replace(/\s+/g, ' ').trim();
  const tokenize = (s: string): string[] =>
    s.split(' ').filter(w =>
      /^\d+$/.test(w) ? true : w.length > 2 && !ADDRESS_STOPWORDS.has(w)
    );

  // 1. Exact nombre match
  if (addressName) {
    const exact = addrs.find(a => a.nombre === addressName) ?? null;
    if (exact) return exact;
  }

  if (!address.trim()) return null;

  const normAddr = normalize(address);
  const addrWords = new Set(tokenize(normAddr));

  // Guard: if no meaningful tokens, skip word-overlap scoring
  if (addrWords.size === 0) return null;

  let bestScore = 0;
  let bestAddr: import('../services/api').ClientAddress | null = null;

  for (const saved of addrs) {
    if (!saved.address?.trim()) continue;
    const savedNorm = normalize(saved.address);
    // 2. Contains check (either direction) → perfect score
    if (savedNorm.includes(normAddr) || normAddr.includes(savedNorm)) {
      return saved;
    }
    // 3. Word overlap score on meaningful tokens only
    const savedWords = tokenize(savedNorm);
    if (savedWords.length === 0) continue;
    const overlap = savedWords.filter(w => addrWords.has(w)).length;
    const score = overlap / Math.max(savedWords.length, addrWords.size);
    if (score > bestScore) {
      bestScore = score;
      bestAddr = saved;
    }
  }

  return bestScore >= 0.7 ? bestAddr : null;
}

/** Derives card-level dot color from raw API order items.
 *  Yellow (glowing) = any item has price_list_missing/catalog source OR dubious/unmatched product match.
 *  Green (static)   = all items have price_list source.
 *  Blue (static)    = no yellow, but at least one item has pdf/oc source.
 */
function computePriceStatus(items: any[]): 'yellow' | 'green' | 'blue' | undefined {
  if (!Array.isArray(items) || items.length === 0) return undefined;
  const sources = items.map((item: any) => item.price_source ?? item.priceSource ?? item.pdfPriceSource).filter(Boolean);
  const qualities = items.map((item: any) => item.matchQuality ?? item.pdfMatchQuality ?? item.match_quality).filter(Boolean);
  const hasWarning =
    sources.some((s: string) => s === 'price_list_missing' || s === 'catalog') ||
    qualities.some((q: string) => q === 'dubious' || q === 'unmatched');
  if (hasWarning) return 'yellow';
  if (sources.some((s: string) => s === 'pdf' || s === 'oc')) return 'blue';
  if (sources.length > 0) return 'green';
  return undefined;
}

const PROCESSING_PHRASES = [
  'Extrayendo información del documento',
  'Identificando al cliente',
  'Buscando productos en catálogo',
  'Calculando precios de lista',
  'Extrayendo más información de la OC',
];

function ProcessingPhrase() {
  const [index, setIndex] = React.useState(0);
  const [visible, setVisible] = React.useState(true);
  const [dots, setDots] = React.useState('.');

  React.useEffect(() => {
    if (index >= PROCESSING_PHRASES.length - 1) return;
    const delay = 3500 + Math.random() * 4500;
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex(i => i + 1);
        setVisible(true);
      }, 400);
    }, delay);
    return () => clearTimeout(timer);
  }, [index]);

  React.useEffect(() => {
    const t = setInterval(() => setDots(d => d.length >= 3 ? '.' : d + '.'), 500);
    return () => clearInterval(t);
  }, []);

  return (
    <p
      className="font-sans font-normal text-sm text-text-subtle text-center transition-opacity duration-400"
      style={{ opacity: visible ? 1 : 0 }}
    >
      {PROCESSING_PHRASES[index]}<span style={{ display: 'inline-block', width: '1.5ch', textAlign: 'left' }}>{dots}</span>
    </p>
  );
}

export const OrdersTray: React.FC<{
  onOrderClick?: (orderId: string) => void;
  onAllChatsClick?: () => void;
  onMenuClick?: () => void;
  onNavigateToDailyReport?: () => void;
  initialOrderId?: string;
  onInitialOrderClose?: () => void;
}> = ({ onOrderClick, onAllChatsClick, onMenuClick, onNavigateToDailyReport, initialOrderId, onInitialOrderClose }) => {
  const api = useApi();
  const navigate = useNavigate();
  const { orderId: urlOrderId } = useParams<{ orderId?: string }>();
  const isMobileDevice = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const [searchValue, setSearchValue] = useState('');
  const [originFilter, setOriginFilter] = useState<OrderOrigin | 'todos'>('todos');
  const [selectedTab, setSelectedTab] = useState('pendientes');
  const [viewMode, setViewMode] = useState<'table' | 'board'>('board');
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [flashItemData, setFlashItemData] = useState<Map<number, { price: boolean; total: boolean }>>(new Map());
  const [priceListFlash, setPriceListFlash] = useState(false);
  const [totalsFlash, setTotalsFlash] = useState(false);
  const [countFlash, setCountFlash] = useState(false);
  const prevItemsRef = useRef<OrderItem[]>([]);
  const recentLocalMutationsRef = useRef<Map<number, number>>(new Map());
  const createOrderInFlightRef = useRef(false);
  const [recentlyChangedOrderIds, setRecentlyChangedOrderIds] = useState<Set<string>>(new Set());
  const markLocalMutation = (orderId: number) => {
    const map = recentLocalMutationsRef.current;
    map.set(orderId, Date.now());
    // Clean up entries older than 10 seconds
    const cutoff = Date.now() - 10_000;
    map.forEach((ts, id) => { if (ts < cutoff) map.delete(id); });
  };
  const isRecentLocalMutation = (orderId: number) => {
    const ts = recentLocalMutationsRef.current.get(orderId);
    return ts !== undefined && Date.now() - ts < 3_000;
  };
  const [error, setError] = useState<string | null>(null);
  const [selectedInvoiceDate, setSelectedInvoiceDate] = useState<Date>(new Date());
  const [kameOrders, setKameOrders] = useState<Order[]>([]);
  const [isLoadingKame, setIsLoadingKame] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerButtonRef = useRef<HTMLButtonElement>(null);
  const [isAddOrdersModalOpen, setIsAddOrdersModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const loadingOrdersRef = useRef(false);
  const apiOrdersCacheRef = useRef<ApiOrder[]>([]);
  const originalOrderSnapshotRef = useRef<string | null>(null);
  
  // Estados para el modal de "Un solo pedido"
  const [showSingleOrderModal, setShowSingleOrderModal] = useState(false);
  const [arrowFlashLeft, setArrowFlashLeft] = useState(0);
  const [arrowFlashRight, setArrowFlashRight] = useState(0);
  const [orderModalMode, setOrderModalMode] = useState<'create' | 'edit' | 'consult'>('create');
  const [consultingOrderId, setConsultingOrderId] = useState<string | null>(null); // e.g. "P-42"
  const [consultingOrderStatus, setConsultingOrderStatus] = useState<StatusType | null>(null);
  const [consultingOrderDocType, setConsultingOrderDocType] = useState<string | null>(null);
  const [consultingPdfUrl, setConsultingPdfUrl] = useState<string | null>(null);
  const [consultingPdfName, setConsultingPdfName] = useState<string | null>(null);
  const [newOrderItems, setNewOrderItems] = useState<OrderItem[]>([]);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [saveError, setSaveError] = useState<{ message: string; retryAt?: string } | null>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [orderToDiscard, setOrderToDiscard] = useState<string | null>(null);
  const [pendingNavOrderId, setPendingNavOrderId] = useState<string | null>(null);
  const [pendingSkipIndex, setPendingSkipIndex] = useState<number | null>(null);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [saveAttemptCount, setSaveAttemptCount] = useState(0);
  const [hasConfirmedAddressWarning, setHasConfirmedAddressWarning] = useState(false);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  
  const [pdfHighlightText, setPdfHighlightText] = useState<string | null>(null);
  const [pdfHighlightPage, setPdfHighlightPage] = useState<number | undefined>(undefined);
  const [pdfHighlightReqId, setPdfHighlightReqId] = useState<number>(0);

  // Estados para procesar PDF desde OC
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const [pdfOrderNumber, setPdfOrderNumber] = useState<string | null>(null);
  const [pdfOrderDate, setPdfOrderDate] = useState<string | null>(null);
  // correlationId from the document extraction — echoed back on save so the
  // backend can finalize the extraction audit row. Null for manual orders.
  const [extractionCorrelationId, setExtractionCorrelationId] = useState<string | null>(null);
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState<LoadingCard | null>(null);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [isQuoteFading, setIsQuoteFading] = useState(false);
  const [pdfProcessingError, setPdfProcessingError] = useState<string | null>(null);
  const [isProductListDropActive, setIsProductListDropActive] = useState(false);

  // Estados para procesamiento secuencial de múltiples PDFs
  const [multiPdfFiles, setMultiPdfFiles] = useState<File[]>([]);
  const [multiPdfCurrentIndex, setMultiPdfCurrentIndex] = useState(0);
  const [isMultiPdfProcessing, setIsMultiPdfProcessing] = useState(false);

  // Combined array for deterministic cycling through phrases
  const allPhrases = [...ORDER_MODAL_LOADING_QUOTES, ...ORDER_MODAL_LOADING_TIPS];

  // Prefetch cache: map of file-index → in-flight or resolved promise for that PDF
  type PrefetchedPdfData = { items: OrderItem[]; customer: KameoneFicha | null; warnings: string[] | null; file: File; orderNumber: string | null; orderDate: string | null };
  const prefetchRef = useRef<Map<number, Promise<PrefetchedPdfData | null>>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Incremented every time a new PDF processing session starts. Any async result
  // that resolves with a different generation is stale and discarded.
  const pdfSessionRef = useRef<number>(0);
  const modalScrollableRef = useRef<HTMLDivElement>(null);

  // Price list dropdown
  const [availablePriceLists, setAvailablePriceLists] = useState<{ name: string; id: number }[]>([]);
  const priceListNameToId = React.useMemo(() => {
    const map: Record<string, number> = {};
    availablePriceLists.forEach(l => { map[l.name.toLowerCase()] = l.id; });
    return map;
  }, [availablePriceLists]);

  const [assignedFolio, setAssignedFolio] = useState<string>('');
  const [isEditingFolio, setIsEditingFolio] = useState(false);
  const [folioInput, setFolioInput] = useState<string>('');
  const [folioManuallyEdited, setFolioManuallyEdited] = useState(false);

  // Estados para overlay de facturación
  const [showInvoiceOverlay, setShowInvoiceOverlay] = useState(false);
  const [kameResponse, setKameResponse] = useState<KameSyncResult | null>(null);
  const [invoiceOrderId, setInvoiceOrderId] = useState<number | null>(null);

  // Estados para selección de tipo de documento
  const [documentType, setDocumentType] = useState<'factura' | 'guia'>('factura');
  const [orderTipoDocumento, setOrderTipoDocumento] = useState<'VENTA' | 'PEDIDO'>('VENTA');
  const [documentDate, setDocumentDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [showDocumentMenu, setShowDocumentMenu] = useState(false);
  const [paymentType, setPaymentType] = useState<'contado' | 'credito'>('contado');
  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().split('T')[0]);
  
  // Edición inline de líneas del nuevo pedido (búsqueda de producto por fila)
  const [lineItemProductSearch, setLineItemProductSearch] = useState<Record<number, string>>({});
  const [lineItemProductResults, setLineItemProductResults] = useState<Record<number, Product[]>>({});
  const [lineItemProductLoading, setLineItemProductLoading] = useState<Record<number, boolean>>({});
  
  // States for customer dialog
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<KameoneFicha | null>(null);
  // Addresses from client_addresses table (by RUT)
  const [clientDbAddresses, setClientDbAddresses] = useState<import('../services/api').ClientAddress[]>([]);
  const [loadingClientAddresses, setLoadingClientAddresses] = useState(false);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [newAddressNombre, setNewAddressNombre] = useState('');
  const [newAddressContacto, setNewAddressContacto] = useState('');
  const [newAddressInput, setNewAddressInput] = useState('');
  const [newAddressComuna, setNewAddressComuna] = useState('');
  const [newAddressCity, setNewAddressCity] = useState('');
  const addressNombreRef = useRef<HTMLInputElement>(null);
  const [savingNewAddress, setSavingNewAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<number | 'selected' | null>(null);
  const [selectedAddressName, setSelectedAddressName] = useState<string | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [addressSource, setAddressSource] = useState<'pdf' | 'user' | 'kame' | null>(null);

  // Imperative handle to the address SearchableDropdown — lets us open it (and its
  // inline editor) programmatically, since its panel only renders while open.
  const addressDropdownRef = useRef<import('../components/SearchableDropdown').SearchableDropdownHandle | null>(null);

  // Open the inline editor for the currently selected delivery address, pre-filled.
  // Reused by the "Editar dirección" pencil and auto-triggered when the
  // "Faltan datos de entrega" warning appears, so the user can complete the
  // address right away instead of hunting for the edit button.
  // We open the dropdown first (its onOpen resets the editing state and clears the
  // fields), then set 'selected' + prefill — the later setters win within the tick.
  const openSelectedAddressEdit = () => {
    if (!selectedCustomer) return;
    addressDropdownRef.current?.open();
    setEditingAddressId('selected');
    setNewAddressNombre(selectedAddressName || '');
    setNewAddressContacto(selectedCustomer.Contacto || '');
    setNewAddressInput(selectedCustomer.Direccion || '');
    setNewAddressComuna(selectedCustomer.Comuna || '');
    setNewAddressCity(selectedCustomer.Ciudad || '');
    setShowNewAddressForm(false);
    setTimeout(() => addressNombreRef.current?.focus(), 50);
  };

  const [editingCustomerName, setEditingCustomerName] = useState(false);
  const [editingCustomerNameValue, setEditingCustomerNameValue] = useState('');
  const [customerSearch, setCustomerSearch] = useState<string>('');
  const [customers, setCustomers] = useState<KameoneFicha[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [activeCustomerIndex, setActiveCustomerIndex] = useState(-1);
  const customerSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const customerSearchFieldRef = useRef<HTMLDivElement>(null);
  const customerListPopoverRef = useRef<HTMLDivElement>(null);

  // Recently used fichas — up to 30 entries, persisted in localStorage
  const RECENT_FICHAS_KEY = 'fichas_recent_v1';
  const RECENT_FICHAS_MAX = 30;
  const [recentFichas, setRecentFichas] = useState<KameoneFicha[]>(() => {
    try {
      const raw = localStorage.getItem('fichas_recent_v1');
      if (raw) return JSON.parse(raw);
    } catch {}
    return [];
  });
  
  const [isMobile, setIsMobile] = useState(false);
  const [isLgLayout, setIsLgLayout] = useState(false);

  useEffect(() => {
    // Only run this effect while actively processing PDF and showing loading state
    if (!isProcessingPdf || !loadingQuote || orderModalMode !== 'create') {
      return;
    }

    // Reset to first phrase when PDF processing starts
    setCurrentQuoteIndex(0);
    setIsQuoteFading(false);

    // Set up 10-second rotation interval
    const interval = setInterval(() => {
      // Fade out (300ms)
      setIsQuoteFading(true);

      // After fade-out completes, switch phrase and fade in
      setTimeout(() => {
        setCurrentQuoteIndex((prevIndex) => (prevIndex + 1) % allPhrases.length);
        setIsQuoteFading(false);
      }, 300);
    }, 10000); // 10-second cycle

    return () => clearInterval(interval);
  }, [isProcessingPdf, loadingQuote, orderModalMode]);

  useEffect(() => {
    const checkBreakpoints = () => {
      setIsMobile(window.innerWidth < 768);
      setIsLgLayout(window.innerWidth >= 1024);
    };

    checkBreakpoints();
    window.addEventListener('resize', checkBreakpoints);
    return () => window.removeEventListener('resize', checkBreakpoints);
  }, []);
  
  // Estados para sort de productos
  const [productSortField, setProductSortField] = useState<string | null>(null);
  const [productSortDirection, setProductSortDirection] = useState<'asc' | 'desc'>('asc');

  // Fetch orders and conversations from API
  const fetchOrders = useCallback(async (silent = false) => {
    // Don't fetch if authentication is still loading or user is not authenticated
    if (api.isLoading || !api.isAuthenticated || loadingOrdersRef.current) {
      return;
    }
      loadingOrdersRef.current = true;
      try {
        if (!silent) setIsLoading(true);
        
        // Fetch orders and conversations, but handle conversations failure gracefully
        const [ordersResult, conversationsResult] = await Promise.allSettled([
          api.getOrders(200, 0),
          api.getAllConversations().catch((err) => {
            // If conversations fail (e.g., 403 admin access), return empty array
            return [] as Conversation[];
          })
        ]);
        
        // Extract values from Promise.allSettled results
        const finalOrders = ordersResult.status === 'fulfilled' && Array.isArray(ordersResult.value) 
          ? ordersResult.value 
          : [];
        const finalConversations = conversationsResult.status === 'fulfilled' && Array.isArray(conversationsResult.value)
          ? conversationsResult.value
          : [];
        
        if (ordersResult.status === 'rejected') {
          throw ordersResult.reason;
        }
        
        // Create a map of phone -> conversation for quick lookup
        // Normalize phone numbers for better matching (remove +, whatsapp:, spaces)
        const normalizePhoneForMatching = (phone: string): string => {
          if (!phone) return '';
          return phone.replace(/^whatsapp:/, '').replace(/^\+/, '').replace(/\s/g, '').trim();
        };
        
        const conversationMap = new Map<string, Conversation>();
        finalConversations.forEach((conv) => {
          const phone = normalizePhoneForMatching(conv.customerPhone);
          if (phone) {
            conversationMap.set(phone, conv);
            // Also store with + prefix for flexible matching
            conversationMap.set(`+${phone}`, conv);
          }
        });
        
        
        // Cache raw API orders for zero-latency modal open
        apiOrdersCacheRef.current = finalOrders;

        // Transform API orders to frontend format
        const seenSet = getSeenSet();
        const transformedOrders: Order[] = finalOrders.map((order: ApiOrder) => {
          const customerPhone = order.customerPhone || order.customer_phone || '';
          const normalizedPhone = normalizePhoneForMatching(customerPhone);
          const formattedPhone = customerPhone.trim() ? `+${customerPhone.trim().replace(/^\+/, '')}` : '';
          
          // Try to get company from conversation context
          let conversation = normalizedPhone ? conversationMap.get(normalizedPhone) : null;
          
          // If not found, try with + prefix
          if (!conversation && normalizedPhone) {
            conversation = conversationMap.get(`+${normalizedPhone}`) || null;
          }
          
          // Extract company from context (handle both string and object formats)
          let company = '';
          if (conversation?.context) {
            if (typeof conversation.context === 'string') {
              try {
                const parsedContext = JSON.parse(conversation.context);
                company = parsedContext?.company || '';
              } catch {
                // If parsing fails, context is not JSON
              }
            } else if (typeof conversation.context === 'object') {
              company = conversation.context.company || '';
            }
          }
          
          if (normalizedPhone) {
          }
          
          // Use company as "Cliente" if available, otherwise fallback to customerName
          const customerNameRaw = order.customerName || order.customer_name || '';
          const customerName = customerNameRaw.trim() || '';
          const displayName = company.trim() || customerName || 'Cliente no registrado';
          
          const total = Math.round(parseFloat(order.totalAmount?.toString() || order.total_amount?.toString() || '0') * 1.19);

          const orderDate = getOrderDate(order);
          const formattedDate = orderDate ? formatRelativeDate(orderDate) : undefined;

          const docType: string = (order as any).document_type || (order as any).documentType || '';
          const hasEmailIntake = !!(order as any).email_intake_id;
          const hasConversation = !!(order as any).conversation_id;
          const hasPdf = !!(order as any).source_pdf_name || !!(order as any).sourcePdfName;
          let origin: OrderOrigin;
          if (hasEmailIntake || docType === 'Pedido Recibido por Email') origin = 'email';
          else if (hasConversation) origin = 'whatsapp';
          else if (hasPdf) origin = 'pdf';
          else origin = 'manual';

          return {
            orderId: `P-${order.id}`,
            company: displayName,
            customer: formattedPhone,
            productCount: order.items?.length || 0,
            total: `$${total.toLocaleString('es-CL')}`,
            status: mapBackendStatusToFrontend(order.status) as StatusType,
            hasUnread: !seenSet.has(String(order.id)),
            date: formattedDate,
            updatedAt: order.updated_at || order.updatedAt || order.created_at || order.createdAt,
            kameoneFolio: (order as any).kameoneFolio || (order as any).kameone_folio || undefined,
            customerRut: order.customerRut || null,
            productCodes: (order.items || [])
              .map((item: any) => item.productCode || item.product_code || item.sku || '')
              .filter(Boolean),
            origin,
            documentType: docType || null,
            addressName: (order as any).deliveryAddressName || (order as any).delivery_address_name || null,
            sourceFileName: (order as any).sourcePdfName || (order as any).source_pdf_name || null,
            documentDate: (order as any).documentDate || (order as any).document_date || null,
            priceStatus: computePriceStatus(order.items || []),
          };
        });

        setOrders(transformedOrders.filter(o => o.status !== 'descartado'));
        setError(null);
      } catch (err) {
        setError('Error al cargar los pedidos. Por favor, intenta nuevamente.');
      } finally {
        setIsLoading(false);
        loadingOrdersRef.current = false;
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.isAuthenticated, api.isLoading]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Fetch orders for "Enviado a Kame" column filtered by date
  const fetchKameOrders = useCallback(async (date: Date) => {
    if (api.isLoading || !api.isAuthenticated) return;
    setIsLoadingKame(true);
    try {
      const localDateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      // Also query the next UTC day to catch orders confirmed late at night
      // (e.g. 23:00 local = 02:00 UTC next day, which the backend's UTC window misses)
      const nextUtcDay = new Date(date);
      nextUtcDay.setDate(nextUtcDay.getDate() + 1);
      const nextDateStr = `${nextUtcDay.getFullYear()}-${String(nextUtcDay.getMonth() + 1).padStart(2, '0')}-${String(nextUtcDay.getDate()).padStart(2, '0')}`;
      const [apiOrders, nextDayOrders] = await Promise.all([
        api.getOrdersByDate(localDateStr, 'confirmed'),
        api.getOrdersByDate(nextDateStr, 'confirmed'),
      ]);
      // Merge and deduplicate; filter next-day orders to only those updated within the local day
      const localDayStartUtc = new Date(`${localDateStr}T00:00:00`).getTime(); // local midnight in ms
      const localDayEndUtc = localDayStartUtc + 24 * 60 * 60 * 1000;
      const nextDayFiltered = (nextDayOrders || []).filter((o: ApiOrder) => {
        const updatedMs = new Date((o as any).updated_at || (o as any).updatedAt || 0).getTime();
        return updatedMs >= localDayStartUtc && updatedMs < localDayEndUtc;
      });
      const seenIds = new Set<number>();
      const mergedOrders: ApiOrder[] = [];
      for (const o of [...(apiOrders || []), ...nextDayFiltered]) {
        if (!seenIds.has(o.id)) { seenIds.add(o.id); mergedOrders.push(o); }
      }
      const seenSet = getSeenSet();
      const transformed: Order[] = mergedOrders.map((order: ApiOrder) => {
        const customerPhone = order.customerPhone || order.customer_phone || '';
        const formattedPhone = customerPhone.trim() ? `+${customerPhone.trim().replace(/^\+/, '')}` : '';
        const customerNameRaw = order.customerName || order.customer_name || '';
        const displayName = customerNameRaw.trim() || 'Cliente no registrado';
        const total = Math.round(parseFloat(order.totalAmount?.toString() || order.total_amount?.toString() || '0') * 1.19);
        const orderDate = getOrderDate(order);
        const formattedDate = orderDate ? formatRelativeDate(orderDate) : undefined;
        const docType: string = (order as any).document_type || (order as any).documentType || '';
        const hasEmailIntake = !!(order as any).email_intake_id;
        const hasConversation = !!(order as any).conversation_id;
        const hasPdf = !!(order as any).source_pdf_name || !!(order as any).sourcePdfName;
        let origin: OrderOrigin;
        if (hasEmailIntake || docType === 'Pedido Recibido por Email') origin = 'email';
        else if (hasConversation) origin = 'whatsapp';
        else if (hasPdf) origin = 'pdf';
        else origin = 'manual';
        return {
          orderId: `P-${order.id}`,
          company: displayName,
          customer: formattedPhone,
          productCount: order.items?.length || 0,
          total: `$${total.toLocaleString('es-CL')}`,
          status: mapBackendStatusToFrontend(order.status) as StatusType,
          hasUnread: !seenSet.has(String(order.id)),
          date: formattedDate,
          updatedAt: order.updated_at || order.updatedAt || order.created_at || order.createdAt,
          kameoneFolio: (order as any).kameoneFolio || (order as any).kameone_folio || undefined,
          customerRut: order.customerRut || null,
          productCodes: (order.items || [])
            .map((item: any) => item.productCode || item.product_code || item.sku || '')
            .filter(Boolean),
          origin,
          addressName: (order as any).deliveryAddressName || (order as any).delivery_address_name || null,
          priceStatus: computePriceStatus(order.items || []),
        };
      });
      setKameOrders(transformed);
    } catch (err) {
      // Non-fatal: kame column will show empty
    } finally {
      setIsLoadingKame(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.isAuthenticated, api.isLoading]);

  // Fetch kame orders when date changes or auth becomes ready
  useEffect(() => {
    fetchKameOrders(selectedInvoiceDate);
  }, [fetchKameOrders, selectedInvoiceDate]);

  useEffect(() => {
    api.getPriceLists().then(({ lists }) => {
      setAvailablePriceLists(lists.map(l => ({ name: l.name, id: l.id })));
    }).catch(() => {});
  }, [api]);

  // Extract order ID from socket payload regardless of field name used by backend.
  const getSocketOrderId = (payload: any): number | null => {
    const raw = payload?.orderId ?? payload?.order_id ?? payload?.id ?? payload?.order?.id;
    const n = parseInt(String(raw), 10);
    return isNaN(n) ? null : n;
  };

  // Transform a single raw API order into the board-card shape.
  // Note: conversation enrichment (company from WA context) is skipped here — acceptable for
  // real-time patches since the vast majority of orders arrive via email/pdf with a name already set.
  const transformApiOrderToCard = (order: ApiOrder, existingCard?: Order): Order => {
    const customerPhone = order.customerPhone || order.customer_phone || '';
    const formattedPhone = customerPhone.trim() ? `+${customerPhone.trim().replace(/^\+/, '')}` : '';
    const customerNameRaw = order.customerName || order.customer_name || '';
    const displayName = customerNameRaw.trim() || existingCard?.company || 'Cliente no registrado';
    const total = Math.round(parseFloat(order.totalAmount?.toString() || order.total_amount?.toString() || '0') * 1.19);
    const orderDate = getOrderDate(order);
    const formattedDate = orderDate ? formatRelativeDate(orderDate) : undefined;
    const docType: string = (order as any).document_type || (order as any).documentType || '';
    const hasEmailIntake = !!(order as any).email_intake_id;
    const hasConversation = !!(order as any).conversation_id;
    const hasPdf = !!(order as any).source_pdf_name || !!(order as any).sourcePdfName;
    let origin: OrderOrigin;
    if (hasEmailIntake || docType === 'Pedido Recibido por Email') origin = 'email';
    else if (hasConversation) origin = 'whatsapp';
    else if (hasPdf) origin = 'pdf';
    else origin = 'manual';
    return {
      orderId: `P-${order.id}`,
      company: displayName,
      customer: formattedPhone,
      productCount: order.items?.length || 0,
      total: `$${total.toLocaleString('es-CL')}`,
      status: mapBackendStatusToFrontend(order.status) as StatusType,
      hasUnread: existingCard ? existingCard.hasUnread : !getSeenSet().has(String(order.id)),
      date: formattedDate,
      updatedAt: order.updated_at || order.updatedAt || order.created_at || order.createdAt,
      kameoneFolio: (order as any).kameoneFolio || (order as any).kameone_folio || undefined,
      customerRut: order.customerRut || null,
      productCodes: (order.items || []).map((item: any) => item.productCode || item.product_code || item.sku || '').filter(Boolean),
      origin,
      documentType: docType || null,
      addressName: (order as any).deliveryAddressName || (order as any).delivery_address_name || null,
      sourceFileName: (order as any).sourcePdfName || (order as any).source_pdf_name || null,
      documentDate: (order as any).documentDate || (order as any).document_date || null,
      priceStatus: computePriceStatus(order.items || []),
    };
  };

  // Patch a single order into both orders and kameOrders state without clearing either list.
  const upsertOrderInState = useCallback((apiOrder: ApiOrder, moveToTop = false) => {
    const boardId = `P-${apiOrder.id}`;
    const frontendStatus = mapBackendStatusToFrontend(apiOrder.status);

    setOrders(prev => {
      if (frontendStatus === 'descartado') {
        return prev.filter(o => o.orderId !== boardId);
      }
      const existing = prev.find(o => o.orderId === boardId);
      const card = transformApiOrderToCard(apiOrder, existing);
      if (moveToTop) return [card, ...prev.filter(o => o.orderId !== boardId)];
      return existing
        ? prev.map(o => o.orderId === boardId ? card : o)
        : [card, ...prev];
    });

    // kameOrders holds confirmed+ orders filtered by date — update in place if present, add if new
    setKameOrders(prev => {
      const existing = prev.find(o => o.orderId === boardId);
      const isKameStatus = frontendStatus === 'facturaGenerada' || frontendStatus === 'facturaEnviada';
      if (!existing && !isKameStatus) return prev;
      const card = transformApiOrderToCard(apiOrder, existing);
      if (existing) {
        return frontendStatus === 'pendiente'
          ? prev.filter(o => o.orderId !== boardId)  // moved back to pending — remove from kame column
          : prev.map(o => o.orderId === boardId ? card : o);
      }
      return isKameStatus ? [card, ...prev] : prev;
    });

    // Flash the card so the remote update is noticeable
    setRecentlyChangedOrderIds(prev => new Set(prev).add(boardId));
    setTimeout(() => {
      setRecentlyChangedOrderIds(prev => {
        const next = new Set(prev);
        next.delete(boardId);
        return next;
      });
    }, 1600);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Realtime: targeted single-order patch on socket events; fall back to full refetch only on failure.
  // Echo guard: skip events caused by this client within the last 3 s.
  useOrgSocket({
    'order.created': async (payload) => {
      if (createOrderInFlightRef.current) return;
      const orderId = getSocketOrderId(payload);
      if (orderId != null && isRecentLocalMutation(orderId)) return;
      if (orderId == null) { fetchOrders(); return; }
      try {
        const fresh = await api.getOrderById(orderId);
        upsertOrderInState(fresh as unknown as ApiOrder);
      } catch { fetchOrders(); fetchKameOrders(selectedInvoiceDate); }
    },
    'order.updated': async (payload) => {
      const orderId = getSocketOrderId(payload);
      if (orderId != null && isRecentLocalMutation(orderId)) return;
      if (orderId == null) { fetchOrders(); return; }
      try {
        const fresh = await api.getOrderById(orderId);
        upsertOrderInState(fresh as unknown as ApiOrder, true);
      } catch { fetchOrders(); fetchKameOrders(selectedInvoiceDate); }
    },
    'order.status_changed': async (payload) => {
      const orderId = getSocketOrderId(payload);
      if (orderId != null && isRecentLocalMutation(orderId)) return;
      if (orderId == null) { fetchOrders(); return; }
      try {
        const fresh = await api.getOrderById(orderId);
        upsertOrderInState(fresh as unknown as ApiOrder);
      } catch { fetchOrders(); fetchKameOrders(selectedInvoiceDate); }
    },
  });

  // Auto-open consult modal when initialOrderId is provided (e.g. from badge click in chats)
  useEffect(() => {
    if (initialOrderId) {
      handleOpenOrderInUnifiedModal(initialOrderId, 'pendiente');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOrderId]);

  // Baseline counters at the time the modal was last opened from closed state
  const arrowFlashBaselineRef = useRef({ left: 0, right: 0 });
  const prevShowModalRef = useRef(false);
  useEffect(() => {
    if (showSingleOrderModal && !prevShowModalRef.current) {
      arrowFlashBaselineRef.current = { left: arrowFlashLeft, right: arrowFlashRight };
    }
    prevShowModalRef.current = showSingleOrderModal;
  }, [showSingleOrderModal, arrowFlashLeft, arrowFlashRight]);

  // URL-driven modal: open/close based on :orderId route param.
  // Wait until the orders list has loaded so we can find the correct status
  // and avoid a redundant getOrderById call racing against fetchOrders.
  const prevUrlOrderIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Don't react while the orders list is still loading — wait for it to settle
    // so orders.find() returns the correct status instead of falling back to the default.
    if (isLoading) return;

    const prev = prevUrlOrderIdRef.current;
    prevUrlOrderIdRef.current = urlOrderId;

    // Open when the URL points at an order that isn't already shown. Checking
    // showSingleOrderModal (not just consultingOrderId) handles the race where the
    // user clicks a card right after closing: consultingOrderId may still hold the
    // just-closed id, so without this the modal would refuse to reopen.
    if (urlOrderId && (urlOrderId !== consultingOrderId || !showSingleOrderModal) && !isProcessingPdf) {
      // URL has an order id — open it
      const order = orders.find(o => o.orderId === urlOrderId);
      if (order?.status === 'pendiente') {
        handleOpenOrderInEditMode(urlOrderId);
      } else {
        handleOpenOrderInUnifiedModal(urlOrderId, (order?.status as StatusType) ?? 'facturaGenerada');
      }
    } else if (!urlOrderId && prev) {
      // URL param was cleared — close the modal
      confirmCloseCreateOrderModal();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlOrderId, isLoading]);

  // Calculate pending orders count
  const pendingOrdersCount = orders.filter(order => order.status === 'pendiente').length;

  // Helper function to filter by search (reusable)
  const filterBySearch = (ordersToFilter: Order[]) => {
    if (!searchValue.trim()) return ordersToFilter;
    const searchLower = searchValue.toLowerCase();
    return ordersToFilter.filter(order =>
      order.orderId.toLowerCase().includes(searchLower) ||
      order.company.toLowerCase().includes(searchLower) ||
      order.customer.toLowerCase().includes(searchLower) ||
      (order.kameoneFolio && order.kameoneFolio.toLowerCase().includes(searchLower)) ||
      (order.customerRut && order.customerRut.toLowerCase().includes(searchLower)) ||
      (order.productCodes && order.productCodes.some(code => code.toLowerCase().includes(searchLower)))
    );
  };

  // Helper function to sort orders
  const sortOrders = (ordersToSort: Order[]) => {
    if (!sortField) return ordersToSort;
    
    return [...ordersToSort].sort((a, b) => {
      let aVal: any = a[sortField as keyof Order];
      let bVal: any = b[sortField as keyof Order];
      
      // Handle numeric values (productCount, total)
      if (sortField === 'productCount') {
        aVal = a.productCount;
        bVal = b.productCount;
      } else if (sortField === 'total') {
        // Extract number from string "$1.234.567"
        aVal = parseFloat(a.total.replace(/[^0-9]/g, '')) || 0;
        bVal = parseFloat(b.total.replace(/[^0-9]/g, '')) || 0;
      } else if (sortField === 'date' || sortField === 'updatedAt') {
        // Sort by date
        aVal = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        bVal = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      } else {
        // Sort strings
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Function to handle sort header click
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter orders based on selected tab (only for table view)
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      if (originFilter !== 'todos' && order.origin !== originFilter) return false;
      if (selectedTab === 'todos') return true;
      if (selectedTab === 'facturaLocal') return order.status === 'facturaGenerada';
      if (selectedTab === 'facturaEnviada') return order.status === 'facturaEnviada';
      if (selectedTab === 'pendientes') return order.status === 'pendiente';
      if (selectedTab === 'descartados') return order.status === 'descartado';
      return true;
    });
  }, [orders, selectedTab, originFilter]);

  // Filtered and sorted orders for table view (tab + search + sort)
  const searchFilteredOrders = useMemo(() => {
    const filtered = filterBySearch(filteredOrders);
    return sortOrders(filtered);
  }, [filteredOrders, searchValue, sortField, sortDirection, originFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filtered orders for board view (only search + origin filter, no tab filter)
  const boardFilteredOrders = useMemo(() => {
    const byOrigin = originFilter === 'todos' ? orders : orders.filter(o => o.origin === originFilter);
    return filterBySearch(byOrigin);
  }, [orders, searchValue, originFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper para formatear fecha (similar a DailyReport)
  const formatDate = (date: Date): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateToCompare = new Date(date);
    dateToCompare.setHours(0, 0, 0, 0);

    if (dateToCompare.getTime() === today.getTime()) {
      return 'Hoy';
    }

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (dateToCompare.getTime() === yesterday.getTime()) {
      return 'Ayer';
    }

    return date.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const changeDate = (days: number) => {
    const newDate = new Date(selectedInvoiceDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedInvoiceDate(newDate);
  };

  const goToToday = () => {
    setSelectedInvoiceDate(new Date());
  };

  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setSelectedInvoiceDate(new Date(e.target.value + 'T00:00:00'));
      setShowDatePicker(false);
    }
  };

  const isToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedInvoiceDate);
    selected.setHours(0, 0, 0, 0);
    return today.getTime() === selected.getTime();
  };

  // Pedidos "Enviado a Kame" — fetched separately via /by-date endpoint
  const filteredInvoiceSentOrders = useMemo(() => {
    // Apply the same search/origin filters used by the board
    let filtered = kameOrders.filter(order => order.status === 'facturaEnviada');
    if (searchValue.trim()) {
      const q = searchValue.toLowerCase();
      filtered = filtered.filter(o =>
        o.company?.toLowerCase().includes(q) ||
        o.customer?.toLowerCase().includes(q) ||
        o.orderId?.toLowerCase().includes(q) ||
        o.kameoneFolio?.toLowerCase().includes(q) ||
        o.customerRut?.toLowerCase().includes(q)
      );
    }
    if (originFilter !== 'todos') {
      filtered = filtered.filter(o => o.origin === originFilter);
    }
    return filtered.sort((a, b) => {
      const aTime = new Date(a.updatedAt || a.date || 0).getTime();
      const bTime = new Date(b.updatedAt || b.date || 0).getTime();
      return bTime - aTime;
    });
  }, [kameOrders, searchValue, originFilter]);

  // Keyboard arrow navigation between orders while modal is open
  useEffect(() => {
    if (!showSingleOrderModal || !consultingOrderId) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const navList =
        consultingOrderStatus === 'pendiente' ? orders.filter(o => o.status === 'pendiente') :
        consultingOrderStatus === 'facturaGenerada' ? orders.filter(o => o.status === 'facturaGenerada') :
        consultingOrderStatus === 'facturaEnviada' ? filteredInvoiceSentOrders :
        null;
      if (!navList) return;
      const currentIdx = navList.findIndex(o => o.orderId === consultingOrderId);
      if (e.key === 'ArrowLeft') {
        const prev = navList[currentIdx - 1];
        if (prev) { setArrowFlashLeft(n => n + 1); navigateToOrder(prev.orderId); }
      } else {
        const next = navList[currentIdx + 1];
        if (next) { setArrowFlashRight(n => n + 1); navigateToOrder(next.orderId); }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [showSingleOrderModal, consultingOrderId, consultingOrderStatus, orders, filteredInvoiceSentOrders, navigate, orderModalMode, selectedCustomer, newOrderItems, isCreatingOrder]);

  // Componente para el selector de fecha en el header
  const DateFilterComponent = () => (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => changeDate(-1)}
        className="flex items-center justify-center p-0.5 rounded-md hover:bg-black/5 transition-colors"
      >
        <CaretLeft size={16} weight="bold" className="text-text-subtle" />
      </button>

      <div className="relative">
        <button
          ref={datePickerButtonRef}
          type="button"
          onClick={() => setShowDatePicker(!showDatePicker)}
          className="flex items-center gap-1.5 px-2 py-0.5 rounded-md hover:bg-black/5 transition-colors font-sans font-medium text-xs text-text"
        >
          <Calendar size={14} weight="bold" className="text-text-subtle" />
          <span>{formatDate(selectedInvoiceDate)}</span>
        </button>

        {showDatePicker && (() => {
          const rect = datePickerButtonRef.current?.getBoundingClientRect();
          return ReactDOM.createPortal(
            <>
              <div
                style={rect ? { position: 'fixed', top: rect.bottom + 4, left: rect.left } : { position: 'fixed', top: 40, left: 0 }}
                className="z-[9999] bg-background border border-border rounded-md shadow-lg p-2"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="date"
                  value={selectedInvoiceDate.toISOString().split('T')[0]}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={handleDatePickerChange}
                  className="font-sans text-xs text-text outline-none border border-border rounded px-2 py-1 bg-background"
                  autoFocus
                />
              </div>
              <div
                className="fixed inset-0 z-[9998]"
                onClick={() => setShowDatePicker(false)}
              />
            </>,
            document.body
          );
        })()}
      </div>

      <button
        type="button"
        onClick={() => changeDate(1)}
        disabled={isToday()}
        className="flex items-center justify-center p-0.5 rounded-md hover:bg-black/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <CaretRight size={16} weight="bold" className="text-text-subtle" />
      </button>

      {!isToday() && (
        <button
          type="button"
          onClick={goToToday}
          className="px-2 py-0.5 rounded-md hover:bg-black/5 transition-colors font-sans font-semibold text-xs text-text"
        >
          Hoy
        </button>
      )}
    </div>
  );

  const resetOrderModalState = (closeModal = true, keepLoadingState = false) => {
    pdfSessionRef.current += 1; // invalidate any in-flight PDF fetch
    setNewOrderItems([]);
    setSelectedCustomer(null);
    setSelectedPdfFile(null);
    setPdfOrderNumber(null);
    setPdfOrderDate(null);
    setExtractionCorrelationId(null);
    setPdfProcessingError(null);
    setHasAttemptedSave(false);
    setSaveAttemptCount(0);
    setHasConfirmedAddressWarning(false);
    if (!keepLoadingState) setIsProcessingPdf(false);
    setIsMultiPdfProcessing(false);
    setMultiPdfFiles([]);
    setMultiPdfCurrentIndex(0);
    setConsultingPdfUrl(null);
    setConsultingPdfName(null);
    if (!keepLoadingState) setOrderModalMode('create');
    setOrderTipoDocumento('VENTA');
    setConsultingOrderId(null);
    setConsultingOrderStatus(null);
    setConsultingOrderDocType(null);
    setAddressSource(null);
    setSelectedAddressId(null);
    setSelectedAddressName(null);
    setSaveError(null);
    setKameResponse(null);
    setInvoiceOrderId(null);
    setLineItemProductSearch({});
    setLineItemProductResults({});
    setLineItemProductLoading({});
    setClientDbAddresses([]);
    setNewAddressNombre('');
    setNewAddressContacto('');
    setNewAddressInput('');
    setNewAddressComuna('');
    setNewAddressCity('');
    setEditingAddressId(null);
    setIsEditingFolio(false);
    setFolioInput('');
    setFolioManuallyEdited(false);
    setEditingCustomerName(false);
    setCustomerSearch('');
    setDocumentDate(new Date().toISOString().split('T')[0]);
    originalOrderSnapshotRef.current = null;
    prefetchRef.current.clear();
    if (closeModal) setShowSingleOrderModal(false);
  };

  const mapApiItemToEditable = (item: any, orderId: number, savedPriceListName?: string | null): OrderItem => {
    // item.priceSource is the canonical stored value (e.g. 'catalog', 'price_list').
    // Only fall back to 'price_list' when there is no stored source at all — never
    // override an existing 'catalog'/'pdf'/etc. source just because a price list exists.
    const storedSource = item.pdfPriceSource ?? item.priceSource ?? null;
    const pdfPriceSource: 'price_list' | 'pdf' | 'catalog' | 'price_list_missing' | 'manual' =
      storedSource ?? (savedPriceListName ? ('price_list' as const) : ('catalog' as const));
    const savedPrice = parseFloat(String(item.unitPrice ?? item.unit_price ?? '0'));

    // Reconstruct priceOptions from the persisted per-item source prices
    // (display_price/oc_price/price_list_price). These are now stored, so the
    // popover can be fully rebuilt with all the sources the user had at creation.
    const toNum = (v: any): number | null => {
      if (v == null) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const kamePrice = toNum(item.displayPrice ?? item.display_price);
    const ocPrice = toNum(item.ocPrice ?? item.oc_price);
    const listPrice = toNum(item.priceListPrice ?? item.price_list_price);

    const priceOptions: OrderItem['priceOptions'] = {};
    if (kamePrice != null) priceOptions.kame = kamePrice;
    if (ocPrice != null) priceOptions.oc = ocPrice;
    if (listPrice != null) priceOptions.priceList = listPrice;

    const priceSource: OrderItem['priceSource'] =
      pdfPriceSource === 'price_list'           ? 'price_list'
      : pdfPriceSource === 'pdf'                ? 'oc'
      : pdfPriceSource === 'catalog'            ? 'kame'
      : pdfPriceSource === 'price_list_missing' ? 'kame'
      : pdfPriceSource === 'manual'             ? 'manual'
      : undefined;

    // Legacy fallback: orders saved before source prices were persisted only have
    // the one winning price — seed the active slot from it so the popover still opens.
    if (kamePrice == null && ocPrice == null && listPrice == null) {
      if (priceSource === 'price_list') priceOptions.priceList = savedPrice;
      else if (priceSource === 'oc')   priceOptions.oc = savedPrice;
      else if (priceSource === 'kame') priceOptions.kame = savedPrice;
    }

    return {
      id: item.id,
      orderId: item.orderId || item.order_id || orderId,
      productId: item.productId || item.product_id || 0,
      quantity: item.quantity,
      quantityUnit: item.quantityUnit ?? item.quantity_unit ?? 'unidad',
      unitPrice: String(item.unitPrice ?? item.unit_price ?? '0'),
      pdfLineText: item.pdfLineText ?? item.pdf_line_text ?? undefined,
      pdfMatchQuality: item.matchQuality ?? item.pdfMatchQuality ?? undefined,
      pdfPriceSource,
      descripcionDetallada: item.descripcionDetallada ?? item.descripcion_detallada ?? undefined,
      product: item.product
        ? {
            id: item.product.id,
            name: item.product.name || item.product.product_name || '',
            sku: item.product.sku || item.product.product_sku || '',
            price: savedPrice,
          }
        : undefined,
      ...(priceSource ? { priceSource } : {}),
      ...(Object.keys(priceOptions).length > 0 ? { priceOptions } : {}),
    };
  };

  const handleOpenOrderInUnifiedModal = async (orderId: string, status: StatusType) => {
    setIsProcessingPdf(true);
    setOrderModalMode('consult');
    resetOrderModalState(!showSingleOrderModal, true);
    const session = pdfSessionRef.current;
    setConsultingOrderId(orderId);
    setConsultingOrderStatus(status);
    setConsultingOrderDocType(null);
    setShowSingleOrderModal(true);

    try {
      const numericId = parseInt(orderId.replace('P-', ''), 10);
      const orderData = await api.getOrderById(numericId);
      if (pdfSessionRef.current !== session) return;

      setConsultingOrderDocType((orderData as any).document_type || (orderData as any).documentType || null);

      const items: OrderItem[] = (orderData.items || []).map((item: any) =>
        mapApiItemToEditable(item, numericId)
      );

      setNewOrderItems(items);
      scrollToFirstYellowItem();

      // Store PDF url if the order has one
      if (orderData.sourcePdfName) {
        const pdfUrl = orderData.pdfUrl
          ? `${process.env.REACT_APP_API_URL || ''}${orderData.pdfUrl}`
          : `${process.env.REACT_APP_API_URL || ''}/api/v1/orders/${numericId}/pdf`;
        setConsultingPdfUrl(pdfUrl);
        setConsultingPdfName(orderData.sourcePdfName);
      } else {
        setConsultingPdfUrl(null);
        setConsultingPdfName(null);
      }

      setPdfOrderNumber((orderData as any).poNumber || (orderData as any).po_number || null);
      setPdfOrderDate((orderData as any).poDate || (orderData as any).po_date || null);

      // Build customer ficha from stored order data — no KAME lookup needed
      const customerRut = orderData.customerRut;
      const customerName = orderData.customerName || orderData.customer_name || '';
      const customerPhone = (orderData.customerPhone || orderData.customer_phone || '').replace(/^\+*/, (m) => m.length > 0 ? '+' : '');

      if (customerRut || customerName) {
        const ficha: KameoneFicha = {
          Rut: customerRut || '',
          RazonSocial: customerName,
          Telefono: customerPhone,
          Email: (orderData as any).customerEmail || (orderData as any).customer_email || '',
          Giro: (orderData as any).customerGiro || (orderData as any).customer_giro || '',
          Direccion: (orderData as any).deliveryAddress || (orderData as any).delivery_address || orderData.address || '',
          Comuna: (orderData as any).deliveryComuna || (orderData as any).delivery_comuna || '',
          Ciudad: (orderData as any).deliveryCity || (orderData as any).delivery_city || '',
        } as any;
        if (pdfSessionRef.current !== session) return;
        setSelectedCustomer(ficha);
      }
    } catch (err) {
    } finally {
      setIsProcessingPdf(false);
    }
  };

  const handleOpenOrderInEditMode = async (orderId: string) => {
    setIsProcessingPdf(true);
    setOrderModalMode('edit');
    resetOrderModalState(!showSingleOrderModal, true);
    const session = pdfSessionRef.current;
    setConsultingOrderId(orderId);
    setConsultingOrderStatus('pendiente');
    setShowSingleOrderModal(true);

    const numericId = parseInt(orderId.replace('P-', ''), 10);
    let orderData: ApiOrder;
    try {
      orderData = await api.getOrderById(numericId);
    } catch (err) {
      setIsProcessingPdf(false);
      setShowSingleOrderModal(false);
      return;
    }

    if (pdfSessionRef.current !== session) return;

    const savedPriceListName: string | null = (orderData as any).priceListName ?? null;

    const items: OrderItem[] = (orderData.items || []).map((item: any) =>
      mapApiItemToEditable(item, numericId, savedPriceListName)
    );

    setNewOrderItems(items);
    scrollToFirstYellowItem();

    // Folio for pendiente: last order successfully sent to Kame + 1
    api.getNextFolio(undefined, 'last_kame').then((folio) => {
      setAssignedFolio(String(folio));
    }).catch(() => {});

    if (orderData.sourcePdfName) {
      const pdfUrl = (orderData as any).pdfUrl
        ? `${process.env.REACT_APP_API_URL || ''}${(orderData as any).pdfUrl}`
        : `${process.env.REACT_APP_API_URL || ''}/api/v1/orders/${numericId}/pdf`;
      setConsultingPdfUrl(pdfUrl);
      setConsultingPdfName(orderData.sourcePdfName);
    }

    setPdfOrderNumber((orderData as any).poNumber || (orderData as any).po_number || null);
    setPdfOrderDate((orderData as any).poDate || (orderData as any).po_date || null);
    if ((orderData as any).documentDate) setDocumentDate((orderData as any).documentDate);

    const customerRut = orderData.customerRut;
    const customerName = orderData.customerName || (orderData as any).customer_name || '';
    const customerPhone = (orderData.customerPhone || (orderData as any).customer_phone || '').replace(/^\+*/, (m: string) => m.length > 0 ? '+' : '');

    if (customerRut || customerName) {
      const savedAddressName: string | null = (orderData as any).deliveryAddressName || (orderData as any).delivery_address_name || null;
      const savedContacto: string = (orderData as any).deliveryRecipientName || (orderData as any).delivery_recipient_name || '';

      const ficha: KameoneFicha = {
        Rut: customerRut || '',
        RazonSocial: customerName,
        Telefono: customerPhone,
        Email: (orderData as any).customerEmail || (orderData as any).customer_email || '',
        Giro: (orderData as any).customerGiro || (orderData as any).customer_giro || '',
        Direccion: (orderData as any).deliveryAddress || (orderData as any).delivery_address || (orderData as any).address || '',
        Comuna: (orderData as any).deliveryComuna || (orderData as any).delivery_comuna || '',
        Ciudad: (orderData as any).deliveryCity || (orderData as any).delivery_city || '',
        Contacto: savedContacto || undefined,
        ListaDePrecio: savedPriceListName || undefined,
      } as any;
      setSelectedCustomer(ficha);

      const clientAddresses = (orderData as any).clientAddresses as import('../services/api').ClientAddress[] | undefined;
      const savedDeliveryAddress = ((orderData as any).deliveryAddress || (orderData as any).delivery_address || '').trim();

      let resolvedAddressName: string | null = savedAddressName;
      if (savedAddressName) {
        setSelectedAddressName(savedAddressName);
        setAddressSource('user');
        const matched = clientAddresses?.find(a => a.nombre === savedAddressName) ?? null;
        if (matched) setSelectedAddressId(matched.id);
      } else if (clientAddresses?.length && savedDeliveryAddress) {
        // No saved address name — try to match by address string against client_addresses
        const matched = clientAddresses.find(a => a.address.trim() === savedDeliveryAddress) ?? null;
        if (matched) {
          resolvedAddressName = matched.nombre || null;
          setSelectedAddressName(resolvedAddressName);
          setSelectedAddressId(matched.id);
          setAddressSource('user');
        }
      }

      // Capture baseline snapshot from raw order data so we can detect unsaved changes on close.
      // Built directly from orderData to avoid React state timing issues.
      originalOrderSnapshotRef.current = JSON.stringify({
        items: (orderData.items || []).map((item: any) => ({
          productId: item.product?.id ?? item.productId ?? item.product_id ?? null,
          quantity: Number(item.quantity) || 0,
          unitPrice: Number(item.unitPrice ?? item.unit_price) || 0,
        })),
        customerRut: (customerRut ?? '').trim() || null,
        customerName: (customerName ?? '').trim() || null,
        direccion: ((orderData as any).deliveryAddress || (orderData as any).delivery_address || '').trim() || null,
        comuna: ((orderData as any).deliveryComuna || (orderData as any).delivery_comuna || '').trim() || null,
        ciudad: ((orderData as any).deliveryCity || (orderData as any).delivery_city || '').trim() || null,
        contacto: (savedContacto ?? '').trim() || null,
        addressName: (resolvedAddressName ?? '').trim() || null,
        ocNumber: ((orderData as any).poNumber || (orderData as any).po_number || '').trim() || null,
        ocDate: ((orderData as any).poDate || (orderData as any).po_date || '').trim() || null,
        documentDate: ((orderData as any).documentDate || (orderData as any).document_date || new Date().toISOString().split('T')[0]).trim() || null,
      });
    }

    setIsProcessingPdf(false);
  };

  const handleDuplicateOrder = async (orderId: string) => {
    resetOrderModalState();
    const session = pdfSessionRef.current;
    setIsProcessingPdf(true);
    setShowSingleOrderModal(true);

    try {
      const numericId = parseInt(orderId.replace('P-', ''), 10);
      const orderData = await api.getOrderById(numericId);
      if (pdfSessionRef.current !== session) return;

      // Build fresh line items — new ids, strip pdf metadata
      const items: OrderItem[] = (orderData.items || []).map((item: any, index: number) => ({
        id: Date.now() + index,
        orderId: 0,
        productId: item.productId || item.product_id || 0,
        quantity: item.quantity,
        unitPrice: String(item.unitPrice ?? item.unit_price ?? '0'),
        product: item.product
          ? {
              id: item.product.id,
              name: item.product.name || item.product.product_name || '',
              sku: item.product.sku || item.product.product_sku || '',
              price: parseFloat(String(item.unitPrice ?? item.unit_price ?? '0')),
            }
          : undefined,
      }));
      setNewOrderItems(items);
      scrollToFirstYellowItem();

      // Build customer ficha — same mapping as edit/consult handlers
      const customerRut = orderData.customerRut;
      const customerName = orderData.customerName || orderData.customer_name || '';
      const customerPhone = (orderData.customerPhone || orderData.customer_phone || '').replace(/^\+*/, (m) => m.length > 0 ? '+' : '');

      if (customerRut || customerName) {
        const ficha: KameoneFicha = {
          Rut: customerRut || '',
          RazonSocial: customerName,
          Telefono: customerPhone,
          Email: (orderData as any).customerEmail || (orderData as any).customer_email || '',
          Giro: (orderData as any).customerGiro || (orderData as any).customer_giro || '',
          Direccion: (orderData as any).deliveryAddress || (orderData as any).delivery_address || orderData.address || '',
          Comuna: (orderData as any).deliveryComuna || (orderData as any).delivery_comuna || '',
          Ciudad: (orderData as any).deliveryCity || (orderData as any).delivery_city || '',
        } as any;
        if (pdfSessionRef.current !== session) return;
        setSelectedCustomer(ficha);
      }
      // Folio auto-fetched by useEffect when modal opens in create mode
      // poNumber, poDate, selectedPdfFile intentionally left blank
    } catch (err) {
      console.error('Error duplicating order:', err);
    } finally {
      setIsProcessingPdf(false);
    }
  };

  const handleOrderClick = (orderId: string) => {
    if (isMobileDevice && onOrderClick) {
      onOrderClick(orderId);
    } else {
      navigate(`/orders/${orderId}`);
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    const numericId = parseInt(orderId.replace('P-', ''));
    setOrders(prev => prev.filter(o => o.orderId !== orderId));
    try {
      await api.updateOrderStatus(numericId, 'cancelled');
      markLocalMutation(numericId);
      console.log(`Order ${orderId} discarded`);
    } catch (err) {
      console.error('Error discarding order on server:', err);
    }
  };


  // Función para manejar la creación de pedidos
  const handleAddOrders = async () => {
    if (selectedFiles.length === 0) return;
    
    // TODO: Aquí irá la lógica para procesar los archivos y crear los pedidos
    
    // Simular creación de pedidos
    // await api.createOrdersFromFiles(selectedFiles);
    
    setIsAddOrdersModalOpen(false);
    setSelectedFiles([]);
    
    // Recargar pedidos después de crear
    // fetchOrders();
  };

  // Búsqueda de productos por línea (debounce por id de ítem) — modal nuevo pedido
  useEffect(() => {
    if (!showSingleOrderModal || !api.isAuthenticated || api.isLoading) return;

    const timeouts: Record<number, ReturnType<typeof setTimeout>> = {};

    Object.keys(lineItemProductSearch).forEach((idStr) => {
      const itemId = Number(idStr);
      const search = lineItemProductSearch[itemId] || '';

      if (!search.trim()) {
        setLineItemProductResults((prev) => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });
        setLineItemProductLoading((prev) => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });
        return;
      }

      setLineItemProductLoading((prev) => ({ ...prev, [itemId]: true }));

      timeouts[itemId] = setTimeout(async () => {
        try {
          const result = await api.getProducts(search.trim(), 20, 1);
          setLineItemProductResults((prev) => ({ ...prev, [itemId]: result.data || [] }));
        } catch (error) {
          setLineItemProductResults((prev) => ({ ...prev, [itemId]: [] }));
        } finally {
          setLineItemProductLoading((prev) => ({ ...prev, [itemId]: false }));
        }
      }, 300);
    });

    return () => {
      Object.values(timeouts).forEach((t) => {
        if (t) clearTimeout(t);
      });
    };
  }, [lineItemProductSearch, showSingleOrderModal, api.isAuthenticated, api.isLoading, api]);

  useEffect(() => {
    if (!showSingleOrderModal) {
      setLineItemProductSearch({});
      setLineItemProductResults({});
      setLineItemProductLoading({});
      setAssignedFolio('');
      setIsEditingFolio(false);
      setFolioInput('');
      setFolioManuallyEdited(false);
    }
  }, [showSingleOrderModal]);

  // Prefetch next folio when the create modal opens (skip if user already edited it manually)
  useEffect(() => {
    if (!showSingleOrderModal || orderModalMode !== 'create') return;
    if (!api.isAuthenticated || api.isLoading) return;
    if (folioManuallyEdited) return;

    api.getNextFolio(undefined, 'last_created').then((folio) => {
      setAssignedFolio(String(folio));
    }).catch(() => {
      // Non-critical — folio badge stays empty
    });
  }, [showSingleOrderModal, orderModalMode, api, api.isAuthenticated, api.isLoading, folioManuallyEdited]);

  // Reset customer highlight when results change
  useEffect(() => {
    setActiveCustomerIndex(-1);
  }, [customers]);

  // Scroll highlighted customer into view
  useEffect(() => {
    if (activeCustomerIndex < 0 || !customerListPopoverRef.current) return;
    const el = customerListPopoverRef.current.querySelectorAll<HTMLElement>('[data-kbd-item]')[activeCustomerIndex];
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeCustomerIndex]);

  // Search customers — show recent cache instantly, merge with debounced Kameone results
  useEffect(() => {
    if (!showCustomerDialog && !editingCustomerName) {
      if (customerSearch || customers.length > 0) {
        setCustomers([]);
        setCustomerSearch('');
      }
      return;
    }

    if (!api.isAuthenticated || api.isLoading) return;

    if (customerSearchTimeoutRef.current) {
      clearTimeout(customerSearchTimeoutRef.current);
    }

    if (!customerSearch.trim()) {
      // Show up to 5 recently used clients when search is empty
      setCustomers(recentFichas.slice(0, 5));
      setIsLoadingCustomers(false);
      return;
    }

    const searchValue = customerSearch.trim();
    const q = searchValue.toLowerCase();
    const words = q.split(/\s+/).filter(Boolean);

    // Instantly show recent cache matches
    const cacheMatches = recentFichas.filter((f) => {
      const hay = [f.RazonSocial, f.RznSocial, f.Rut].filter(Boolean).join(' ').toLowerCase();
      return words.every((w) => hay.includes(w));
    });
    setCustomers(cacheMatches);
    setIsLoadingCustomers(true);

    // Debounced Kameone search — merge results, recent ones first
    customerSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const isRutSearch = /^[\d.-]+$/.test(searchValue);
        const result = isRutSearch
          ? await api.getKameoneFichas(searchValue)
          : await api.getKameoneFichas(undefined, searchValue);
        const fresh = result || [];
        const cacheRuts = new Set(cacheMatches.map((f) => f.Rut));
        const merged = [...cacheMatches, ...fresh.filter((f) => !cacheRuts.has(f.Rut))];
        setCustomers(merged);
      } catch {
        // Keep cache results on error
      } finally {
        setIsLoadingCustomers(false);
      }
    }, 300);

    return () => {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearch, showCustomerDialog, editingCustomerName, api, api.isAuthenticated, api.isLoading, recentFichas]);

  // Position customer popover below search field (solo para desktop)
  useEffect(() => {
    if (isMobile || !showCustomerDialog || !customerSearchFieldRef.current || !customerListPopoverRef.current) {
      return;
    }

    const updatePosition = () => {
      if (!customerSearchFieldRef.current || !customerListPopoverRef.current) return;
      
      const searchField = customerSearchFieldRef.current;
      const popover = customerListPopoverRef.current;
      const rect = searchField.getBoundingClientRect();
      
      popover.style.position = 'fixed';
      popover.style.top = `${rect.bottom + 8}px`;
      popover.style.left = `${rect.left}px`;
      popover.style.width = `${rect.width}px`;
      popover.style.zIndex = '1000';
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showCustomerDialog, customerSearch, customers, isMobile]);

  // Close customer popover on outside click (solo para desktop)
  useEffect(() => {
    if (isMobile || !showCustomerDialog || selectedCustomer) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        customerSearchFieldRef.current?.contains(event.target as Node) ||
        customerListPopoverRef.current?.contains(event.target as Node)
      ) {
        return;
      }
      setCustomerSearch('');
      setCustomers([]);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCustomerDialog, customerSearch, selectedCustomer, isMobile]);

  // Close customer edit field on outside click
  useEffect(() => {
    if (!editingCustomerName) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (customerSearchFieldRef.current?.contains(event.target as Node)) {
        return;
      }
      setEditingCustomerName(false);
      setCustomerSearch('');
      setCustomers([]);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editingCustomerName]);

  // Reset customer dialog state when closed
  useEffect(() => {
    if (!showCustomerDialog && !editingCustomerName) {
      resetCustomerDialog();
    }
  }, [showCustomerDialog, editingCustomerName]);

  // Función para validar el formulario de "Un solo pedido"
  const isFacturaEnviada = consultingOrderStatus === 'facturaEnviada';

  const hasAddressWarning = () =>
    !!selectedCustomer && (
      !selectedCustomer.Contacto?.trim() ||
      !selectedAddressName?.trim() ||
      !selectedCustomer.Direccion?.trim() ||
      !selectedCustomer.Comuna?.trim() ||
      !selectedCustomer.Ciudad?.trim()
    );

  const isItemValid = (item: OrderItem) =>
    item.product != null &&
    item.quantity != null && item.quantity > 0 &&
    item.unitPrice != null && item.unitPrice !== '';

  const isFormValid = (isDraft = false) => {
    if (newOrderItems.length === 0) return false;
    if (!newOrderItems.every(isItemValid)) return false;
    if (!isDraft && selectedPdfFile) {
      if (!pdfOrderNumber?.trim()) return false;
      if (!pdfOrderDate?.trim()) return false;
    }
    return true;
  };

  // Normalized snapshot of all editable order fields — used to detect unsaved changes.
  const buildOrderDraftSnapshot = () => JSON.stringify({
    items: newOrderItems.map(item => ({
      productId: item.product?.id ?? null,
      quantity: Number(item.quantity) || 0,
      unitPrice: Number(item.unitPrice) || 0,
    })),
    customerRut: selectedCustomer?.Rut?.trim() || null,
    customerName: selectedCustomer?.RazonSocial?.trim() || null,
    direccion: selectedCustomer?.Direccion?.trim() || null,
    comuna: selectedCustomer?.Comuna?.trim() || null,
    ciudad: selectedCustomer?.Ciudad?.trim() || null,
    contacto: selectedCustomer?.Contacto?.trim() || null,
    addressName: selectedAddressName?.trim() || null,
    ocNumber: pdfOrderNumber?.trim() || null,
    ocDate: pdfOrderDate?.trim() || null,
    documentDate: documentDate?.trim() || null,
  });

  // Función helper para obtener el estado de error de un campo
  const getFieldError = (field: 'items' | 'pdfOrderNumber' | 'pdfOrderDate' | 'contacto' | 'addressName' | 'address' | 'comuna' | 'ciudad') => {
    if (!hasAttemptedSave) return false;
    switch (field) {
      case 'items':
        return newOrderItems.length === 0 || !newOrderItems.every(isItemValid);
      case 'pdfOrderNumber':
        return !!selectedPdfFile && !pdfOrderNumber?.trim();
      case 'pdfOrderDate':
        return !!selectedPdfFile && !pdfOrderDate?.trim();
      case 'contacto':
        return !!selectedCustomer && !selectedCustomer.Contacto?.trim();
      case 'addressName':
        return !!selectedCustomer && !selectedAddressName?.trim();
      case 'address':
        return !!selectedCustomer && !selectedCustomer.Direccion?.trim();
      case 'comuna':
        return !!selectedCustomer && !selectedCustomer.Comuna?.trim();
      case 'ciudad':
        return !!selectedCustomer && !selectedCustomer.Ciudad?.trim();
      default:
        return false;
    }
  };

  // Cierra el modal y limpia TODO el estado — sin excepción, sin avanzar al siguiente PDF
  const confirmCloseCreateOrderModal = () => {
    const navTarget = pendingNavOrderId;
    const skipIndex = pendingSkipIndex;
    setPendingNavOrderId(null);
    setPendingSkipIndex(null);
    setShowCloseConfirmDialog(false);
    if (skipIndex !== null) {
      processNextPdfFile(multiPdfFiles, skipIndex);
      return;
    }
    setShowSingleOrderModal(false);
    setOrderModalMode('create');
    setOrderTipoDocumento('VENTA');
    setConsultingOrderId(null);
    setConsultingOrderStatus(null);
    setConsultingOrderDocType(null);
    resetOrderModalState(); // limpia PDF, items, cliente, multi-pdf batch, prefetch
    if (onInitialOrderClose) onInitialOrderClose();
    if (navTarget) navigate(`/orders/${navTarget}`);
    else if (urlOrderId) navigate('/orders');
  };

  const navigateToOrder = (orderId: string) => {
    if (orderModalMode === 'consult') {
      navigate(`/orders/${orderId}`);
      return;
    }
    const isDirty = orderModalMode === 'edit' && originalOrderSnapshotRef.current !== null
      ? originalOrderSnapshotRef.current !== buildOrderDraftSnapshot()
      : selectedCustomer !== null || newOrderItems.length > 0;
    if (!isDirty || isCreatingOrder) {
      navigate(`/orders/${orderId}`);
    } else {
      setPendingNavOrderId(orderId);
      setShowCloseConfirmDialog(true);
    }
  };

  const handleCloseCreateOrderModal = () => {
    // In consult mode, always close immediately — no confirm dialog
    if (orderModalMode === 'consult') {
      confirmCloseCreateOrderModal();
      return;
    }

    // In edit mode, only warn if the user actually changed something
    if (orderModalMode === 'edit' && originalOrderSnapshotRef.current !== null) {
      const currentSnap = buildOrderDraftSnapshot();
      const isDirty = originalOrderSnapshotRef.current !== currentSnap;
if (!isDirty || isCreatingOrder) {
        confirmCloseCreateOrderModal();
        return;
      }
      setShowCloseConfirmDialog(true);
      return;
    }

    const hasData = selectedCustomer !== null || newOrderItems.length > 0;
    if (hasData && !isCreatingOrder) {
      setShowCloseConfirmDialog(true);
    } else {
      confirmCloseCreateOrderModal();
    }
  };

  // Helper function to sort products
  const sortProducts = (items: OrderItem[]) => {
    if (!productSortField) return items;
    
    return [...items].sort((a, b) => {
      let aVal: any;
      let bVal: any;
      
      if (productSortField === 'name') {
        const aName = a.product?.name || '';
        const bName = b.product?.name || '';
        aVal = aName.toLowerCase();
        bVal = bName.toLowerCase();
      } else if (productSortField === 'quantity') {
        aVal = a.quantity || 0;
        bVal = b.quantity || 0;
      } else if (productSortField === 'unitPrice') {
        aVal = parseFloat(a.unitPrice || '0');
        bVal = parseFloat(b.unitPrice || '0');
      } else if (productSortField === 'total') {
        const aQty = a.quantity || 0;
        const aPrice = parseFloat(a.unitPrice || '0');
        const bQty = b.quantity || 0;
        const bPrice = parseFloat(b.unitPrice || '0');
        aVal = aQty * aPrice;
        bVal = bQty * bPrice;
      }
      
      if (aVal < bVal) return productSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return productSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleProductSort = (field: string) => {
    if (productSortField === field) {
      setProductSortDirection(productSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setProductSortField(field);
      setProductSortDirection('asc');
    }
  };


  const clearLineItemSearchKeys = useCallback((itemId: number) => {
    setLineItemProductSearch((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    setLineItemProductResults((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    setLineItemProductLoading((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }, []);

  const scrollToFirstYellowItem = useCallback(() => {
    setTimeout(() => {
      const el = document.querySelector<HTMLElement>('[data-yellow-item]');
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 100);
  }, []);

  const addEmptyLineItem = useCallback(() => {
    setNewOrderItems((prev) => [
      ...prev,
      {
        id: Date.now() + Math.floor(Math.random() * 10000),
        orderId: 0,
        productId: 0,
        quantity: 1,
        unitPrice: '0',
        product: undefined,
      },
    ]);
    setCountFlash(true);
    setTimeout(() => setCountFlash(false), 600);

    // Scroll to bottom to show the newly created product input
    setTimeout(() => {
      if (modalScrollableRef.current) {
        modalScrollableRef.current.scrollTo({
          top: modalScrollableRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 0);
  }, []);

  // Keyboard shortcut: press "a" to add a product line when the button is active
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'a' && e.key !== 'A') return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (!showSingleOrderModal) return;
      if (isFacturaEnviada || isProcessingPdf || !selectedCustomer) return;
      e.preventDefault();
      addEmptyLineItem();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showSingleOrderModal, isFacturaEnviada, isProcessingPdf, selectedCustomer, addEmptyLineItem]);

  const applyProductToLineItem = useCallback(
    async (itemId: number, product: Product) => {
      const minQ = product.minOrderQuantity || 1;
      const catalogPrice = product.price.toString();

      // Set product immediately with catalog price so the total is never $0
      setNewOrderItems((prev) =>
        prev.map((item) => {
          if (item.id !== itemId) return item;
          const q = Math.max(item.quantity || 1, minQ);
          return {
            ...item,
            productId: product.id,
            product,
            quantity: q,
            unitPrice: catalogPrice,
            pdfPriceSource: undefined,
            priceSource: 'kame' as const,
            priceOptions: { kame: product.price },
          };
        })
      );
      clearLineItemSearchKeys(itemId);
      setTimeout(() => {
        const qtyInput = document.querySelector<HTMLInputElement>(`[data-qty-item-id="${itemId}"]`);
        if (qtyInput) { qtyInput.focus(); qtyInput.select(); }
      }, 50);

      // If customer has a price list, resolve the price and update — but only if the
      // user hasn't manually edited the price in the meantime (unitPrice still = catalogPrice)
      const listName = selectedCustomer?.ListaDePrecio;
      if (listName) {
        try {
          const priceMap = await api.resolvePricesForPriceList(listName, [product.id]);
          if (priceMap?.[product.id]) {
            const resolvedPrice = String(priceMap[product.id]);
            setNewOrderItems((prev) =>
              prev.map((item) => {
                if (item.id !== itemId) return item;
                // Don't overwrite if user already changed the price manually
                if (item.unitPrice !== catalogPrice) return item;
                return {
                  ...item,
                  unitPrice: resolvedPrice,
                  pdfPriceSource: 'price_list' as const,
                  priceSource: 'price_list' as const,
                  priceOptions: {
                    ...item.priceOptions,
                    priceList: Number(resolvedPrice),
                  },
                };
              })
            );
          } else if (priceMap) {
            // Product not in the price list — mark as price_list_missing so the dot shows
            setNewOrderItems((prev) =>
              prev.map((item) => {
                if (item.id !== itemId) return item;
                return {
                  ...item,
                  pdfPriceSource: 'price_list_missing' as const,
                  priceSource: 'kame' as const,
                  priceOptions: { ...item.priceOptions, priceList: null },
                };
              })
            );
          }
        } catch {
          // keep catalog price on error
        }
      }
    },
    [clearLineItemSearchKeys, selectedCustomer, api]
  );

  const clearProductFromLineItem = useCallback(
    (itemId: number) => {
      setNewOrderItems((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? { ...item, productId: 0, product: undefined, unitPrice: '0' }
            : item
        )
      );
      clearLineItemSearchKeys(itemId);
    },
    [clearLineItemSearchKeys]
  );

  const updateLineItemQuantity = useCallback((itemId: number, raw: string) => {
    if (raw !== '' && !/^\d+$/.test(raw)) return;
    setNewOrderItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        if (raw === '') return { ...item, quantity: 0 };
        const n = parseInt(raw, 10);
        if (isNaN(n)) return item;
        return { ...item, quantity: n };
      })
    );
  }, []);

  const updateLineItemUnit = useCallback((itemId: number, unit: QuantityUnit) => {
    setNewOrderItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, quantityUnit: unit } : item))
    );
  }, []);

  const updateLineItemUnitPrice = useCallback((itemId: number, value: string) => {
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setNewOrderItems((prev) =>
      // A manual edit also flips the dot to gray (pdfPriceSource drives the indicator).
      prev.map((item) => (item.id === itemId ? { ...item, unitPrice: value, priceSource: 'manual' as const, pdfPriceSource: 'manual' as const } : item))
    );
  }, []);

  const handlePriceSourceSelect = useCallback(
    (itemId: number, price: number, source: PriceOptionSource) => {
      // Map the popover source to the stored dot enum so the indicator updates live.
      const pdfPriceSource =
        source === 'price_list' ? ('price_list' as const)
        : source === 'oc'       ? ('pdf' as const)
        : ('catalog' as const); // 'kame' → catalog price
      setNewOrderItems((prev) =>
        prev.map((item) =>
          item.id !== itemId
            ? item
            : { ...item, unitPrice: String(price), priceSource: source, pdfPriceSource }
        )
      );
    },
    []
  );

  const updateLineItemDescripcion = useCallback((itemId: number, value: string) => {
    setNewOrderItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, descripcionDetallada: value } : item))
    );
  }, []);

  const handleLineItemSearchClear = useCallback((itemId: number) => {
    setLineItemProductSearch((prev) => ({ ...prev, [itemId]: '' }));
    setLineItemProductResults((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    setLineItemProductLoading((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }, []);

  // Detect per-item price/quantity changes and flash only the affected fields
  useEffect(() => {
    const prev = prevItemsRef.current;
    const changed = new Map<number, { price: boolean; total: boolean }>();
    newOrderItems.forEach((item) => {
      const old = prev.find((p) => p.id === item.id);
      if (!old) return;
      const priceChanged = old.unitPrice !== item.unitPrice;
      const qtyChanged = old.quantity !== item.quantity;
      if (priceChanged || qtyChanged) {
        changed.set(item.id, { price: priceChanged, total: true });
      }
    });
    prevItemsRef.current = newOrderItems;
    if (changed.size === 0) return;
    setFlashItemData(changed);
    const t = setTimeout(() => setFlashItemData(new Map()), 800);
    return () => clearTimeout(t);
  }, [newOrderItems]);

  const handleDeleteItem = (itemId: number) => {
    const deletedItem = newOrderItems.find((item) => item.id === itemId);
    const deletedTotal = (deletedItem?.quantity ?? 0) * parseFloat(String(deletedItem?.unitPrice ?? '0') || '0');
    setNewOrderItems((prev) => prev.filter((item) => item.id !== itemId));
    clearLineItemSearchKeys(itemId);
    setCountFlash(true);
    setTimeout(() => setCountFlash(false), 600);
    if (Number.isFinite(deletedTotal) && deletedTotal > 0) {
      setTotalsFlash(true);
      setTimeout(() => setTotalsFlash(false), 600);
    }
  };

  // Load addresses from DB for the selected customer's RUT
  const loadClientDbAddresses = useCallback(async (rut: string) => {
    try {
      setLoadingClientAddresses(true);
      const addrs = await api.getClientAddresses(rut);
      setClientDbAddresses(addrs);
    } catch {
      setClientDbAddresses([]);
    } finally {
      setLoadingClientAddresses(false);
    }
  }, [api]);


  const handleSelectDbAddress = (addr: import('../services/api').ClientAddress) => {
    if (!selectedCustomer) return;
    setSelectedCustomer({
      ...selectedCustomer,
      Direccion: addr.address,
      Comuna: addr.comuna ?? undefined,
      Ciudad: addr.city ?? undefined,
      Contacto: addr.contacto ?? undefined,
    });
    setSelectedAddressName(addr.nombre || null);
    setSelectedAddressId(addr.id);
    setAddressSource('user');
  };

  const handleSaveEditedAddress = async (id: number | 'selected') => {
    if (!newAddressInput.trim() || !selectedCustomer) return;
    if (id === 'selected') {
      try {
        setSavingNewAddress(true);
        const created = await api.createClientAddress({
          clientName: selectedCustomer?.RazonSocial ?? 'Cliente',
          rut: selectedCustomer?.Rut,
          nombre: newAddressNombre.trim() || undefined,
          contacto: newAddressContacto.trim() || undefined,
          address: newAddressInput.trim(),
          comuna: newAddressComuna.trim() || undefined,
          city: newAddressCity.trim() || undefined,
        });
        setClientDbAddresses(prev => [...prev, created]);
        handleSelectDbAddress(created);
      } catch {
        // keep form open on error
      } finally {
        setSavingNewAddress(false);
      }
      setEditingAddressId(null);
      setNewAddressNombre('');
      setNewAddressContacto('');
      setNewAddressInput('');
      setNewAddressComuna('');
      setNewAddressCity('');
    } else {
      try {
        setSavingNewAddress(true);
        const updated = await api.updateClientAddress(id, {
          nombre: newAddressNombre.trim() || undefined,
          contacto: newAddressContacto.trim() || undefined,
          address: newAddressInput.trim(),
          comuna: newAddressComuna.trim() || undefined,
          city: newAddressCity.trim() || undefined,
        });
        setClientDbAddresses(prev => prev.map(a => a.id === id ? updated : a));

        // If this address is currently selected, sync all fields to state
        if (selectedAddressId === id) {
          setSelectedCustomer({
            ...selectedCustomer,
            Direccion: updated.address,
            Comuna: updated.comuna ?? undefined,
            Ciudad: updated.city ?? undefined,
            Contacto: updated.contacto ?? undefined,
          });
          setSelectedAddressName(updated.nombre || null);
        }
      } catch {
        // keep form open on error
      } finally {
        setSavingNewAddress(false);
      }
      setEditingAddressId(null);
      setNewAddressNombre('');
      setNewAddressContacto('');
      setNewAddressInput('');
      setNewAddressComuna('');
      setNewAddressCity('');
    }
  };

  const handleSaveNewAddress = async () => {
    if (!newAddressInput.trim()) return;
    try {
      setSavingNewAddress(true);
      const created = await api.createClientAddress({
        clientName: selectedCustomer?.RazonSocial ?? 'Cliente',
        rut: selectedCustomer?.Rut,
        nombre: newAddressNombre.trim() || undefined,
        contacto: newAddressContacto.trim() || undefined,
        address: newAddressInput.trim(),
        comuna: newAddressComuna.trim() || undefined,
        city: newAddressCity.trim() || undefined,
      });
      setClientDbAddresses(prev => [...prev, created]);
      handleSelectDbAddress(created);
      setShowNewAddressForm(false);
      setNewAddressNombre('');
      setNewAddressContacto('');
      setNewAddressInput('');
      setNewAddressComuna('');
      setNewAddressCity('');
    } catch {
      // keep form open on error
    } finally {
      setSavingNewAddress(false);
    }
  };

  // Handle customer select in dialog
  const handleCustomerSelect = async (customer: KameoneFicha) => {
    setSelectedCustomer(customer);
    setCustomerSearch('');
    setCustomers([]);

    // Save to recent cache
    setRecentFichas((prev) => {
      const updated = [customer, ...prev.filter((f) => f.Rut !== customer.Rut)].slice(0, RECENT_FICHAS_MAX);
      try { localStorage.setItem(RECENT_FICHAS_KEY, JSON.stringify(updated)); } catch {}
      return updated;
    });
    setSelectedAddressName(null);
    setSelectedAddressId(null);

    // Try to match customer's Kame address against saved client addresses.
    // If clientDbAddresses isn't loaded yet for this RUT, fetch them first.
    if (customer.Rut && customer.Direccion) {
      try {
        const addrsToSearch = await api.getClientAddresses(customer.Rut);
        setClientDbAddresses(addrsToSearch);
        const matched = matchAddressToClientAddresses(customer.Direccion, undefined, addrsToSearch);
        if (matched) {
          setSelectedCustomer(prev => prev ? {
            ...prev,
            Direccion: matched.address,
            Comuna: matched.comuna ?? prev.Comuna,
            Ciudad: matched.city ?? prev.Ciudad,
            Contacto: matched.contacto ?? prev.Contacto,
          } : prev);
          setSelectedAddressName(matched.nombre || null);
          setSelectedAddressId(matched.id);
          setAddressSource('user');
        } else if (customer.Direccion) {
          setAddressSource('kame');
        }
      } catch {
        if (customer.Direccion) setAddressSource('kame');
      }
    } else if (customer.Direccion) {
      setAddressSource('kame');
    }

    // Resolve price list: ficha's ListaDePrecio OR local DB assignment
    let listName = customer.ListaDePrecio as string | undefined;
    // Ignore Kame's ListaDePrecio if it doesn't exist in our local price lists
    if (listName && !availablePriceLists.some(l => l.name.toLowerCase() === listName!.toLowerCase())) {
      listName = undefined;
    }
    if (!listName && customer.RazonSocial) {
      try {
        const localAssignment = await api.getCustomerPriceListAssignment(customer.RazonSocial);
        if (localAssignment) {
          listName = localAssignment;
          // Stamp on the customer so the rest of the UI sees it
          customer = { ...customer, ListaDePrecio: listName };
          setSelectedCustomer(customer);
        }
      } catch {
        // silent
      }
    }

    // Auto-apply price list if customer has one
    if (listName && newOrderItems.length > 0) {
      const productIds = newOrderItems.filter(i => i.product?.id).map(i => i.product!.id);
      if (productIds.length > 0) {
        const priceMap = await api.resolvePricesForPriceList(listName, productIds).catch(() => null);
        if (priceMap) {
          setNewOrderItems(prev => prev.map(item => {
            if (!item.product?.id) return item;
            const newPrice = priceMap[item.product.id];
            if (newPrice !== undefined && newPrice !== null) {
              return {
                ...item,
                unitPrice: String(newPrice),
                pdfPriceSource: 'price_list' as const,
                priceSource: 'price_list' as const,
                priceOptions: {
                  ...item.priceOptions,
                  priceList: Number(newPrice),
                },
              };
            }
            // Not in the list — fall back by priority: OC price, then catalog (kame).
            const ocPrice = item.priceOptions?.oc;
            if (ocPrice != null && ocPrice > 0) {
              return {
                ...item,
                unitPrice: String(ocPrice),
                pdfPriceSource: 'pdf' as const,
                priceSource: 'oc' as const,
                priceOptions: { ...item.priceOptions, priceList: null },
              };
            }
            return {
              ...item,
              pdfPriceSource: 'price_list_missing' as const,
              priceSource: 'kame' as const,
              priceOptions: {
                ...item.priceOptions,
                priceList: null,
              },
            };
          }));
          setTimeout(() => {
            setPriceListFlash(true);
            setTimeout(() => setPriceListFlash(false), 800);
          }, 50);
        }
      }
    }
  };


  // Apply price list to items (extracted logic for reuse)
  const applyPriceListToItems = useCallback(async (listName: string, items: OrderItem[]) => {
    const productIds = items
      .filter((item) => item.product?.id)
      .map((item) => item.product!.id);

    if (productIds.length === 0) return items;

    try {
      const priceMap = await api.resolvePricesForPriceList(listName, productIds);

      if (priceMap) {
        const updatedItems = items.map((item) => {
          if (!item.product?.id) return item;
          const newPrice = priceMap[item.product.id];
          if (newPrice !== undefined && newPrice !== null) {
            return {
              ...item,
              unitPrice: String(newPrice),
              pdfPriceSource: 'price_list' as const,
              priceSource: 'price_list' as const,
              priceOptions: {
                ...item.priceOptions,
                priceList: Number(newPrice),
              },
            };
          }
          // Not in the list — fall back by priority: OC price, then catalog (kame).
          const ocPrice = item.priceOptions?.oc;
          if (ocPrice != null && ocPrice > 0) {
            return {
              ...item,
              unitPrice: String(ocPrice),
              pdfPriceSource: 'pdf' as const,
              priceSource: 'oc' as const,
              priceOptions: { ...item.priceOptions, priceList: null },
            };
          }
          return {
            ...item,
            unitPrice: String(item.product?.price ?? item.unitPrice ?? '0'),
            pdfPriceSource: 'price_list_missing' as const,
            priceSource: 'kame' as const,
            priceOptions: {
              ...item.priceOptions,
              priceList: null,
            },
          };
        });
        return updatedItems;
      }
    } catch (error) {
      console.error('Failed to resolve prices for price list:', error);
    }
    return items;
  }, [api]);

  // Handle price list selection and recalculate prices
  const handlePriceListSelect = async (listName: string) => {
    if (!selectedCustomer) return;

    const razonSocial = selectedCustomer.RazonSocial;

    // Handle "Sin lista" — clear price list and restore PDF/catalog prices
    if (listName === 'Sin lista') {
      setSelectedCustomer({
        ...selectedCustomer,
        ListaDePrecio: undefined,
      });
      const restoredItems = newOrderItems.map((item) => {
        if (item.pdfPriceSource === 'price_list' || item.pdfPriceSource === 'price_list_missing') {
          const { pdfPriceSource, ...rest } = item;
          return rest;
        }
        return item;
      });
      setNewOrderItems(restoredItems);
      if (razonSocial) {
        api.assignClientToPriceList(razonSocial, null).catch(() => {});
      }
      return;
    }

    // Update customer's price list
    setSelectedCustomer({
      ...selectedCustomer,
      ListaDePrecio: listName,
    });

    // Persist assignment so it auto-applies on future orders
    if (razonSocial) {
      api.assignClientToPriceList(razonSocial, listName).catch(() => {});
    }

    // Apply price list to items
    const updatedItems = await applyPriceListToItems(listName, newOrderItems);
    if (updatedItems !== newOrderItems) {
      setNewOrderItems(updatedItems);
      setTimeout(() => {
        setPriceListFlash(true);
        setTimeout(() => setPriceListFlash(false), 800);
      }, 50);
    }
  };

  const handleCustomerSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, onEscape?: () => void) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveCustomerIndex(i => Math.min(i + 1, customers.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveCustomerIndex(i => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') {
      setActiveCustomerIndex(i => {
        if (i >= 0 && i < customers.length) { e.preventDefault(); handleCustomerSelect(customers[i]); onEscape?.(); }
        return i;
      });
      return;
    }
    if (e.key === 'Escape') { e.stopPropagation(); setCustomerSearch(''); setCustomers([]); onEscape?.(); }
  };

  // Clear selected customer
  const handleClearSelectedCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomers([]);
    setSelectedAddressName(null);
    setSelectedAddressId(null);
  };

  // Reset customer dialog state
  const resetCustomerDialog = () => {
    setCustomerSearch('');
    setCustomers([]);
  };

  const confirmFolioEdit = () => {
    if (folioInput) {
      setAssignedFolio(folioInput);
      setFolioManuallyEdited(true);
    }
    setIsEditingFolio(false);
  };

  // Construye el payload para crear un pedido a partir del estado actual del modal
  const buildOrderData = () => {
    // If the address edit form is open, flush its values so they aren't lost
    const effectiveAddressName = editingAddressId !== null
      ? (newAddressNombre.trim() || selectedAddressName)
      : selectedAddressName;
    const effectiveContacto = editingAddressId !== null
      ? (newAddressContacto.trim() || selectedCustomer?.Contacto)
      : selectedCustomer?.Contacto;
    const effectiveDireccion = editingAddressId !== null
      ? (newAddressInput.trim() || selectedCustomer?.Direccion)
      : selectedCustomer?.Direccion;
    const effectiveComuna = editingAddressId !== null
      ? (newAddressComuna.trim() || selectedCustomer?.Comuna)
      : selectedCustomer?.Comuna;
    const effectiveCiudad = editingAddressId !== null
      ? (newAddressCity.trim() || selectedCustomer?.Ciudad)
      : selectedCustomer?.Ciudad;

    // Map the transient priceSource (price_list | kame | oc | manual) back to the stored
    // enum the backend persists (price_list | pdf | catalog | price_list_missing | manual).
    // 'kame' is the catalog price, but keep the more specific 'price_list_missing' when that's
    // what was loaded so the "not in list" warning survives a re-save.
    const toStoredPriceSource = (item: OrderItem): string | undefined => {
      switch (item.priceSource) {
        case 'manual':     return 'manual';
        case 'price_list': return 'price_list';
        case 'oc':         return 'pdf';
        case 'kame':       return item.pdfPriceSource === 'price_list_missing' ? 'price_list_missing' : 'catalog';
        default:           return item.pdfPriceSource;
      }
    };

    const builtItems = newOrderItems.filter(item => item.product != null).map(item => {
      const pdfPriceSource = toStoredPriceSource(item);
      return {
        productId: item.product!.id,
        quantity: item.quantity,
        quantityUnit: item.quantityUnit ?? 'unidad',
        unitPrice: !isNaN(parseFloat(item.unitPrice)) ? parseFloat(item.unitPrice) : undefined,
        ...(item.pdfLineText ? { pdfLineText: item.pdfLineText } : {}),
        ...(item.pdfPage != null ? { pdfPage: item.pdfPage } : {}),
        ...(pdfPriceSource ? { priceSource: pdfPriceSource } : {}),
        descripcionDetallada: item.descripcionDetallada ?? '',
        ...(item.priceOptions?.kame != null ? { displayPrice: item.priceOptions.kame } : {}),
        ...(item.priceOptions?.oc != null ? { ocPrice: item.priceOptions.oc } : {}),
        ...(item.priceOptions?.priceList != null ? { priceListPrice: item.priceOptions.priceList } : {}),
      };
    });
    const orderData: any = {
      items: builtItems,
      pdfFile: (selectedPdfFile?.type === 'application/pdf' ? selectedPdfFile : undefined),
    };

    if (selectedCustomer?.ListaDePrecio) {
      orderData.priceListName = selectedCustomer.ListaDePrecio;
    }

    // If user was mid-edit when submitting, use folioInput; otherwise use assignedFolio
    const effectiveFolio = isEditingFolio && folioInput ? folioInput : assignedFolio;
    if (effectiveFolio) {
      orderData.kameoneFolio = effectiveFolio;
    }

    if (pdfOrderNumber) orderData.orderNumber = pdfOrderNumber;
    if (pdfOrderDate) orderData.orderDate = pdfOrderDate;

    // Echoed back so the backend can finalize the extraction audit row.
    if (extractionCorrelationId) orderData.correlationId = extractionCorrelationId;

    if (selectedCustomer?.Rut) {
      orderData.customerRut = selectedCustomer.Rut;
      if (selectedCustomer.RazonSocial) orderData.customerName = selectedCustomer.RazonSocial;
      if (selectedCustomer.Telefono) orderData.customerPhone = selectedCustomer.Telefono;
      if (selectedCustomer.Email) orderData.customerEmail = selectedCustomer.Email;
      if (selectedCustomer.Giro) orderData.customerGiro = selectedCustomer.Giro;
      if (effectiveComuna) orderData.deliveryComuna = effectiveComuna;
      if (effectiveCiudad) orderData.deliveryCity = effectiveCiudad;
      if (effectiveAddressName) orderData.deliveryAddressName = effectiveAddressName;
      if (effectiveDireccion) orderData.deliveryAddress = effectiveDireccion;
      if (effectiveContacto) orderData.deliveryRecipientName = effectiveContacto;
    }

    return orderData;
  };

  // Limpia el modal y avanza al siguiente PDF si corresponde
  const closeAndAdvanceAfterSave = useCallback(() => {
    console.log('[closeAndAdvanceAfterSave] called, selectedInvoiceDate:', selectedInvoiceDate?.toISOString());
    setShowSingleOrderModal(false);
    fetchOrders(true); // silent — no loading skeleton
    fetchKameOrders(selectedInvoiceDate);
    if (isMultiPdfProcessing) {
      // Capture batch state before any reset, then advance directly.
      // resetOrderModalState() would wipe multiPdfFiles/isMultiPdfProcessing
      // before processNextPdfFile can read them, so we bypass it here.
      const files = multiPdfFiles;
      const nextIndex = multiPdfCurrentIndex + 1;
      pdfSessionRef.current += 1;
      setNewOrderItems([]);
      setSelectedCustomer(null);
      setHasAttemptedSave(false);
      setHasConfirmedAddressWarning(false);
      setSelectedAddressName(null);
      setSelectedAddressId(null);
      setAddressSource(null);
      setSelectedPdfFile(null);
      setExtractionCorrelationId(null);
      setPdfProcessingError(null);
      if (nextIndex < files.length) {
        processNextPdfFile(files, nextIndex);
      } else {
        setIsMultiPdfProcessing(false);
        setMultiPdfFiles([]);
        setMultiPdfCurrentIndex(0);
      }
    } else {
      resetOrderModalState();
      // Return to the list. Without this the :orderId stays in the URL and the
      // urlOrderId effect re-opens the order instead of proceeding to /orders.
      if (urlOrderId) navigate('/orders');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOrders, fetchKameOrders, selectedInvoiceDate, isMultiPdfProcessing, multiPdfFiles, multiPdfCurrentIndex, resetOrderModalState, urlOrderId, navigate]);

  // Escape on Kame success overlay = Entendido
  useEffect(() => {
    if (!showInvoiceOverlay || !kameResponse?.success) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setShowInvoiceOverlay(false);
      setKameResponse(null);
      closeAndAdvanceAfterSave();
    };
    document.addEventListener('keydown', handle, true);
    return () => document.removeEventListener('keydown', handle, true);
  }, [showInvoiceOverlay, kameResponse, closeAndAdvanceAfterSave]);

  // Persists current address fields back to the selected DB address record (non-fatal)
  const syncSelectedAddress = async () => {
    if (!selectedAddressId || !selectedCustomer) {
      console.log('[syncSelectedAddress] skipped — no selectedAddressId or selectedCustomer', { selectedAddressId, selectedCustomer });
      return;
    }
    // Flush any open address edit form values
    const nombre = (editingAddressId !== null ? newAddressNombre.trim() : null) || selectedAddressName || undefined;
    const contacto = (editingAddressId !== null ? newAddressContacto.trim() : null) || selectedCustomer.Contacto || undefined;
    const address = (editingAddressId !== null ? newAddressInput.trim() : null) || selectedCustomer.Direccion || '';
    const comuna = (editingAddressId !== null ? newAddressComuna.trim() : null) || selectedCustomer.Comuna || undefined;
    const city = (editingAddressId !== null ? newAddressCity.trim() : null) || selectedCustomer.Ciudad || undefined;
    console.log('[syncSelectedAddress] updating address id', selectedAddressId, { address, comuna, city, contacto, nombre });
    try {
      await api.updateClientAddress(selectedAddressId, { address, comuna, city, contacto, nombre });
      console.log('[syncSelectedAddress] success');
    } catch (e) {
      console.error('[syncSelectedAddress] failed', e);
    }
  };

  // Función para guardar pedido (sin facturar)
  const handleSaveOrder = async () => {
    setHasAttemptedSave(true);
    setSaveAttemptCount(c => c + 1);
    if (!isFormValid(true)) return;
    if (hasAddressWarning() && !hasConfirmedAddressWarning) {
      setHasConfirmedAddressWarning(true);
      return;
    }

    setSaveError(null);
    setIsCreatingOrder(true);
    try {
      await syncSelectedAddress();
      const orderData = buildOrderData();
      orderData.documentDate = documentDate;
      createOrderInFlightRef.current = true;
      const created = await api.createOrder(orderData);
      createOrderInFlightRef.current = false;
      if (created?.id) {
        markLocalMutation(created.id);
        upsertOrderInState(created as any); // insert immediately, no flicker
      }
      closeAndAdvanceAfterSave();
    } catch (error) {
      createOrderInFlightRef.current = false;
      const msg = error instanceof Error ? error.message : 'Error desconocido al crear el pedido';
      const rateLimit = parseRateLimitError(msg);
      setSaveError(rateLimit ? { message: 'rate_limit', retryAt: rateLimit.retryAt } : { message: msg });
    } finally {
      setIsCreatingOrder(false);
    }
  };

  // Guardar cambios en un pedido pendiente (modo edit)
  const handleUpdateOrder = async () => {
    setHasAttemptedSave(true);
    setSaveAttemptCount(c => c + 1);
    if (!isFormValid(true)) return;
    // Show address warning and wait for user to confirm via "Guardar de todas formas"
    if (hasAddressWarning() && !hasConfirmedAddressWarning) {
      setHasConfirmedAddressWarning(true);
      return;
    }

    const numericId = consultingOrderId ? parseInt(consultingOrderId.replace('P-', ''), 10) : null;
    if (!numericId) return;

    setIsCreatingOrder(true);
    try {
      await syncSelectedAddress();
      const orderData = buildOrderData();
      const payload: any = {
        items: orderData.items,
      };
      if (orderData.customerRut) payload.customerRut = orderData.customerRut;
      if (orderData.customerName) payload.customerName = orderData.customerName;
      if (orderData.customerPhone) payload.customerPhone = orderData.customerPhone;
      if (orderData.customerEmail) payload.customerEmail = orderData.customerEmail;
      if (orderData.customerGiro) payload.customerGiro = orderData.customerGiro;
      if (orderData.deliveryAddress) payload.deliveryAddress = orderData.deliveryAddress;
      if (orderData.deliveryComuna) payload.deliveryComuna = orderData.deliveryComuna;
      if (orderData.deliveryCity) payload.deliveryCity = orderData.deliveryCity;
      if (orderData.deliveryAddressName) payload.deliveryAddressName = orderData.deliveryAddressName;
      if (orderData.deliveryRecipientName) payload.deliveryRecipientName = orderData.deliveryRecipientName;
      if (orderData.orderNumber) payload.orderNumber = orderData.orderNumber;
      if (orderData.orderDate) payload.orderDate = orderData.orderDate;
      if (orderData.kameoneFolio) payload.kameoneFolio = orderData.kameoneFolio;
      if (orderData.priceListName) payload.priceListName = orderData.priceListName;
      payload.documentDate = documentDate;

      await api.updatePendingOrder(numericId, payload);
      markLocalMutation(numericId);
      const fresh = await api.getOrderById(numericId);
      upsertOrderInState(fresh as unknown as ApiOrder, true);
      apiOrdersCacheRef.current = apiOrdersCacheRef.current.map(o => o.id === numericId ? (fresh as unknown as ApiOrder) : o);
      closeAndAdvanceAfterSave();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido al actualizar el pedido';
      alert(`Error al actualizar el pedido: ${errorMessage}`);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  // Pure helper: fetches and transforms PDF data without touching React state.
  // Used both for the active PDF and for background prefetching of the next one.
  const fetchPdfData = useCallback(async (file: File): Promise<{ items: OrderItem[]; customer: KameoneFicha | null; warnings: string[] | null; file: File; orderNumber: string | null; orderDate: string | null; matchedAddress: import('../services/api').ClientAddress | null; correlationId: string | null }> => {
    const result = file.type === 'application/pdf'
      ? await api.processPdfOrder(file)
      : await api.processDocumentOrder(file);

    // Build order items in original PDF order using the unified `items` array.
    // Each entry has a `status` ('matched' | 'dubious' | 'unmatched') and `position`
    // that reflects where it appeared in the PDF.
    const baseId = Date.now();
    const unifiedItems: any[] = result.data.items ?? [];

    // Fallback: if backend doesn't send `items` yet, reconstruct from separate arrays
    const orderedSource = unifiedItems.length > 0
      ? unifiedItems
      : [
          ...result.data.matchedItems.map((m: any) => ({ ...m, status: m.matchQuality === 'dubious' ? 'dubious' : 'matched' })),
          ...result.data.unmatchedItems.map((u: any) => ({ ...u, status: 'unmatched' })),
        ];

    const buildPriceOptions = (entry: any): NonNullable<OrderItem['priceOptions']> => {
      // The matcher sends all three source prices explicitly (displayPrice/ocPrice/priceListPrice).
      // Fall back to deriving from the winning price for older payloads.
      const rawKame = entry.displayPrice ?? entry.product?.price ?? entry.unitPrice;
      const kame: number | null = Number.isFinite(Number(rawKame)) ? Number(rawKame) : null;
      const oc: number | null =
        entry.ocPrice != null ? (Number(entry.ocPrice) || null)
        : entry.priceSource === 'pdf' ? (Number(entry.unitPrice) || null)
        : null;
      const priceList: number | null =
        entry.priceListPrice != null ? (Number(entry.priceListPrice) || null)
        : entry.priceSource === 'price_list' ? (Number(entry.unitPrice) || null)
        : null;
      return { kame, oc, priceList };
    };

    const items: OrderItem[] = orderedSource.map((entry: any, index: number) => {
      // Prefer verbatim PDF text; fall back to requestedName/originalName paraphrase
      const line = (entry.pdfVerbatimText ?? entry.requestedName ?? entry.originalName ?? '').trim();
      const isMatched = entry.status === 'matched' || entry.status === 'dubious';

      if (isMatched) {
        return {
          id: baseId + index,
          orderId: 0,
          productId: entry.productId,
          quantity: entry.quantity,
          unitPrice: entry.unitPrice?.toString() ?? '0',
          product: {
            id: entry.productId,
            name: entry.productName,
            sku: entry.productSku ?? entry.sku,
            price: entry.displayPrice,
          },
          ...(line ? { pdfLineText: line } : {}),
          pdfMatchQuality: (entry.matchQuality ?? entry.status) as 'high' | 'dubious' | 'unmatched',
          ...(entry.matchStrategy ? { pdfMatchSource: entry.matchStrategy } : {}),
          ...(entry.priceSource ? { pdfPriceSource: entry.priceSource } : {}),
          priceSource: (entry.priceSource === 'price_list' ? 'price_list'
                      : entry.priceSource === 'pdf'         ? 'oc'
                      : 'kame') as OrderItem['priceSource'],
          priceOptions: buildPriceOptions(entry),
          ...(entry.pdfPage != null ? { pdfPage: entry.pdfPage } : {}),
        };
      } else {
        return {
          id: baseId + index,
          orderId: 0,
          productId: 0,
          quantity: entry.quantity,
          unitPrice: '0',
          product: undefined,
          ...(line ? { pdfLineText: line } : {}),
          pdfMatchQuality: 'unmatched' as const,
          ...(entry.pdfPage != null ? { pdfPage: entry.pdfPage } : {}),
        };
      }
    });

    // Client identification is exclusively by RUT match from the backend.
    // No name-based lookup or text-inference fallback.
    let customer: KameoneFicha | null = null;
    const customerRut = result.data.customerInfo?.rut;

    if (customerRut) {
      try {
        const fichas = await api.getKameoneFichas(customerRut);
        if (fichas.length > 0) {
          const { ListaDePrecio: _stripped, ...fichaWithoutList } = fichas[0];
          customer = {
            ...fichaWithoutList,
            ...(result.data.deliveryInfo?.address && { Direccion: result.data.deliveryInfo.address }),
            ...(result.data.deliveryInfo?.comuna && { Comuna: result.data.deliveryInfo.comuna }),
            ...(result.data.deliveryCity && { Ciudad: result.data.deliveryCity }),
          };
        }
      } catch (err) {
        // Kameone lookup failed — leave client unset
      }
    }

    // If the backend resolved a price list during PDF processing, stamp it onto the
    // customer ficha so the UI shows it in the dropdown and buildOrderData persists it.
    const resolvedPriceListName = result.data.priceListName;
    if (customer && resolvedPriceListName) {
      customer = { ...customer, ListaDePrecio: resolvedPriceListName };
    }

    const warnings = (result.warnings ?? []).filter((w) => w && String(w).trim());

    // Resolve the best matching saved client_address for this PDF's delivery address.
    // First try exact addressName match, then fall back to fuzzy address string match.
    let matchedAddress: import('../services/api').ClientAddress | null = null;
    const addressName = result.data.deliveryInfo?.addressName;
    const pdfAddress = result.data.deliveryInfo?.address || customer?.Direccion || '';
    if (customerRut) {
      try {
        const addrs = await api.getClientAddresses(customerRut);
        matchedAddress = matchAddressToClientAddresses(pdfAddress, addressName, addrs);
        console.log('[fetchPdfData] matchedAddress:', matchedAddress);
      } catch { /* non-critical */ }
    }

    return {
      items,
      customer,
      warnings: warnings.length > 0 ? warnings : null,
      file,
      orderNumber: (result.data as any).orderNumber ?? (result.data as any).poNumber ?? null,
      orderDate: (result.data as any).orderDate ?? (result.data as any).poDate ?? null,
      matchedAddress,
      correlationId: result.correlationId ?? null,
    };
  }, [api]);

  // Converts various extracted date formats to YYYY-MM-DD for the date input.
  // Handles: DD/MM/YYYY, DD-MM-YYYY, already-ISO YYYY-MM-DD, and falls back to null.
  const normalizeToIsoDate = (raw: string | null): string | null => {
    if (!raw) return null;
    const s = raw.trim();
    // Already ISO
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // DD/MM/YYYY or DD-MM-YYYY
    const dmy = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
    // Try native Date parse as last resort
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
    return null;
  };

  // Applies prefetched data to the modal state
  const applyPdfData = useCallback(async (data: { items: OrderItem[]; customer: KameoneFicha | null; warnings: string[] | null; file: File; orderNumber?: string | null; orderDate?: string | null; matchedAddress?: import('../services/api').ClientAddress | null; correlationId?: string | null }) => {
    let finalItems = data.items;

    // Auto-apply price list if customer has one
    if (data.customer?.ListaDePrecio) {
      const updatedItems = await applyPriceListToItems(data.customer.ListaDePrecio, data.items);
      finalItems = updatedItems;
    }

    setNewOrderItems(finalItems);
    scrollToFirstYellowItem();
    setPdfOrderNumber(data.orderNumber ?? null);
    setPdfOrderDate(normalizeToIsoDate(data.orderDate ?? null));
    setExtractionCorrelationId(data.correlationId ?? null);
    setSelectedPdfFile(data.file);

    if (data.matchedAddress && data.customer) {
      // Replace PDF-extracted address with saved record, only overriding fields that exist
      const addr = data.matchedAddress;
      setSelectedCustomer({
        ...data.customer,
        ...(addr.address?.trim() && { Direccion: addr.address }),
        ...(addr.comuna?.trim() && { Comuna: addr.comuna }),
        ...(addr.city?.trim() && { Ciudad: addr.city }),
        ...(addr.contacto?.trim() && { Contacto: addr.contacto }),
      });
      setSelectedAddressName(addr.nombre || null);
      setSelectedAddressId(addr.id);
      setAddressSource('user');
    } else {
      setSelectedCustomer(data.customer);
      setAddressSource('pdf');
    }
  }, [applyPriceListToItems, scrollToFirstYellowItem]);

  // Función para procesar PDF de Orden de Compra
  const handleProcessPdf = useCallback(async (file?: File) => {
    const fileToProcess = file || selectedPdfFile;
    if (!fileToProcess) return;

    // Capture the session at the moment this call starts.
    // If resetOrderModalState() runs while we're awaiting, the session counter
    // will have incremented and we discard the stale result.
    const session = pdfSessionRef.current;
    setLoadingQuote(getRandomLoadingQuote());
    setIsProcessingPdf(true);
    setPdfProcessingError(null);

    try {
      const data = await fetchPdfData(fileToProcess);
      if (pdfSessionRef.current !== session) {
        return;
      }
      await applyPdfData(data);
    } catch (error) {
      if (pdfSessionRef.current !== session) return;
      setPdfProcessingError(error instanceof Error ? error.message : 'Error desconocido al procesar el archivo');
    } finally {
      if (pdfSessionRef.current === session) setIsProcessingPdf(false);
    }
  }, [selectedPdfFile, fetchPdfData, applyPdfData]);

  // Punto único para "usuario eligió/soltó un PDF" (FileDropzone y drop en listado de productos)
  const handlePdfFileSelected = useCallback(async (file: File) => {
    setSelectedPdfFile(file);
    setPdfProcessingError(null);
    await handleProcessPdf(file);
  }, [handleProcessPdf]);

  // Procesamiento secuencial de múltiples PDFs
  const processNextPdfFile = useCallback(async (files: File[], index: number) => {
    if (index >= files.length) {
      setIsMultiPdfProcessing(false);
      setMultiPdfFiles([]);
      setMultiPdfCurrentIndex(0);
      prefetchRef.current.clear();
      return;
    }

    const file = files[index];
    setMultiPdfCurrentIndex(index);

    pdfSessionRef.current += 1;
    const session = pdfSessionRef.current;

    // Reset modal content for this file
    setNewOrderItems([]);
    setSelectedCustomer(null);
    setHasAttemptedSave(false);
    setSaveAttemptCount(0);
    setHasConfirmedAddressWarning(false);
    setSelectedAddressName(null);
    setSelectedAddressId(null);
    setAddressSource(null);
    setSelectedPdfFile(null);
    setPdfProcessingError(null);
    setFolioManuallyEdited(false);
    api.getNextFolio(undefined, 'last_created').then((folio) => setAssignedFolio(String(folio))).catch(() => setAssignedFolio(''));

    setLoadingQuote(getRandomLoadingQuote());
    setSelectedPdfFile(file);
    setShowSingleOrderModal(true);
    setIsProcessingPdf(true);

    try {
      // Use prefetched promise if available, otherwise start fresh
      const cached = prefetchRef.current.get(index);
      const promise = cached ?? fetchPdfData(file).catch(() => null);
      if (!cached) prefetchRef.current.set(index, promise);

      // Race against an already-resolved sentinel — if data wins synchronously,
      // the promise is settled and we can apply it with no loader at all.
      const PENDING = Symbol();
      const isReady = await Promise.race([promise, Promise.resolve(PENDING)]) !== PENDING;

      if (isReady) {
        setLoadingQuote(null);
        setSelectedPdfFile(null);
      }

      const data = await promise;

      if (pdfSessionRef.current !== session) return;
      setIsProcessingPdf(false);

      if (data) await applyPdfData(data);
      setSelectedPdfFile(file);

      // Eagerly prefetch all remaining files we haven't started yet
      for (let i = index + 1; i < files.length; i++) {
        if (!prefetchRef.current.has(i)) {
          prefetchRef.current.set(i, fetchPdfData(files[i]).catch(() => null));
        }
      }
    } catch (err) {
      if (pdfSessionRef.current !== session) return;
      setIsProcessingPdf(false);
      setPdfProcessingError(err instanceof Error ? err.message : 'Error al procesar el archivo');
    }
  }, [fetchPdfData, applyPdfData, api]);

  const startMultiPdfProcessing = useCallback((files: File[]) => {
    const SUPPORTED_TYPES = new Set(['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'text/csv', 'application/csv']);
    const pdfs = files.filter(f => SUPPORTED_TYPES.has(f.type) || /\.(xlsx|xls|csv)$/i.test(f.name)).sort((a, b) => a.size - b.size);
    if (pdfs.length === 0) return;

    // Reset all order state before starting a fresh flow
    resetOrderModalState();

    if (pdfs.length === 1) {
      // Single file — existing behavior
      setShowSingleOrderModal(true);
      handlePdfFileSelected(pdfs[0]);
      return;
    }

    // Multi-file — fire all fetches immediately so every PDF is being processed in parallel
    prefetchRef.current.clear();
    pdfs.forEach((pdf, i) => {
      prefetchRef.current.set(i, fetchPdfData(pdf).catch(() => null));
    });

    setMultiPdfFiles(pdfs);
    setMultiPdfCurrentIndex(0);
    setIsMultiPdfProcessing(true);
    processNextPdfFile(pdfs, 0);
  }, [handlePdfFileSelected, processNextPdfFile, fetchPdfData]);


  const handleProductListDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types?.includes('Files')) setIsProductListDropActive(true);
  }, []);

  const handleProductListDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsProductListDropActive(false);
  }, []);

  const handleProductListPdfDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsProductListDropActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handlePdfFileSelected(file);
  }, [handlePdfFileSelected]);

  // Función para generar factura (igual que "Facturar y enviar al SII")
  const handleGenerateInvoice = async (tipoDocumento: 'VENTA' | 'PEDIDO' = 'VENTA') => {
    setHasAttemptedSave(true);
    if (!isFormValid()) return;
    if (hasAddressWarning() && !hasConfirmedAddressWarning) {
      setHasConfirmedAddressWarning(true);
      openSelectedAddressEdit();
      return;
    }

    if (!selectedCustomer?.Rut) {
      setError('No se puede facturar el pedido sin un cliente asociado. Por favor, selecciona un cliente de KAME antes de facturar.');
      return;
    }

    setShowInvoiceOverlay(true);
    setKameResponse(null);
    setInvoiceOrderId(null);
    setIsCreatingOrder(true);
    try {
      await syncSelectedAddress();
      let orderId: number;

      const documentTypeLabel = documentType === 'factura' ? 'Factura Electrónica' : 'Guía de Despacho Electrónica';

      if (orderModalMode === 'edit' && consultingOrderId) {
        // 1. Update the existing pending order first
        const numericId = parseInt(consultingOrderId.replace('P-', ''), 10);
        const orderData = buildOrderData();
        const payload: any = { items: orderData.items };
        if (orderData.customerRut) payload.customerRut = orderData.customerRut;
        if (orderData.customerName) payload.customerName = orderData.customerName;
        if (orderData.customerPhone) payload.customerPhone = orderData.customerPhone;
        if (orderData.customerEmail) payload.customerEmail = orderData.customerEmail;
        if (orderData.customerGiro) payload.customerGiro = orderData.customerGiro;
        if (orderData.deliveryAddress) payload.deliveryAddress = orderData.deliveryAddress;
        if (orderData.deliveryComuna) payload.deliveryComuna = orderData.deliveryComuna;
        if (orderData.deliveryCity) payload.deliveryCity = orderData.deliveryCity;
        if (orderData.deliveryAddressName) payload.deliveryAddressName = orderData.deliveryAddressName;
        if (orderData.deliveryRecipientName) payload.deliveryRecipientName = orderData.deliveryRecipientName;
        if (orderData.kameoneFolio) payload.kameoneFolio = orderData.kameoneFolio;
        payload.documentDate = documentDate;
        await api.updatePendingOrder(numericId, payload);
        orderId = numericId;
      } else {
        // 1. Create new order
        const orderData = buildOrderData();
        createOrderInFlightRef.current = true;
        const newOrder = await api.createOrder(orderData);
        createOrderInFlightRef.current = false;
        orderId = newOrder.id;
      }
      markLocalMutation(orderId);
      setInvoiceOrderId(orderId);

      // 2. Confirm / generate document
      const result = await api.updateOrderStatus(orderId, 'confirmed', { documentType: documentTypeLabel, documentDate, paymentType, paymentDate: paymentType === 'credito' ? paymentDate : undefined, tipoDocumento });
      markLocalMutation(orderId);
      console.log('[handleGenerateInvoice] updateOrderStatus result:', { orderId, kameSync: result.kameSync, result });

      // 3. Handle KAME response — overlay stays open until user dismisses
      if (result.kameSync) {
        setKameResponse(result.kameSync);
      } else {
        setKameResponse({
          success: false,
          error: {
            message: 'No se recibió respuesta de sincronización',
            userFriendlyMessage: 'El pedido se creó pero no se pudo verificar la sincronización con KAME. Por favor, verifica en el ERP si el pedido se creó correctamente.'
          }
        });
      }
    } catch (error) {
      createOrderInFlightRef.current = false;
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido al crear y facturar el pedido';
      if (!showInvoiceOverlay) setShowInvoiceOverlay(true);
      setKameResponse({
        success: false,
        error: { message: errorMessage, userFriendlyMessage: errorMessage }
      });
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const getEmptyStateIcon = () => {
    if (!searchValue.trim() && originFilter === 'whatsapp') return <span className="text-2xl">🚧</span>;
    if (!searchValue.trim() && selectedTab === 'pendientes') return <EnvelopeSimple size={24} weight="duotone" className="text-muted-foreground" />;
    return <NoteBlank size={24} weight="bold" className="text-muted-foreground" />;
  };

  const getEmptyStateMessage = () => {
    // If there's an active search, show search-specific message
    if (searchValue.trim()) {
      return `No hay pedidos para tu búsqueda "${searchValue}"`;
    }

    if (originFilter === 'whatsapp') return 'WhatsApp pronto';

    // Otherwise, show tab-specific messages
    switch (selectedTab) {
      case 'pendientes':
        return 'Para crear una orden envía un correo con una OC a tresmundosonline@pedidos.tresmundosltda.cl';
      case 'facturaLocal':
        return 'No hay pedidos con factura local';
      case 'facturaEnviada':
        return 'No hay pedidos con factura enviada al SII';
      case 'descartados':
        return 'No hay pedidos descartados';
      case 'todos':
        return originFilter === 'todos' ? 'No hay pedidos pendientes' : 'No hay pedidos';
      default:
        return 'No hay pedidos para mostrar';
    }
  };

  return (
    <div 
      className="bg-muted flex flex-col relative w-full min-h-screen"
      data-name="Bandeja de pedidos" 
      data-node-id="1:48"
    >
      {/* Header - extends full width */}
      <Header className="shrink-0 w-full" onMenuClick={onMenuClick} />
      
      {/* Content container with max-width */}
      <PageContainer>
        {/* Navigation Cards + PDF Dropzone */}
        <div className="flex flex-col gap-2 items-start py-6 shrink-0 w-full">
          <div className="flex flex-col md:flex-row gap-2 w-full">
              {/* HIDDEN: Chats button
              <div className="flex-1">
                <ChatsOverviewCard onClick={onAllChatsClick} />
              </div>
              */}
              {/* HIDDEN: Reporte diario button
              <div className="flex-1">
                <DailyReportCard onClick={onNavigateToDailyReport} />
              </div>
              */}
              <div className="flex-1">
                <FileDropzone
                  key={isMultiPdfProcessing ? 'processing' : 'idle'}
                  onFilesSelected={(files) => {
                    if (files.length > 0 && !isMultiPdfProcessing) {
                      startMultiPdfProcessing(files);
                    }
                  }}
                  acceptedFileTypes={{
                    'application/pdf': ['.pdf'],
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                    'application/vnd.ms-excel': ['.xls'],
                    'text/csv': ['.csv'],
                  }}
                  maxSize={10 * 1024 * 1024}
                  label="Arrastra OCs para generar pedidos"
                  showIcon={false}
                  showSelectedFiles={false}
                />
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="flex flex-col items-start pb-2 pt-4 shrink-0 w-full">
            <h1 className="font-sans font-semibold text-[18px] text-text tracking-[-0.18px]">
              Pedidos
            </h1>
          </div>

          {/* Search Field and View Toggle */}
          <div className="flex gap-2 items-center pb-2 pt-2 shrink-0 w-full justify-between sticky top-0 z-10 bg-muted" data-node-id="1:57">
            <div className="flex items-center gap-2 flex-1 max-w-[400px]">
              <SearchField
                placeholder="Buscar"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => setOriginFilter('todos')}
                  className={`h-8 px-2.5 rounded-lg border text-xs font-medium transition-colors ${originFilter === 'todos' ? 'bg-text text-background border-text' : 'bg-background border-border text-text-subtle hover:bg-black/5'}`}
                >
                  Todos
                </button>
                <button
                  onClick={() => setOriginFilter(originFilter === 'whatsapp' ? 'todos' : 'whatsapp')}
                  className={`h-8 w-8 flex items-center justify-center rounded-lg border transition-colors ${originFilter === 'whatsapp' ? 'bg-text text-background border-text' : 'bg-background border-border text-text-subtle hover:bg-black/5'}`}
                  title="WhatsApp"
                >
                  <WhatsappLogo size={14} weight="bold" />
                </button>
                <button
                  onClick={() => setOriginFilter(originFilter === 'email' ? 'todos' : 'email')}
                  className={`h-8 w-8 flex items-center justify-center rounded-lg border transition-colors ${originFilter === 'email' ? 'bg-text text-background border-text' : 'bg-background border-border text-text-subtle hover:bg-black/5'}`}
                  title="Correo"
                >
                  <EnvelopeSimple size={14} weight="bold" />
                </button>
              </div>
            </div>
            {/* View Toggle and Add Button */}
            <div className="flex items-center gap-6">
              {/* View Toggle - Only on desktop */}
              <div className="hidden md:block">
                {/* HIDDEN: Tablero/Lista toggle buttons
                <ToggleGroup
                  value={viewMode}
                  onValueChange={(value) => {
                    if (value) setViewMode(value as 'board' | 'table');
                  }}
                >
                  <ToggleGroupItem value="board">
                    <Columns size={16} weight="bold" />
                    <span>Tablero</span>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="table">
                    <ListBullets size={16} weight="bold" />
                    <span>Lista</span>
                  </ToggleGroupItem>
                </ToggleGroup>
                */}
              </div>
              {/* Add Orders Button - Hidden on mobile, visible on desktop */}
              <div className="relative">
                <UnSoloPedidoButton onClick={() => {
                  resetOrderModalState();
                  setShowSingleOrderModal(true);
                  setEditingCustomerName(true);
                }} />
              </div>
            </div>
          </div>

          {/* Tabs - Only visible below the two-column breakpoint */}
          <div className="md:hidden bg-muted flex flex-col gap-2 items-start py-2 shrink-0 sticky top-0 z-10 w-full">
            <div className="relative shrink-0 w-full overflow-x-auto">
              <Tabs value={selectedTab} onValueChange={setSelectedTab}>
                <TabsList>
                  <TabsTrigger value="pendientes" badgeCount={pendingOrdersCount}>
                    Pendientes
                  </TabsTrigger>
                  <TabsTrigger value="facturaEnviada">
                    Enviado a Kame
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          {/* Orders List - Board View */}
          {viewMode === 'board' && (
            <div className="flex flex-1 gap-3 py-2 relative w-full min-h-0 h-full">
              {isLoading ? (
                <>
                  {/* Pendientes Column Skeleton */}
                  <BoardColumn 
                    title="Pendientes" 
                    count={0}
                  >
                    {[...Array(3)].map((_, index) => (
                      <motion.div 
                        key={index} 
                        className="bg-background border border-border rounded-lg p-3"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.4 }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Skeleton width="100px" height="20px" />
                          <Skeleton width="80px" height="24px" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <Skeleton width="180px" height="16px" />
                          <Skeleton width="120px" height="14px" />
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                            <Skeleton width="90px" height="14px" />
                            <Skeleton width="70px" height="16px" />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </BoardColumn>
                  
                  {/* HIDDEN: Con factura local Column Skeleton
                  <BoardColumn
                    title="Con factura local"
                    count={0}
                  >
                    {[...Array(3)].map((_, index) => (
                      <motion.div
                        key={index}
                        className="bg-background border border-border rounded-lg p-3"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 + 0.05, duration: 0.4 }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Skeleton width="100px" height="20px" />
                          <Skeleton width="80px" height="24px" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <Skeleton width="180px" height="16px" />
                          <Skeleton width="120px" height="14px" />
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                            <Skeleton width="90px" height="14px" />
                            <Skeleton width="70px" height="16px" />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </BoardColumn>
                  */}
                  
                  {/* Con factura enviada al SII Column Skeleton */}
                  <BoardColumn 
                    title="Enviado a Kame" 
                    count={0}
                  >
                    {[...Array(3)].map((_, index) => (
                      <motion.div 
                        key={index} 
                        className="bg-background border border-border rounded-lg p-3"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 + 0.1, duration: 0.4 }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Skeleton width="100px" height="20px" />
                          <Skeleton width="80px" height="24px" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <Skeleton width="180px" height="16px" />
                          <Skeleton width="120px" height="14px" />
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                            <Skeleton width="90px" height="14px" />
                            <Skeleton width="70px" height="16px" />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </BoardColumn>
                </>
              ) : (
                <>
                  {/* Pendientes Column */}
                  <BoardColumn
                    className={selectedTab !== 'pendientes' ? 'hidden md:flex' : ''}
                    title="Pendientes"
                    count={boardFilteredOrders.filter(o => o.status === 'pendiente').length}
                    headerActions={
                      <button
                        onClick={() => downloadColumnXlsx(boardFilteredOrders.filter(o => o.status === 'pendiente'), 'Pendientes')}
                        className="text-primary/70 hover:text-primary transition-colors"
                        aria-label="Descargar columna"
                      >
                        <DownloadSimple size={14} weight="bold" />
                      </button>
                    }
                  >
                    {boardFilteredOrders
                      .filter(order => order.status === 'pendiente')
                      .map((order) => (
                        <OrderCard
                          key={order.orderId}
                          {...order}
                          isHighlighted={recentlyChangedOrderIds.has(order.orderId)}
                          onClick={() => handleOrderClick(order.orderId)}
                          onDuplicate={() => handleDuplicateOrder(order.orderId)}
                        />
                      ))}
                    {boardFilteredOrders.filter(o => o.status === 'pendiente').length === 0 && (
                      <div className="flex flex-col items-center justify-center py-8 gap-3">
                        <p className="text-text-subtle text-sm text-center">
                          {originFilter === 'whatsapp' ? 'WhatsApp pronto' : 'Para crear una orden envía un correo con una OC a tresmundosonline@pedidos.tresmundosltda.cl'}
                        </p>
                        {originFilter === 'whatsapp'
                          ? <span className="text-2xl">🚧</span>
                          : <EnvelopeSimple size={28} weight="duotone" className="text-text-subtle/50" />
                        }
                      </div>
                    )}
                  </BoardColumn>
                  
                  {/* HIDDEN: Con factura local Column
                  <BoardColumn
                    title="Con factura local"
                    count={boardFilteredOrders.filter(o => o.status === 'facturaGenerada').length}
                  >
                    {boardFilteredOrders
                      .filter(order => order.status === 'facturaGenerada')
                      .map((order) => (
                        <OrderCard
                          key={order.orderId}
                          {...order}
                          onClick={() => handleOrderClick(order.orderId)}
                        />
                      ))}
                    {boardFilteredOrders.filter(o => o.status === 'facturaGenerada').length === 0 && (
                      <div className="flex items-center justify-center py-8">
                        <p className="text-text-subtle text-sm text-center">
                          No hay pedidos con factura local
                        </p>
                      </div>
                    )}
                  </BoardColumn>
                  */}
                  
                  {/* Con factura enviada al SII Column */}
                  <BoardColumn
                    className={selectedTab !== 'facturaEnviada' ? 'hidden md:flex' : ''}
                    title="Enviado a Kame"
                    count={filteredInvoiceSentOrders.length}
                    headerActions={
                      <>
                        <DateFilterComponent />
                        <button
                          onClick={() => downloadColumnXlsx(filteredInvoiceSentOrders, 'Enviado-a-Kame')}
                          className="text-primary/70 hover:text-primary transition-colors"
                          aria-label="Descargar columna"
                        >
                          <DownloadSimple size={14} weight="bold" />
                        </button>
                      </>
                    }
                  >
                    {filteredInvoiceSentOrders.map((order) => (
                      <OrderCard
                        key={order.orderId}
                        {...order}
                        hasUnread={false}
                        isHighlighted={recentlyChangedOrderIds.has(order.orderId)}
                        onClick={() => handleOrderClick(order.orderId)}
                        onDuplicate={() => handleDuplicateOrder(order.orderId)}
                      />
                    ))}
                    {filteredInvoiceSentOrders.length === 0 && (
                      <div className="flex items-center justify-center py-8">
                        <p className="text-text-subtle text-sm text-center">
                          {selectedInvoiceDate.toDateString() === new Date().toDateString()
                            ? 'No has enviado nada a Kame hoy'
                            : 'No se envió nada a Kame en esta fecha'}
                        </p>
                      </div>
                    )}
                  </BoardColumn>
                </>
              )}
            </div>
          )}

          {/* Orders List - Table View */}
          {viewMode === 'table' && (
            <div className="hidden md:flex flex-1 py-2 relative w-full min-h-0" data-node-id="1:61">
              <div className="flex-1 bg-background flex flex-col items-start p-2 rounded-lg w-full overflow-y-auto">
          {/* Desktop Header - Hidden on mobile */}
          {!isLoading && searchFilteredOrders.length > 0 && (
            <div className="hidden md:flex items-center px-0 py-2 pb-3 border-b border-border w-full" style={{ gap: '16px' }}>
              <div className="w-4" /> {/* Spacer for unread indicator */}
              <div className="w-[120px]">
                <button
                  onClick={() => handleSort('orderId')}
                  className="flex items-center gap-1 hover:opacity-70 transition-opacity"
                >
                  <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">
                    Pedido
                  </p>
                  {sortField === 'orderId' && (
                    sortDirection === 'asc' ? 
                      <CaretUp size={12} weight="bold" className="text-text-subtle" /> : 
                      <CaretDown size={12} weight="bold" className="text-text-subtle" />
                  )}
                </button>
              </div>
              <div className="w-[250px]">
                <button
                  onClick={() => handleSort('customer')}
                  className="flex items-center gap-1 hover:opacity-70 transition-opacity"
                >
                  <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">
                    Cliente
                  </p>
                  {sortField === 'customer' && (
                    sortDirection === 'asc' ? 
                      <CaretUp size={12} weight="bold" className="text-text-subtle" /> : 
                      <CaretDown size={12} weight="bold" className="text-text-subtle" />
                  )}
                </button>
              </div>
              <div className="w-[150px]">
                <button
                  onClick={() => handleSort('company')}
                  className="flex items-center gap-1 hover:opacity-70 transition-opacity"
                >
                  <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">
                    Teléfono
                  </p>
                  {sortField === 'company' && (
                    sortDirection === 'asc' ? 
                      <CaretUp size={12} weight="bold" className="text-text-subtle" /> : 
                      <CaretDown size={12} weight="bold" className="text-text-subtle" />
                  )}
                </button>
              </div>
              <div className="w-[120px]">
                <button
                  onClick={() => handleSort('productCount')}
                  className="flex items-center gap-1 hover:opacity-70 transition-opacity"
                >
                  <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">
                    Productos
                  </p>
                  {sortField === 'productCount' && (
                    sortDirection === 'asc' ? 
                      <CaretUp size={12} weight="bold" className="text-text-subtle" /> : 
                      <CaretDown size={12} weight="bold" className="text-text-subtle" />
                  )}
                </button>
              </div>
              <div className="w-[120px]">
                <button
                  onClick={() => handleSort('total')}
                  className="flex items-center gap-1 hover:opacity-70 transition-opacity ml-auto"
                >
                  <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide text-right">
                    Total
                  </p>
                  {sortField === 'total' && (
                    sortDirection === 'asc' ? 
                      <CaretUp size={12} weight="bold" className="text-text-subtle" /> : 
                      <CaretDown size={12} weight="bold" className="text-text-subtle" />
                  )}
                </button>
              </div>
              <div className="w-[150px]">
                <button
                  onClick={() => handleSort('updatedAt')}
                  className="flex items-center gap-1 hover:opacity-70 transition-opacity"
                >
                  <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">
                    Fecha
                  </p>
                  {sortField === 'updatedAt' && (
                    sortDirection === 'asc' ? 
                      <CaretUp size={12} weight="bold" className="text-text-subtle" /> : 
                      <CaretDown size={12} weight="bold" className="text-text-subtle" />
                  )}
                </button>
              </div>
              <div className="w-[140px]">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-1 hover:opacity-70 transition-opacity"
                >
                  <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">
                    Estado
                  </p>
                  {sortField === 'status' && (
                    sortDirection === 'asc' ? 
                      <CaretUp size={12} weight="bold" className="text-text-subtle" /> : 
                      <CaretDown size={12} weight="bold" className="text-text-subtle" />
                  )}
                </button>
              </div>
              <div className="flex-1" /> {/* Spacer for arrow */}
            </div>
          )}
          {isLoading ? (
            <div className="flex flex-col gap-2 w-full">
              {[...Array(6)].map((_, index) => (
                <motion.div 
                  key={index} 
                  className="p-3 w-full"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.4 }}
                >
                  <div className="flex gap-3 items-start">
                    <div className="flex-1 flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <Skeleton width="120px" height="20px" />
                        <Skeleton width="80px" height="20px" />
                      </div>
                      <Skeleton width="200px" height="24px" />
                      <Skeleton width="150px" height="18px" />
                      <div className="flex gap-1 items-center">
                        <Skeleton width="100px" height="18px" />
                        <Skeleton width="8px" height="8px" />
                        <Skeleton width="100px" height="18px" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : error ? (
            <motion.div 
              className="text-center py-8 text-red-600 w-full"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <p>{error}</p>
              <p className="text-xs mt-2">Por favor, abre la consola (F12) para ver más detalles del error.</p>
            </motion.div>
          ) : searchFilteredOrders.length > 0 ? (
            <AnimatePresence>
              {searchFilteredOrders.map((order, index) => (
                <motion.div
                  key={order.orderId}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: index * 0.02,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                  className="w-full"
                >
                  <OrderTile
                    orderId={order.orderId}
                    company={order.company}
                    customer={order.customer}
                    productCount={order.productCount}
                    total={order.total}
                    status={order.status}
                    hasUnread={order.hasUnread}
                    date={order.date}
                    kameoneFolio={order.kameoneFolio}
                    onClick={() => {
                      const numericId = order.orderId.replace(/^P-/, '');
                      markSeen(numericId);
                      setOrders(prev => prev.map(o =>
                        o.orderId === order.orderId ? { ...o, hasUnread: false } : o
                      ));
                      handleOrderClick(order.orderId);
                    }}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          ) : (
            <motion.div 
              key={`empty-state-${selectedTab}`}
              className="flex flex-col items-center justify-center py-12 text-text-subtle w-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <motion.p
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.3 }}
                className="text-center"
              >
                {getEmptyStateMessage()}
              </motion.p>
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.3 }}
                className="flex justify-center mt-3"
              >
                {getEmptyStateIcon()}
              </motion.div>
            </motion.div>
          )}
              </div>
            </div>
          )}


      </PageContainer>

      {/* Add Orders Modal */}
      <CenterPeekModal
        isOpen={isAddOrdersModalOpen}
        onClose={() => {
          setIsAddOrdersModalOpen(false);
          setSelectedFiles([]);
        }}
        title="Cargar Órdenes de Compra"
        description="Carga uno o más archivos de órdenes de compra para crear pedidos pendientes"
        size="lg"
        showCloseButton={true}
        footer={
          <Button
            variant="primary"
            onClick={handleAddOrders}
            disabled={selectedFiles.length === 0}
          >
            Agregar pedidos
          </Button>
        }
        bodyBackground="bg-muted"
        headerBackground="bg-muted"
      >
        <FileDropzone
          onFilesSelected={setSelectedFiles}
          maxFiles={10}
          maxSize={10 * 1024 * 1024} // 10MB
        />
      </CenterPeekModal>

      {/* Keyboard arrow flash indicators + hover nav zones */}
      {showSingleOrderModal && (() => {
        const navList = consultingOrderId
          ? consultingOrderStatus === 'pendiente' ? orders.filter(o => o.status === 'pendiente')
            : consultingOrderStatus === 'facturaGenerada' ? orders.filter(o => o.status === 'facturaGenerada')
            : consultingOrderStatus === 'facturaEnviada' ? filteredInvoiceSentOrders
            : null
          : null;
        const currentIdx = navList ? navList.findIndex(o => o.orderId === consultingOrderId) : -1;
        const prevOrder = navList && currentIdx >= 0 ? navList[currentIdx - 1] ?? null : null;
        const nextOrder = navList && currentIdx >= 0 ? navList[currentIdx + 1] ?? null : null;
        return (
          <>
            {/* Left side — click strip closes modal, arrow button navigates */}
            <div
              className="nav-zone-left fixed left-0 top-0 h-full w-20 z-[200]"
              onClick={handleCloseCreateOrderModal}
            >
              {prevOrder && (
                <button
                  type="button"
                  className="nav-zone-arrow absolute left-4 -translate-y-1/2 pointer-events-auto cursor-pointer"
                  style={{ top: '50%' }}
                  onClick={e => { e.stopPropagation(); setArrowFlashLeft(n => n + 1); navigateToOrder(prevOrder.orderId); }}
                >
                  <div key={arrowFlashLeft} className={arrowFlashLeft > arrowFlashBaselineRef.current.left ? 'arrow-flash-left' : ''}>
                    <div className="bg-background/80 backdrop-blur-sm border border-border rounded-full p-3 shadow-lg">
                      <CaretLeft size={22} weight="bold" className="text-text" />
                    </div>
                  </div>
                </button>
              )}
            </div>
            {/* Right side — click strip closes modal, arrow button navigates */}
            <div
              className="nav-zone-right fixed right-0 top-0 h-full w-20 z-[200]"
              onClick={handleCloseCreateOrderModal}
            >
              {nextOrder && (
                <button
                  type="button"
                  className="nav-zone-arrow absolute right-4 -translate-y-1/2 pointer-events-auto cursor-pointer"
                  style={{ top: '50%' }}
                  onClick={e => { e.stopPropagation(); setArrowFlashRight(n => n + 1); navigateToOrder(nextOrder.orderId); }}
                >
                  <div key={arrowFlashRight} className={arrowFlashRight > arrowFlashBaselineRef.current.right ? 'arrow-flash-right' : ''}>
                    <div className="bg-background/80 backdrop-blur-sm border border-border rounded-full p-3 shadow-lg">
                      <CaretRight size={22} weight="bold" className="text-text" />
                    </div>
                  </div>
                </button>
              )}
            </div>
          </>
        );
      })()}

      {/* Single Order Modal */}
      <CenterPeekModal
        isOpen={showSingleOrderModal && !showInvoiceOverlay}
        onClose={handleCloseCreateOrderModal}
        {...(orderModalMode !== 'create' && consultingOrderId
          ? {
              header: (
                <div className="flex items-center gap-2 min-w-0">
                  <h2 className="font-sans font-semibold text-lg text-foreground tracking-[-0.18px]">
                    {consultingOrderId}
                  </h2>
                  {consultingOrderStatus && (
                    <div className="flex items-center gap-1.5">
                      {consultingOrderDocType && (
                        <span className="font-sans text-xs text-text-subtle">
                          {consultingOrderDocType.toLowerCase().includes('guía') || consultingOrderDocType.toLowerCase().includes('guia') ? 'Guía' : 'Factura'}
                        </span>
                      )}
                      <StatusTag status={consultingOrderStatus} />
                    </div>
                  )}
                  {(() => {
                    const order = orders.find(o => o.orderId === consultingOrderId);
                    const folio = order?.kameoneFolio;
                    if (consultingOrderStatus === 'pendiente') {
                      return null;
                    }
                    return folio ? (
                      <a
                        href={`https://www.kameone.cl/Documento/Index?tipoDoc=V&folio=${folio}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-0.5 font-sans text-xs font-medium text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Abrir en Kame
                        <ArrowSquareOut size={12} weight="bold" />
                      </a>
                    ) : null;
                  })()}
                </div>
              ),
            }
          : {
              title: isMultiPdfProcessing
                ? `Crear nuevo pedido (${multiPdfCurrentIndex + 1} de ${multiPdfFiles.length})`
                : 'Crear nuevo pedido',
            }
        )}
        size="viewport"
        showCloseButton={true}
        headerExtra={
          (orderModalMode === 'create' || orderModalMode === 'edit' || orderModalMode === 'consult')
            ? (
              <div className="flex items-center gap-2">
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background border focus-within:ring-1 focus-within:ring-ring ${isProcessingPdf ? 'opacity-50 pointer-events-none' : ''} ${getFieldError('pdfOrderNumber') ? 'border-destructive' : 'border-border'}`}>
                  <span className={`font-sans text-xs shrink-0 ${getFieldError('pdfOrderNumber') ? 'text-destructive' : 'text-text-subtle'}`}>N° OC</span>
                  <input
                    type="text"
                    value={pdfOrderNumber ?? ''}
                    onChange={e => orderModalMode !== 'consult' && setPdfOrderNumber(e.target.value || null)}
                    placeholder="—"
                    readOnly={orderModalMode === 'consult'}
                    disabled={isProcessingPdf}
                    className="font-sans text-xs font-semibold text-foreground bg-transparent border-none outline-none placeholder:text-text-subtle placeholder:font-normal disabled:opacity-50 disabled:cursor-not-allowed read-only:cursor-default"
                    style={{ fieldSizing: 'content' } as React.CSSProperties}
                  />
                </div>
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background border focus-within:ring-1 focus-within:ring-ring ${isProcessingPdf ? 'opacity-50 pointer-events-none' : ''} ${getFieldError('pdfOrderDate') ? 'border-destructive' : 'border-border'}`}>
                  <span className={`font-sans text-xs shrink-0 ${getFieldError('pdfOrderDate') ? 'text-destructive' : 'text-text-subtle'}`}>Fecha OC</span>
                  <input
                    type="date"
                    value={pdfOrderDate ?? ''}
                    onChange={e => orderModalMode !== 'consult' && setPdfOrderDate(e.target.value || null)}
                    placeholder="AAAA-MM-DD"
                    readOnly={orderModalMode === 'consult'}
                    disabled={isProcessingPdf}
                    className="font-sans text-xs font-semibold text-foreground bg-transparent border-none outline-none w-28 placeholder:text-text-subtle placeholder:font-normal disabled:opacity-50 disabled:cursor-not-allowed read-only:cursor-default"
                  />
                </div>
              </div>
            )
            : undefined
        }
        footer={
          <>
            {saveError && (
              <div className="w-full mb-2 p-3 rounded-lg border border-amber-300 bg-amber-50 flex flex-col gap-1">
                {saveError.message === 'rate_limit' ? (
                  <>
                    <p className="font-sans font-semibold text-sm text-amber-900">Kame limitó temporalmente el acceso</p>
                    <p className="font-sans text-xs text-amber-800">No pudimos completar la consulta porque Kame bloqueó temporalmente nuevas solicitudes a su API por límite de uso.</p>
                    <p className="font-sans text-xs text-amber-800">La integración está funcionando correctamente. Este límite es definido por Kame y afecta el acceso al servicio que ellos entregan.</p>
                    {saveError.retryAt && (
                      <p className="font-sans text-xs text-amber-800">Puedes volver a intentarlo a las <span className="font-semibold">{saveError.retryAt}</span>.</p>
                    )}
                  </>
                ) : (
                  <p className="font-sans text-sm text-red-800">Error al guardar: {saveError.message}</p>
                )}
              </div>
            )}
            {isMultiPdfProcessing && multiPdfCurrentIndex + 1 < multiPdfFiles.length && (
              <button
                type="button"
                onClick={() => processNextPdfFile(multiPdfFiles, multiPdfCurrentIndex + 1)}
                disabled={isCreatingOrder || isProcessingPdf}
                className="text-xs text-text-subtle mr-auto flex items-center gap-1 hover:text-primary transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente: {multiPdfFiles[multiPdfCurrentIndex + 1]?.name}
                <CaretRight size={14} weight="bold" />
              </button>
            )}
            {(() => {
              const navList = consultingOrderId
                ? consultingOrderStatus === 'pendiente' ? orders.filter(o => o.status === 'pendiente')
                  : consultingOrderStatus === 'facturaGenerada' ? orders.filter(o => o.status === 'facturaGenerada')
                  : consultingOrderStatus === 'facturaEnviada' ? filteredInvoiceSentOrders
                  : null
                : null;
              const currentIdx = navList ? navList.findIndex(o => o.orderId === consultingOrderId) : -1;
              const prevOrder = navList && currentIdx >= 0 ? navList[currentIdx - 1] ?? null : null;
              const nextOrder = navList && currentIdx >= 0 ? navList[currentIdx + 1] ?? null : null;
              const hasNav = !!(prevOrder || nextOrder);
              return (
                <>
                  {hasNav && (
                    <div className="mr-auto flex items-center gap-3">
                      {prevOrder && (
                        <button
                          type="button"
                          onClick={() => { setArrowFlashLeft(n => n + 1); navigateToOrder(prevOrder.orderId); }}
                          disabled={isCreatingOrder}
                          className="text-xs text-text-subtle flex items-center gap-1 hover:text-primary transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <CaretLeft size={14} weight="bold" />
                          Anterior: {prevOrder.orderId}
                        </button>
                      )}
                      {prevOrder && nextOrder && <span className="text-xs text-text-subtle">|</span>}
                      {nextOrder && (
                        <button
                          type="button"
                          onClick={() => { setArrowFlashRight(n => n + 1); navigateToOrder(nextOrder.orderId); }}
                          disabled={isCreatingOrder}
                          className="text-xs text-text-subtle flex items-center gap-1 hover:text-primary transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Siguiente: {nextOrder.orderId}
                          <CaretRight size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    onClick={
                      isMultiPdfProcessing && multiPdfCurrentIndex + 1 < multiPdfFiles.length
                        ? () => {
                            const nextIndex = multiPdfCurrentIndex + 1;
                            const hasData = selectedCustomer !== null || newOrderItems.length > 0;
                            if (hasData && !isCreatingOrder) {
                              setPendingSkipIndex(nextIndex);
                              setShowCloseConfirmDialog(true);
                            } else {
                              processNextPdfFile(multiPdfFiles, nextIndex);
                            }
                          }
                        : handleCloseCreateOrderModal
                    }
                    disabled={isCreatingOrder || isProcessingPdf}
                  >
                    {isFacturaEnviada
                      ? 'Cerrar'
                      : orderModalMode === 'consult'
                        ? 'Cerrar'
                        : isMultiPdfProcessing && multiPdfCurrentIndex + 1 < multiPdfFiles.length
                          ? 'Saltar'
                          : 'Cancelar'
                    }
                  </Button>
                </>
              );
            })()}
            {!isFacturaEnviada && (
              <Button
                variant="outline"
                onClick={orderModalMode === 'edit' ? handleUpdateOrder : handleSaveOrder}
                disabled={isCreatingOrder || isProcessingPdf}
              >
                {isCreatingOrder && orderModalMode === 'edit' ? <Loader /> : 'Guardar para después'}
              </Button>
            )}
            {!isFacturaEnviada && <DropdownMenu open={showDocumentMenu} onOpenChange={setShowDocumentMenu}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="primary"
                  disabled={isCreatingOrder || isProcessingPdf}
                >
                  Generar documento
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="p-3 min-w-[260px]">
                <div className="flex flex-col gap-3">
                  {/* Section title: DOCUMENTO */}
                  <span className="font-sans text-xs text-text-subtle font-medium uppercase tracking-wide">Documento</span>

                  {/* Radio buttons */}
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="documentType"
                        value="factura"
                        checked={documentType === 'factura'}
                        onChange={() => setDocumentType('factura')}
                        className="accent-primary"
                      />
                      <span className="font-sans text-sm text-text">Factura Electrónica</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="documentType"
                        value="guia"
                        checked={documentType === 'guia'}
                        onChange={() => setDocumentType('guia')}
                        className="accent-primary"
                      />
                      <span className="font-sans text-sm text-text">Guía de Despacho Electrónica</span>
                    </label>
                  </div>

                  {/* Document date row (above divider) */}
                  <div className="flex items-center gap-2">
                    <span className="font-sans text-sm text-text-subtle whitespace-nowrap">Fecha</span>
                    <input
                      type="date"
                      value={documentDate}
                      onChange={(e) => setDocumentDate(e.target.value)}
                      className="font-sans text-sm text-text outline-none border border-border rounded-md px-2 py-1.5 bg-background flex-1"
                    />
                  </div>

                  {/* Divider */}
                  <div className="border-t border-border" />

                  {/* Section title: PAGO */}
                  <span className="font-sans text-xs text-text-subtle font-medium uppercase tracking-wide">Pago</span>

                  {/* Payment type radio buttons */}
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentType"
                        value="contado"
                        checked={paymentType === 'contado'}
                        onChange={() => setPaymentType('contado')}
                        className="accent-primary"
                      />
                      <span className="font-sans text-sm text-text">Contado</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentType"
                        value="credito"
                        checked={paymentType === 'credito'}
                        onChange={() => setPaymentType('credito')}
                        className="accent-primary"
                      />
                      <span className="font-sans text-sm text-text">Crédito</span>
                    </label>
                  </div>

                  {/* Payment date (only shown for Crédito) */}
                  {paymentType === 'credito' && (
                    <div className="flex items-center gap-2">
                      <span className="font-sans text-sm text-text-subtle whitespace-nowrap">Fecha</span>
                      <input
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        className="font-sans text-sm text-text outline-none border border-border rounded-md px-2 py-1.5 bg-background flex-1"
                      />
                    </div>
                  )}

                  {/* Divider */}
                  <div className="border-t border-border" />

                  {/* Section title: TIPO */}
                  <span className="font-sans text-xs text-text-subtle font-medium uppercase tracking-wide">Tipo</span>

                  {/* Tipo radio buttons */}
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="orderTipoDocumento"
                        value="VENTA"
                        checked={orderTipoDocumento === 'VENTA'}
                        onChange={() => setOrderTipoDocumento('VENTA')}
                        className="accent-primary"
                      />
                      <span className="font-sans text-sm text-text">Venta</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="orderTipoDocumento"
                        value="PEDIDO"
                        checked={orderTipoDocumento === 'PEDIDO'}
                        onChange={() => setOrderTipoDocumento('PEDIDO')}
                        className="accent-primary"
                      />
                      <span className="font-sans text-sm text-text">Pedido</span>
                    </label>
                  </div>

                  {/* Confirm button */}
                  <Button
                    variant="primary"
                    onClick={() => { setShowDocumentMenu(false); handleGenerateInvoice(orderTipoDocumento); }}
                    disabled={isCreatingOrder || (paymentType === 'credito' && !paymentDate)}
                  >
                    Confirmar
                  </Button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>}
            {((orderModalMode === 'edit' && consultingOrderStatus === 'pendiente') || consultingOrderStatus === 'facturaEnviada') && (
              <button
                type="button"
                onClick={() => { setOrderToDiscard(consultingOrderId); setShowDiscardDialog(true); }}
                disabled={isCreatingOrder}
                className="absolute left-1/2 -translate-x-1/2 font-sans text-xs transition-all duration-200 disabled:opacity-0 disabled:cursor-not-allowed active:scale-90 active:brightness-75 descartar-btn"
              >
                Descartar orden
              </button>
            )}
          </>
        }
        bodyBackground="bg-muted"
        headerBackground="bg-muted"
        footerClassName="relative group/footer"
        bodyClassName="p-0 !overflow-hidden flex flex-col min-h-0"
        contentClassName="flex-1 min-h-0 flex flex-col"
      >
        {/* Layout de dos paneles similar a OrderDetail */}
        <div className={`${isMobileDevice ? 'hidden' : 'flex'} flex-1 gap-3 min-h-0 w-full`}>
          
          {/* Panel Izquierdo - flex-[3] */}
          <div className="flex-[5] flex flex-col min-h-0 overflow-hidden">
            {/* Scrollable content area */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden" ref={modalScrollableRef}>
              
              {/* Header editable (similar a OrderDetail pero con inputs) */}
              {/* Products List */}
              <div className="bg-muted flex gap-2 py-2 relative w-full flex-1 flex-col min-h-0 overflow-hidden">
                <div className="px-3 w-full flex flex-col min-h-0 flex-1">
                  <div className="flex items-start gap-2 mb-2">
                    {/* Customer Tile */}
                    <div className="flex-1 min-w-0 flex flex-col gap-0">
                      <div
                        onClick={() => {
                          if (!selectedCustomer && !isProcessingPdf) {
                            setEditingCustomerName(true);
                          }
                        }}
                        className={`relative w-full rounded-lg border transition-colors cursor-pointer ${isProcessingPdf ? 'pointer-events-none opacity-50' : ''} ${!selectedCustomer && !isProcessingPdf ? 'border-dashed border-yellow-500 hover:bg-muted' : 'border-border bg-background hover:bg-muted/50'}`}
                        style={{ padding: '12px' }}
                      >
                        {!selectedCustomer && editingCustomerName ? (
                          <div className="relative w-full" ref={customerSearchFieldRef}>
                            <Input
                              autoFocus
                              placeholder="Buscar por RUT o razón social..."
                              value={customerSearch}
                              onChange={e => setCustomerSearch(e.target.value)}
                              onClick={e => e.stopPropagation()}
                              onKeyDown={e => { e.stopPropagation(); handleCustomerSearchKeyDown(e, () => setEditingCustomerName(false)); }}
                              className="py-1.5 text-sm"
                            />
                            {customers.length > 0 && (
                              <div ref={customerListPopoverRef} className="absolute left-0 right-0 top-full z-[60] mt-1 max-h-[320px] overflow-y-auto rounded-lg border border-border bg-background shadow-lg">
                                {!customerSearch.trim() && (
                                  <p className="px-3 pt-2 pb-1 text-xs font-medium text-text-subtle uppercase tracking-wide">Recientes</p>
                                )}
                                {isLoadingCustomers && customers.length === 0 ? (
                                  <p className="p-3 text-sm text-text-subtle text-center">Buscando...</p>
                                ) : (
                                  <div className="py-1">
                                    {customers.map((customer, index) => (
                                      <button
                                        key={`${customer.Rut || ''}-${index}`}
                                        data-kbd-item
                                        type="button"
                                        onClick={e => { e.stopPropagation(); handleCustomerSelect(customer); setEditingCustomerName(false); }}
                                        className={`w-full px-3 py-2 text-left transition-colors ${activeCustomerIndex === index ? 'bg-muted' : 'hover:bg-muted'}`}
                                      >
                                        <p className="font-sans font-medium text-sm text-text">{customer.RazonSocial || 'Cliente'}</p>
                                        {(customer.Rut || customer.Telefono) && (
                                          <div className="flex items-center gap-1 flex-wrap">
                                            {customer.Rut && <p className="font-sans font-normal text-xs text-text-subtle">RUT: {customer.Rut}</p>}
                                            {customer.Rut && customer.Telefono && <span className="text-text-subtle">·</span>}
                                            {customer.Telefono && <p className="font-sans font-normal text-xs text-text-subtle">{customer.Telefono.replace(/^\+{2,}/, '+')}</p>}
                                          </div>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : selectedCustomer ? (
                          <div className="relative flex flex-col gap-0 group/customer animate-in fade-in duration-200">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                            {editingCustomerName ? (
                              <div className="relative flex-1 min-w-0" ref={customerSearchFieldRef}>
                                <Input
                                  autoFocus
                                  placeholder="Buscar por RUT o razón social..."
                                  value={customerSearch}
                                  onChange={e => setCustomerSearch(e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                  onKeyDown={e => { e.stopPropagation(); handleCustomerSearchKeyDown(e, () => setEditingCustomerName(false)); }}
                                  className="py-1.5 text-sm"
                                />
                                {customers.length > 0 && (
                                  <div ref={customerListPopoverRef} className="absolute left-0 right-0 top-full z-[60] mt-1 max-h-[320px] overflow-y-auto rounded-lg border border-border bg-background shadow-lg">
                                    {!customerSearch.trim() && (
                                      <p className="px-3 pt-2 pb-1 text-xs font-medium text-text-subtle uppercase tracking-wide">Recientes</p>
                                    )}
                                    {isLoadingCustomers && customers.length === 0 ? (
                                      <p className="p-3 text-sm text-text-subtle text-center">Buscando...</p>
                                    ) : (
                                      <div className="py-1">
                                        {customers.map((customer, index) => (
                                          <button
                                            key={`${customer.Rut || ''}-${index}`}
                                            data-kbd-item
                                            type="button"
                                            onClick={e => { e.stopPropagation(); handleCustomerSelect(customer); setEditingCustomerName(false); }}
                                            className={`w-full px-3 py-2 text-left transition-colors ${activeCustomerIndex === index ? 'bg-muted' : 'hover:bg-muted'}`}
                                          >
                                            <p className="font-sans font-medium text-sm text-text">{customer.RazonSocial || 'Cliente'}</p>
                                            {(customer.Rut || customer.Telefono) && (
                                              <div className="flex items-center gap-1 flex-wrap">
                                                {customer.Rut && <p className="font-sans font-normal text-xs text-text-subtle">RUT: {customer.Rut}</p>}
                                                {customer.Rut && customer.Telefono && <span className="text-text-subtle">·</span>}
                                                {customer.Telefono && <p className="font-sans font-normal text-xs text-text-subtle">{customer.Telefono.replace(/^\+{2,}/, '+')}</p>}
                                              </div>
                                            )}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex-1 min-w-0">
                                <span className="font-sans font-semibold text-base text-text tracking-[-0.16px] break-words">
                                  {selectedCustomer.RazonSocial || 'Cliente'}
                                </span>
                                {!isFacturaEnviada && !isProcessingPdf && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setEditingCustomerNameValue(selectedCustomer.RazonSocial || ''); setEditingCustomerName(true); }}
                                    className="opacity-0 group-hover/customer:opacity-100 p-0.5 rounded hover:bg-muted transition-all inline-flex align-middle ml-0.5"
                                  >
                                    <PencilSimple size={12} weight="bold" className="text-text-subtle" />
                                  </button>
                                )}
                                {(selectedCustomer.Rut || selectedCustomer.Telefono) && (
                                  <span className="inline font-sans font-medium text-xs text-text-subtle tracking-[-0.12px] whitespace-nowrap">
                                    {' '}
                                    {selectedCustomer.Rut && <span>RUT: {selectedCustomer.Rut}</span>}
                                    {selectedCustomer.Rut && selectedCustomer.Telefono && <span className="text-border mx-1 select-none">|</span>}
                                    {selectedCustomer.Telefono && <span>{selectedCustomer.Telefono.replace(/^\+{2,}/, '+')}</span>}
                                  </span>
                                )}
                              </div>
                            )}

                            <div className="shrink-0 z-[60] pointer-events-auto">
                                <SearchableDropdown
                                  ref={addressDropdownRef}
                                  mode="showAll"
                                  disabled={isFacturaEnviada}
                                  minWidth={280}
                                  autoFocusSearch={false}
                                  searchPlaceholder="Filtrar direcciones..."
                                  items={clientDbAddresses
                                    .filter((addr) => addr.id !== selectedAddressId)
                                    .map((addr) => ({
                                      id: addr.id,
                                      label: addr.nombre || addr.address || '—',
                                      subtitle: (() => {
                                        const parts = addr.nombre
                                          ? [addr.address, addr.comuna, addr.city].filter(Boolean)
                                          : [addr.comuna, addr.city].filter(Boolean);
                                        return parts.join(', ') || undefined;
                                      })(),
                                    }))}
                                  selectedId={null}
                                  isLoading={loadingClientAddresses}
                                  onSelect={(item) => {
                                    const addr = clientDbAddresses.find((a) => a.id === item.id);
                                    if (addr) handleSelectDbAddress(addr);
                                  }}
                                  onOpen={() => {
                                    if (selectedCustomer?.Rut) loadClientDbAddresses(selectedCustomer.Rut);
                                    setShowNewAddressForm(false);
                                    setEditingAddressId(null);
                                    setNewAddressNombre('');
                                    setNewAddressContacto('');
                                    setNewAddressInput('');
                                    setNewAddressComuna('');
                                    setNewAddressCity('');
                                  }}
                                  renderItem={(item, onSelectItem) => {
                                    const addr = clientDbAddresses.find((a) => a.id === item.id);
                                    if (!addr) return null;
                                    const isEditing = editingAddressId === addr.id;
                                    return (
                                      <div key={addr.id} className="border-b border-border last:border-b-0">
                                        <div
                                          className="group relative flex items-center gap-2 px-3 py-2 hover:bg-muted cursor-pointer"
                                          onClick={() => !isEditing && onSelectItem()}
                                        >
                                          <div className="flex-1 min-w-0">
                                            <p className="font-sans font-medium text-xs text-text truncate">{item.label}</p>
                                            {item.subtitle && (
                                              <p className="font-sans text-xs text-text-subtle truncate mt-0.5">{item.subtitle}</p>
                                            )}
                                          </div>
                                          {!isEditing && (
                                            <div className="absolute top-0 right-0 bottom-0 hidden group-hover:flex items-center gap-1 shrink-0 px-3 py-2 bg-muted/50 backdrop-blur-sm">
                                              <button
                                                type="button"
                                                onClick={e => {
                                                  e.stopPropagation();
                                                  setEditingAddressId(addr.id);
                                                  setNewAddressNombre(addr.nombre || '');
                                                  setNewAddressContacto(addr.contacto || '');
                                                  setNewAddressInput(addr.address);
                                                  setNewAddressComuna(addr.comuna || '');
                                                  setNewAddressCity(addr.city || '');
                                                  setShowNewAddressForm(false);
                                                }}
                                                className="p-1 rounded hover:bg-muted-foreground/10 transition-colors"
                                              >
                                                <PencilSimple size={14} weight="bold" className="text-text-subtle" />
                                              </button>
                                              <DeleteButton
                                                onDelete={async () => {
                                                  await api.deleteClientAddress(addr.id);
                                                  setClientDbAddresses(prev => prev.filter(a => a.id !== addr.id));
                                                }}
                                              />
                                            </div>
                                          )}
                                        </div>
                                        {isEditing && (
                                          <div className="p-3 bg-muted/30 flex flex-col gap-2">
                                            <div>
                                              <label className="block font-sans text-xs font-semibold text-text mb-1">Nombre:</label>
                                              <input
                                                ref={addressNombreRef}
                                                type="text"
                                                placeholder="Nombre de la dirección (ej: Oficina Central)"
                                                value={newAddressNombre}
                                                onChange={e => setNewAddressNombre(e.target.value)}
                                                className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                              />
                                            </div>
                                            <div>
                                              <label className="block font-sans text-xs font-semibold text-text mb-1">Contacto:</label>
                                              <input
                                                type="text"
                                                placeholder="Contacto"
                                                value={newAddressContacto}
                                                onChange={e => setNewAddressContacto(e.target.value)}
                                                className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                              />
                                            </div>
                                            <div>
                                              <label className="block font-sans text-xs font-semibold text-text mb-1">Dirección:</label>
                                              <input
                                                type="text"
                                                placeholder="Dirección"
                                                value={newAddressInput}
                                                onChange={e => setNewAddressInput(e.target.value)}
                                                className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                              />
                                            </div>
                                            <div className="flex gap-2">
                                              <div className="flex-1">
                                                <label className="block font-sans text-xs font-semibold text-text mb-1">Comuna:</label>
                                                <input
                                                  type="text"
                                                  placeholder="Comuna"
                                                  value={newAddressComuna}
                                                  onChange={e => setNewAddressComuna(e.target.value)}
                                                  className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                                />
                                              </div>
                                              <div className="flex-1">
                                                <label className="block font-sans text-xs font-semibold text-text mb-1">Ciudad:</label>
                                                <input
                                                  type="text"
                                                  placeholder="Ciudad"
                                                  value={newAddressCity}
                                                  onChange={e => setNewAddressCity(e.target.value)}
                                                  className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                                />
                                              </div>
                                            </div>
                                            <div className="flex gap-2 justify-end">
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setEditingAddressId(null);
                                                  setNewAddressNombre('');
                                                  setNewAddressContacto('');
                                                  setNewAddressInput('');
                                                  setNewAddressComuna('');
                                                  setNewAddressCity('');
                                                }}
                                                className="font-sans text-xs text-text-subtle hover:text-text px-2 py-1 rounded transition-colors"
                                              >
                                                Cancelar
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => handleSaveEditedAddress(addr.id)}
                                                disabled={!newAddressInput.trim() || savingNewAddress}
                                                className="font-sans text-xs font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 px-3 py-1 rounded transition-colors"
                                              >
                                                {savingNewAddress ? <Loader /> : 'Guardar'}
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }}
                                  header={
                                    <div>
                                      <div className="px-3 py-2 bg-muted/50 group">
                                        <p className="font-sans font-medium text-xs text-text-subtle mb-1">{addressSource === 'pdf' ? 'Tomada de la OC' : addressSource === 'kame' ? 'Tomada de Kame' : 'Seleccionada'}</p>
                                        <div className="flex items-center gap-1">
                                          <div className="flex-1 min-w-0">
                                            <p className="font-sans font-semibold text-xs text-text truncate">
                                              {selectedAddressName || selectedCustomer.Direccion}
                                            </p>
                                            {selectedAddressName && (
                                              <p className="font-sans text-xs text-text-subtle truncate mt-0.5">
                                                {[selectedCustomer.Direccion, selectedCustomer.Comuna, selectedCustomer.Ciudad].filter(Boolean).join(', ')}
                                              </p>
                                            )}
                                          </div>
                                          {editingAddressId !== 'selected' && (
                                            <CustomTooltip text="Editar dirección">
                                              <button
                                                type="button"
                                                onClick={openSelectedAddressEdit}
                                                className="group p-0.5 rounded hover:bg-muted transition-all"
                                              >
                                                <PencilSimple size={11} weight="bold" className="text-text-subtle" />
                                              </button>
                                            </CustomTooltip>
                                          )}
                                        </div>
                                      </div>
                                      {editingAddressId === 'selected' && (
                                        <div className="p-3 bg-muted/30 flex flex-col gap-2">
                                          <div>
                                            <label className="block font-sans text-xs font-semibold text-text mb-1">Nombre:</label>
                                            <input
                                              autoFocus
                                              type="text"
                                              placeholder="Nombre de la dirección (ej: Oficina Central)"
                                              value={newAddressNombre}
                                              onChange={e => setNewAddressNombre(e.target.value)}
                                              className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                            />
                                          </div>
                                          <div>
                                            <label className="block font-sans text-xs font-semibold text-text mb-1">Contacto:</label>
                                            <input
                                              type="text"
                                              placeholder="Contacto"
                                              value={newAddressContacto}
                                              onChange={e => setNewAddressContacto(e.target.value)}
                                              className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                            />
                                          </div>
                                          <div>
                                            <label className="block font-sans text-xs font-semibold text-text mb-1">Dirección:</label>
                                            <input
                                              type="text"
                                              placeholder="Dirección"
                                              value={newAddressInput}
                                              onChange={e => setNewAddressInput(e.target.value)}
                                              className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                            />
                                          </div>
                                          <div className="flex gap-2">
                                            <div className="flex-1">
                                              <label className="block font-sans text-xs font-semibold text-text mb-1">Comuna:</label>
                                              <input
                                                type="text"
                                                placeholder="Comuna"
                                                value={newAddressComuna}
                                                onChange={e => setNewAddressComuna(e.target.value)}
                                                className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                              />
                                            </div>
                                            <div className="flex-1">
                                              <label className="block font-sans text-xs font-semibold text-text mb-1">Ciudad:</label>
                                              <input
                                                type="text"
                                                placeholder="Ciudad"
                                                value={newAddressCity}
                                                onChange={e => setNewAddressCity(e.target.value)}
                                                className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                              />
                                            </div>
                                          </div>
                                          <div className="flex gap-2 justify-end">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setEditingAddressId(null);
                                                setNewAddressNombre('');
                                                setNewAddressContacto('');
                                                setNewAddressInput('');
                                                setNewAddressComuna('');
                                                setNewAddressCity('');
                                              }}
                                              className="font-sans text-xs text-text-subtle hover:text-text px-2 py-1 rounded transition-colors"
                                            >
                                              Cancelar
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => handleSaveEditedAddress('selected')}
                                              disabled={!newAddressInput.trim() || savingNewAddress}
                                              className="font-sans text-xs font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 px-3 py-1 rounded transition-colors"
                                            >
                                              {savingNewAddress ? <Loader /> : 'Guardar'}
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  }
                                  footer={
                                    showNewAddressForm ? (
                                      <div className="p-3 flex flex-col gap-2">
                                        <div>
                                          <label className="block font-sans text-xs font-semibold text-text mb-1">Nombre:</label>
                                          <input
                                            autoFocus
                                            type="text"
                                            placeholder="Nombre de la dirección (ej: Oficina Central)"
                                            value={newAddressNombre}
                                            onChange={e => setNewAddressNombre(e.target.value)}
                                            className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                          />
                                        </div>
                                        <div>
                                          <label className="block font-sans text-xs font-semibold text-text mb-1">Contacto:</label>
                                          <input
                                            type="text"
                                            placeholder="Contacto"
                                            value={newAddressContacto}
                                            onChange={e => setNewAddressContacto(e.target.value)}
                                            className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                          />
                                        </div>
                                        <div>
                                          <label className="block font-sans text-xs font-semibold text-text mb-1">Dirección:</label>
                                          <input
                                            type="text"
                                            placeholder="Dirección"
                                            value={newAddressInput}
                                            onChange={e => setNewAddressInput(e.target.value)}
                                            className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                          />
                                        </div>
                                        <div className="flex gap-2">
                                          <div className="flex-1">
                                            <label className="block font-sans text-xs font-semibold text-text mb-1">Comuna:</label>
                                            <input
                                              type="text"
                                              placeholder="Comuna"
                                              value={newAddressComuna}
                                              onChange={e => setNewAddressComuna(e.target.value)}
                                              className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                            />
                                          </div>
                                          <div className="flex-1">
                                            <label className="block font-sans text-xs font-semibold text-text mb-1">Ciudad:</label>
                                            <input
                                              type="text"
                                              placeholder="Ciudad"
                                              value={newAddressCity}
                                              onChange={e => setNewAddressCity(e.target.value)}
                                              className="w-full px-2 py-1.5 text-xs font-sans border border-border rounded bg-background text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-ring"
                                            />
                                          </div>
                                        </div>
                                        <div className="flex gap-2 justify-end">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setShowNewAddressForm(false);
                                              setEditingAddressId(null);
                                              setNewAddressNombre('');
                                              setNewAddressContacto('');
                                              setNewAddressInput('');
                                              setNewAddressComuna('');
                                              setNewAddressCity('');
                                            }}
                                            className="font-sans text-xs text-text-subtle hover:text-text px-2 py-1 rounded transition-colors"
                                          >
                                            Cancelar
                                          </button>
                                          <button
                                            type="button"
                                            onClick={handleSaveNewAddress}
                                            disabled={!newAddressInput.trim() || savingNewAddress}
                                            className="font-sans text-xs font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 px-3 py-1 rounded transition-colors"
                                          >
                                            {savingNewAddress ? <Loader /> : 'Guardar'}
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setShowNewAddressForm(true); }}
                                        className="flex items-center gap-1.5 w-full px-3 py-2 text-xs font-sans text-text-subtle hover:text-text hover:bg-muted/50 transition-colors"
                                      >
                                        <Plus size={11} weight="bold" />
                                        Nueva dirección
                                      </button>
                                    )
                                  }
                                  trigger={
                                    <button
                                      type="button"
                                      disabled={isFacturaEnviada}
                                      className="flex items-start gap-1 text-xs text-text-subtle rounded border border-border bg-muted/60 px-2 py-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:enabled:text-text hover:enabled:bg-muted"
                                    >
                                      <MapPin size={12} weight="fill" className="shrink-0 mt-0.5" />
                                      <span className="font-sans font-medium text-xs max-w-[210px] whitespace-normal leading-tight text-left">
                                        {selectedAddressName || [selectedCustomer.Direccion, selectedCustomer.Comuna].filter(Boolean).join(', ') || 'Sin dirección'}
                                      </span>
                                      <CaretDown size={10} weight="bold" className="shrink-0 mt-0.5" />
                                    </button>
                                  }
                                />
                            </div>
                            </div>
                          </div>
                        ) : isProcessingPdf && orderModalMode !== 'create' ? null : (
                          <div className="flex items-center gap-2 animate-in fade-in duration-200">
                            <p className="font-sans font-normal text-sm text-text-subtle tracking-[-0.14px]">
                              Seleccionar cliente
                            </p>
                            <Tag size={12} weight="fill" className="text-yellow-600" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>


                  {(getFieldError('addressName') || getFieldError('contacto') || getFieldError('address') || getFieldError('comuna') || getFieldError('ciudad')) && (
                    <div className="mb-2 p-3 rounded-md bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-700/40">
                      <p className="text-sm text-yellow-800 dark:text-yellow-300 font-sans font-medium mb-1">Faltan datos de entrega:</p>
                      <ul className="list-disc list-inside space-y-0.5 mb-2.5">
                        {getFieldError('addressName') && <li className="text-xs text-yellow-700 dark:text-yellow-400 font-sans">Nombre de dirección</li>}
                        {getFieldError('contacto') && <li className="text-xs text-yellow-700 dark:text-yellow-400 font-sans">Contacto</li>}
                        {getFieldError('address') && <li className="text-xs text-yellow-700 dark:text-yellow-400 font-sans">Dirección</li>}
                        {getFieldError('comuna') && <li className="text-xs text-yellow-700 dark:text-yellow-400 font-sans">Comuna</li>}
                        {getFieldError('ciudad') && <li className="text-xs text-yellow-700 dark:text-yellow-400 font-sans">Ciudad</li>}
                      </ul>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={openSelectedAddressEdit}
                          className="inline-flex items-center gap-1 text-xs font-sans font-medium px-2 py-1 rounded border border-yellow-400 text-yellow-800 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors"
                        >
                          <PencilSimple size={11} weight="bold" />
                          Completar datos
                        </button>
                        {hasConfirmedAddressWarning && (
                          <button
                            type="button"
                            onClick={() => orderModalMode === 'edit' ? handleUpdateOrder() : handleSaveOrder()}
                            className="inline-flex items-center gap-1 text-xs font-sans font-medium px-2 py-1 rounded border border-yellow-400 text-yellow-800 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors"
                          >
                            Guardar de todas formas
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {!isFacturaEnviada && (
                    <div className="sticky top-0 z-50 mb-2 flex items-center justify-between w-full gap-3 px-2 py-0 bg-muted">
                      <button
                        type="button"
                        onClick={addEmptyLineItem}
                        disabled={isProcessingPdf || !selectedCustomer}
                        className={`flex items-center gap-1 shrink-0 transition-colors ${
                          isProcessingPdf || !selectedCustomer ? 'pointer-events-none opacity-50' : 'hover:opacity-80'
                        }`}
                      >
                        <Plus size={14} weight="bold" />
                        <span className="font-sans font-medium text-xs text-text-subtle">Agregar producto</span>
                      </button>


                      {/* Folio Badge — shown in create and edit mode, editable */}
                      {(orderModalMode === 'create' || orderModalMode === 'edit') && (
                        <div className={`shrink-0 ${isProcessingPdf ? 'pointer-events-none opacity-50' : ''}`}>
                          {isEditingFolio ? (
                            <div className="flex items-center gap-1">
                              <span className="text-text-subtle text-xs font-sans">Folio:</span>
                              <input
                                type="number"
                                autoFocus
                                value={folioInput}
                                onChange={e => setFolioInput(e.target.value)}
                                onBlur={confirmFolioEdit}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') confirmFolioEdit();
                                  if (e.key === 'Escape') {
                                    setFolioInput(assignedFolio);
                                    setIsEditingFolio(false);
                                  }
                                }}
                                className="w-16 px-1.5 py-0.5 border border-border rounded text-xs font-sans font-semibold text-text bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                              />
                              <button
                                type="button"
                                onClick={confirmFolioEdit}
                                className="p-0.5 rounded hover:bg-muted transition-colors shrink-0"
                              >
                                <Check size={13} weight="bold" className="text-text" />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => { setFolioInput(assignedFolio); setIsEditingFolio(true); }}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs font-sans font-medium text-text hover:bg-muted/80 transition-colors"
                            >
                              <span className="text-text-subtle">Folio:</span>
                              <span className="font-semibold">{assignedFolio || '—'}</span>
                            </button>
                          )}
                        </div>
                      )}

                      {/* Price List Indicator with Dropdown */}
                      <div className={`shrink-0 z-[60] ${(isProcessingPdf || !selectedCustomer) ? 'pointer-events-none' : 'pointer-events-auto'}`}>
                        <SearchableDropdown
                          mode="showAll"
                          disabled={isProcessingPdf || !selectedCustomer}
                          minWidth={224}
                          searchPlaceholder="Filtrar lista..."
                          items={[{ id: 'Sin lista', label: 'Sin lista' }, ...availablePriceLists.map(l => ({ id: l.name, label: l.name }))]}
                          selectedId={null}
                          onSelect={(item) => handlePriceListSelect(item.label)}
                          trigger={
                            <button
                              type="button"
                              disabled={isProcessingPdf || !selectedCustomer}
                              className={`flex items-center gap-1 text-xs rounded border px-2 py-1.5 transition-colors ${
                                selectedCustomer?.ListaDePrecio
                                  ? 'text-text-subtle border-border bg-muted/60 hover:enabled:bg-muted'
                                  : 'text-text-subtle/50 border-border/50 bg-muted/30 hover:enabled:bg-muted/40'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              <span className="font-sans font-medium text-xs text-text-subtle/70">Lista de precio</span>
                              <span className={`font-sans font-medium text-xs max-w-[150px] whitespace-nowrap truncate ${!selectedCustomer ? 'italic' : ''}`}>
                                {!selectedCustomer ? 'Selecciona cliente' : (selectedCustomer.ListaDePrecio || '—')}
                              </span>
                              <CaretDown size={10} weight="bold" className="shrink-0" />
                            </button>
                          }
                        />
                      </div>
                    </div>
                  )}

                  <div
                    className={`flex-1 overflow-y-auto min-h-0 min-w-0 rounded-t-lg rounded-br-none rounded-bl-none border border-border bg-background transition-colors flex flex-col ${
                      isProductListDropActive ? 'ring-2 ring-primary bg-primary/5' : ''
                    }`}
                    onDragOver={handleProductListDragOver}
                    onDragLeave={handleProductListDragLeave}
                    onDrop={handleProductListPdfDrop}
                  >
                    {newOrderItems.length === 0 ? (
                      <div className="flex-1 flex flex-col min-h-0 bg-background overflow-hidden">
                        {isProcessingPdf && loadingQuote && orderModalMode === 'create' ? (
                          <div className="flex-1 flex flex-col items-center justify-center py-12 px-8 bg-background" role="status" aria-live="polite">
                            <div className={`transition-opacity duration-300 ${isQuoteFading ? 'opacity-0' : 'opacity-100'}`}>
                              {loadingQuote.type === 'tip' ? (
                                <div className="text-center max-w-xs">
                                  <p className="font-sans text-xs font-semibold text-text-subtle uppercase tracking-widest mb-2">Consejo</p>
                                  <p className="font-sans text-sm text-text-subtle leading-relaxed">{loadingQuote.text}</p>
                                </div>
                              ) : (
                                <div className="text-center max-w-xs">
                                  <p className="font-sans text-sm text-text-subtle leading-relaxed">"{loadingQuote.quote}"</p>
                                  <p className="font-sans text-xs text-text-subtle/60 mt-2">— {loadingQuote.author}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : isProcessingPdf ? (
                          <div className="flex-1 bg-background" />
                        ) : (
                        <div
                          onClick={!selectedCustomer ? () => setEditingCustomerName(true) : addEmptyLineItem}
                          className="flex-1 flex flex-col items-center justify-center py-12 px-4 bg-background empty-product-state cursor-pointer"
                        >
                          <ShoppingCart size={48} weight="light" className="text-text-subtle mb-3" />
                          <p className="font-sans text-sm text-text-subtle text-center empty-product-label">
                            {!selectedCustomer ? 'Selecciona un cliente para continuar.' : 'Agrega un producto para continuar.'}
                          </p>
                        </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-background block p-2 w-full min-w-0 animate-in fade-in duration-200">
                <table className="w-full border-collapse table-fixed">
                  <colgroup>
                    <col style={{ width: 'auto' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '75px' }} />
                    <col style={{ width: '40px' }} />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 pb-3 text-left">
                        <button onClick={() => handleProductSort('name')} className="flex items-center gap-1 hover:opacity-70 transition-opacity">
                          <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">Producto</p>
                          {productSortField === 'name' && (productSortDirection === 'asc' ? <CaretUp size={12} weight="bold" className="text-text-subtle" /> : <CaretDown size={12} weight="bold" className="text-text-subtle" />)}
                        </button>
                      </th>
                      <th className="py-2 pb-3 text-center">
                        <button onClick={() => handleProductSort('quantity')} className="flex items-center justify-center gap-1 w-full hover:opacity-70 transition-opacity">
                          <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">Cantidad</p>
                          {productSortField === 'quantity' && (productSortDirection === 'asc' ? <CaretUp size={12} weight="bold" className="text-text-subtle" /> : <CaretDown size={12} weight="bold" className="text-text-subtle" />)}
                        </button>
                      </th>
                      <th className="py-2 pb-3 text-center">
                        <button onClick={() => handleProductSort('unitPrice')} className="flex items-center justify-center gap-1 w-full hover:opacity-70 transition-opacity">
                          <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">Precio Unit.</p>
                          {productSortField === 'unitPrice' && (productSortDirection === 'asc' ? <CaretUp size={12} weight="bold" className="text-text-subtle" /> : <CaretDown size={12} weight="bold" className="text-text-subtle" />)}
                        </button>
                      </th>
                      <th className="py-2 pb-3 text-right">
                        <button onClick={() => handleProductSort('total')} className="flex items-center justify-end gap-1 w-full hover:opacity-70 transition-opacity">
                          <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">Total</p>
                          {productSortField === 'total' && (productSortDirection === 'asc' ? <CaretUp size={12} weight="bold" className="text-text-subtle" /> : <CaretDown size={12} weight="bold" className="text-text-subtle" />)}
                        </button>
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                {sortProducts(newOrderItems).map((item: OrderItem, index: number) => {
                  const searchQ = lineItemProductSearch[item.id] ?? '';
                  const results = lineItemProductResults[item.id] ?? [];
                  const loadingRow = lineItemProductLoading[item.id] ?? false;
                  return (
                    <NewOrderProductLineBlock
                      key={item.id || index}
                      item={item}
                      index={index}
                      layout="desktop"
                      searchQ={searchQ}
                      results={results}
                      loadingRow={loadingRow}
                      readOnly={isFacturaEnviada}
                      flashPrice={priceListFlash || !!(flashItemData.get(item.id)?.price)}
                      flashTotal={priceListFlash || !!(flashItemData.get(item.id)?.total)}
                      hasError={hasAttemptedSave && !isItemValid(item)}
                      saveAttempt={!isItemValid(item) && newOrderItems.filter(i => !isItemValid(i))[0]?.id === item.id ? saveAttemptCount : 0}
                      priceListId={selectedCustomer?.ListaDePrecio ? (priceListNameToId[selectedCustomer.ListaDePrecio.toLowerCase()] ?? null) : null}
                      onSearchChange={(id, v) =>
                        setLineItemProductSearch((prev) => ({ ...prev, [id]: v }))
                      }
                      onSearchClear={handleLineItemSearchClear}
                      onSelectProduct={applyProductToLineItem}
                      onCreateProduct={applyProductToLineItem}
                      onClearProduct={clearProductFromLineItem}
                      onQuantityChange={updateLineItemQuantity}
                      onUnitChange={updateLineItemUnit}
                      onUnitPriceChange={updateLineItemUnitPrice}
                      onPriceSourceSelect={handlePriceSourceSelect}
                      onDelete={handleDeleteItem}
                      onAddItem={addEmptyLineItem}
                      onDescripcionChange={updateLineItemDescripcion}
                      onPdfHighlight={(text, page) => { setPdfHighlightText(text); setPdfHighlightPage(page); setPdfHighlightReqId(r => r + 1); }}
                    />
                  );
                })}
                  </tbody>
                </table>

                {/* Mobile (layout lg+): mismas líneas en columna */}
                {sortProducts(newOrderItems).map((item: OrderItem, index: number) => {
                  const searchQ = lineItemProductSearch[item.id] ?? '';
                  const results = lineItemProductResults[item.id] ?? [];
                  const loadingRow = lineItemProductLoading[item.id] ?? false;
                  return (
                    <div
                      key={`mobile-${item.id || index}`}
                      className="hidden w-full flex flex-col gap-2 border-b border-border last:border-b-0 pb-3"
                    >
                      <NewOrderProductLineBlock
                        item={item}
                        index={index}
                        layout="mobile"
                        searchQ={searchQ}
                        results={results}
                        loadingRow={loadingRow}
                        readOnly={isFacturaEnviada}
                        flashPrice={priceListFlash || !!(flashItemData.get(item.id)?.price)}
                        flashTotal={priceListFlash || !!(flashItemData.get(item.id)?.total)}
                        onSearchChange={(id, v) =>
                          setLineItemProductSearch((prev) => ({ ...prev, [id]: v }))
                        }
                        onSearchClear={handleLineItemSearchClear}
                        onSelectProduct={applyProductToLineItem}
                        onCreateProduct={applyProductToLineItem}
                        onClearProduct={clearProductFromLineItem}
                        onQuantityChange={updateLineItemQuantity}
                        onUnitChange={updateLineItemUnit}
                        onUnitPriceChange={updateLineItemUnitPrice}
                        onPriceSourceSelect={handlePriceSourceSelect}
                        onDelete={handleDeleteItem}
                        onDescripcionChange={updateLineItemDescripcion}
                        onPdfHighlight={(text, page) => { setPdfHighlightText(text); setPdfHighlightPage(page); setPdfHighlightReqId(r => r + 1); }}
                      />
                    </div>
                  );
                })}

              </div>
            )}
                  </div>

                {/* Resumen de totales - Desktop (sticky) */}
                {(() => {
                  const subtotal = newOrderItems.reduce((sum, item) => {
                    const price = parseFloat(String(item.unitPrice ?? '0'));
                    return sum + (item.quantity ?? 0) * (Number.isFinite(price) ? price : 0);
                  }, 0);
                  const impuestos = Math.round(subtotal * 0.19);
                  const total = subtotal + impuestos;
                  const hasItems = newOrderItems.length > 0;
                  return (
                    <div className="flex flex-col gap-1 items-start py-0.5 shrink-0 w-full sticky bottom-0 z-10 bg-background border border-border border-t-0 rounded-b-lg" style={{ borderTop: '1.11px solid hsl(var(--border))' }}>
                      <div className="w-full flex flex-col gap-1">
                        <div className={`bg-background relative px-2 py-0.5 rounded-lg w-full transition-opacity ${isProcessingPdf ? 'pointer-events-none opacity-50' : ''}`}>
                          {hasItems && (
                            <span className="absolute left-2 top-0.5 text-xs text-text-subtle font-sans font-medium leading-[1.4] select-none overflow-hidden inline-block">
                              {(priceListFlash || countFlash) && <span className="price-shine-overlay" />}
                              {newOrderItems.length} producto{newOrderItems.length !== 1 ? 's' : ''}
                            </span>
                          )}
                          <OrderSummary
                            subtotal={hasItems ? `$${subtotal.toLocaleString('es-CL')}` : ''}
                            taxes={hasItems ? `$${impuestos.toLocaleString('es-CL')}` : ''}
                            surcharges="$0"
                            total={hasItems ? `$${total.toLocaleString('es-CL')}` : ''}
                            flash={priceListFlash || totalsFlash}
                            labelOffset="auto"
                            labelWidth="96px"
                            valueWidth="85px"
                            trailingWidth="40px"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}
                </div>
              </div>
            </div>
          </div>

            {/* Panel Derecho - flex-[2] */}
            <div className="flex-[4] hidden lg:flex flex-col min-h-0 overflow-hidden px-4 py-2">
              <div className="flex-1 rounded-lg overflow-hidden flex flex-col w-full border border-border">
                {(selectedPdfFile || consultingPdfUrl || isProcessingPdf) ? (
                  <>
                    {isProcessingPdf ? (
                      // Mostrar loader mientras procesa
                      <div className="flex-1 flex flex-col items-center justify-center p-8">
                        <div className="flex flex-col items-center gap-4">
                          <div className="relative" style={{ width: '100px', height: '100px', fontSize: '100px' }}>
                            <Loader />
                          </div>
                          <p className="font-sans font-semibold text-base text-text">
                            Procesando archivo
                          </p>
                          <ProcessingPhrase />
                        </div>
                      </div>
                    ) : (
                      // Mostrar PDF una vez procesado
                      <>
                        {(() => {
                          const isNonPdf = selectedPdfFile
                            ? selectedPdfFile.type !== 'application/pdf'
                            : /\.(xlsx|xls|csv)$/i.test(consultingPdfName ?? '');
                          if (isNonPdf) {
                            return (
                              <ExcelViewer
                                key={selectedPdfFile ? selectedPdfFile.name : (consultingPdfUrl ?? 'none')}
                                file={selectedPdfFile || undefined}
                                url={!selectedPdfFile && consultingPdfUrl ? consultingPdfUrl : undefined}
                                fileName={selectedPdfFile?.name || consultingPdfName || undefined}
                              />
                            );
                          }
                          return (
                            <PDFViewer
                              key={selectedPdfFile ? selectedPdfFile.name : (consultingPdfUrl ?? 'none')}
                              file={selectedPdfFile || undefined}
                              url={!selectedPdfFile && consultingPdfUrl ? consultingPdfUrl : undefined}
                              fileName={selectedPdfFile?.name || consultingPdfName || undefined}
                              highlightText={pdfHighlightText}
                              highlightPage={pdfHighlightPage}
                              highlightReqId={pdfHighlightReqId}
                            />
                          );
                        })()}
                        {/* Mostrar error si existe */}
                        {pdfProcessingError && (
                          <div className="px-4 py-3 border-t border-border bg-muted shrink-0">
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                              <p className="text-sm text-red-600 font-sans font-normal">
                                {pdfProcessingError}
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : orderModalMode === 'create' ? (
                  <div className="flex-1 flex items-center justify-center p-4">
                    <FileDropzone
                      onFilesSelected={async (files) => {
                        if (files.length > 0) await handlePdfFileSelected(files[0]);
                      }}
                      acceptedFileTypes={{
                        'application/pdf': ['.pdf'],
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                        'application/vnd.ms-excel': ['.xls'],
                        'text/csv': ['.csv'],
                      }}
                      maxFiles={1}
                      maxSize={10 * 1024 * 1024} // 10MB
                    />
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center p-4">
                    <p className="font-sans text-sm text-text-subtle">Sin OC adjunta</p>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Mobile view - mantener estructura simple para mobile */}
          <div className={`${isMobileDevice ? 'flex' : 'hidden'} flex-col flex-1 min-h-0 w-full px-4 py-4 gap-4`}>
            {/* Header */}
            <div className="bg-muted flex flex-col gap-2 items-start justify-center py-2 rounded-lg">
              <div className="px-3 w-full">
                <p className="font-sans font-medium text-base text-text tracking-[-0.16px] mb-2">
                  Nuevo pedido
                </p>
                  <div className="flex flex-col gap-2">
                    {/* Customer Tile */}
                    <div
                      onClick={() => { if (!isProcessingPdf) setShowCustomerDialog(true); }}
                      className={`w-full rounded-lg border transition-colors ${
                        'border-border bg-background cursor-pointer hover:bg-muted'
                      } ${!selectedCustomer ? 'border-dashed border-yellow-500' : ''} ${isProcessingPdf ? 'pointer-events-none opacity-50' : ''}`}
                      style={{ padding: '12px' }}
                    >
                      {selectedCustomer ? (
                        <div className="flex flex-col gap-1">
                          <p className="font-sans font-semibold text-base text-text tracking-[-0.16px] w-full truncate">
                            {selectedCustomer.RazonSocial || 'Cliente'}
                          </p>
                          {selectedCustomer.Rut && (
                            <p className="font-sans font-normal text-sm text-text-subtle tracking-[-0.14px] w-full truncate">
                              RUT: {selectedCustomer.Rut}
                            </p>
                          )}
                          {selectedCustomer.Telefono && (
                            <p className="font-sans font-normal text-sm text-text-subtle tracking-[-0.14px] w-full truncate">
                              {selectedCustomer.Telefono.replace(/^\+{2,}/, '+')}
                            </p>
                          )}
                          {selectedCustomer.Email && (
                            <p className="font-sans font-normal text-xs text-text-subtle tracking-[-0.12px] w-full truncate">
                              {selectedCustomer.Email}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="font-sans font-normal text-sm text-text-subtle tracking-[-0.14px]">
                            Seleccionar cliente de KAME
                          </p>
                          <Tag size={12} weight="fill" className="text-yellow-600" />
                        </div>
                      )}
                    </div>
                  </div>
              </div>
            </div>

            {/* Productos - Mobile */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="flex items-center justify-end mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Plus size={16} weight="bold" />}
                  onClick={addEmptyLineItem}
                >
                  Agregar
                </Button>
              </div>
              {newOrderItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 border border-border rounded-lg bg-background">
                  <ShoppingCart size={48} weight="light" className="text-text-subtle mb-3" />
                  <p className="font-sans font-normal text-sm text-text-subtle text-center">
                    No hay productos agregados
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {sortProducts(newOrderItems).map((item: OrderItem, index: number) => {
                    const searchQ = lineItemProductSearch[item.id] ?? '';
                    const results = lineItemProductResults[item.id] ?? [];
                    const loadingRow = lineItemProductLoading[item.id] ?? false;
                    return (
                      <div key={`mobile-lg-${item.id || index}`} className="flex flex-col gap-2 border-b border-border last:border-0 pb-3 last:pb-0">
                        <NewOrderProductLineBlock
                          item={item}
                          index={index}
                          layout="mobile"
                          searchQ={searchQ}
                          results={results}
                          loadingRow={loadingRow}
                          readOnly={isFacturaEnviada}
                          flashPrice={priceListFlash || !!(flashItemData.get(item.id)?.price)}
                          flashTotal={priceListFlash || !!(flashItemData.get(item.id)?.total)}
                          onSearchChange={(id, v) =>
                            setLineItemProductSearch((prev) => ({ ...prev,[id]: v }))
                          }
                          onSearchClear={handleLineItemSearchClear}
                          onSelectProduct={applyProductToLineItem}
                          onCreateProduct={applyProductToLineItem}
                          onClearProduct={clearProductFromLineItem}
                          onQuantityChange={updateLineItemQuantity}
                          onUnitChange={updateLineItemUnit}
                          onUnitPriceChange={updateLineItemUnitPrice}
                          onPriceSourceSelect={handlePriceSourceSelect}
                          onDelete={handleDeleteItem}
                          onDescripcionChange={updateLineItemDescripcion}
                          onPdfHighlight={(text, page) => { setPdfHighlightText(text); setPdfHighlightPage(page); setPdfHighlightReqId(r => r + 1); }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Resumen de totales */}
              {newOrderItems.length > 0 && (() => {
                const subtotal = newOrderItems.reduce((sum, item) => {
                  const price = parseFloat(String(item.unitPrice ?? '0'));
                  return sum + (item.quantity ?? 0) * (Number.isFinite(price) ? price : 0);
                }, 0);
                const impuestos = Math.round(subtotal * 0.19);
                const total = subtotal + impuestos;
                return (
                  <div className="bg-muted flex flex-col gap-2 items-start justify-center py-2 pb-4 relative shrink-0 w-full mt-2">
                    <div className="px-3 w-full flex flex-col gap-2">
                      <div className="bg-background flex flex-col items-start p-2 rounded-lg w-full">
                        <OrderSummary
                          subtotal={`$${subtotal.toLocaleString('es-CL')}`}
                          taxes={`$${impuestos.toLocaleString('es-CL')}`}
                          surcharges="$0"
                          total={`$${total.toLocaleString('es-CL')}`}
                          flash={priceListFlash || totalsFlash}
                        />
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

        </CenterPeekModal>

      {/* Dialog/BottomSheet para seleccionar cliente */}
      {isMobile ? (
        <BottomSheet open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
          <BottomSheetContent className="flex flex-col">
            <BottomSheetClose onClose={() => {
              setShowCustomerDialog(false);
              resetCustomerDialog();
            }} />
            <BottomSheetHeader>
              <BottomSheetTitle>Seleccionar Cliente</BottomSheetTitle>
            </BottomSheetHeader>
            
            <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">
              <div className="relative">
                <label className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px] mb-2 block">
                  Cliente
                </label>
                <div className="relative">
                  {!selectedCustomer ? (
                    <div ref={customerSearchFieldRef}>
                      <SearchField
                        autoFocus
                        placeholder="Buscar por RUT o razón social..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        onClear={() => { setCustomerSearch(''); setCustomers([]); }}
                        onKeyDown={(e) => handleCustomerSearchKeyDown(e)}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 rounded-lg border border-border">
                      <div className="flex-1 flex flex-col gap-1">
                        <p className="font-sans font-medium text-base text-foreground">
                          {selectedCustomer.RazonSocial || 'Cliente'}
                        </p>
                        {selectedCustomer.Rut && (
                          <p className="font-sans font-normal text-xs text-text-subtle">
                            RUT: {selectedCustomer.Rut}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleClearSelectedCustomer}
                        aria-label="Eliminar selección"
                      >
                        <X size={18} weight="bold" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {!selectedCustomer && customers.length > 0 ? (
                <div className="flex-1 flex flex-col min-h-0 border border-border rounded-lg overflow-hidden">
                  <div ref={customerListPopoverRef} className="overflow-y-auto flex-1">
                    {!customerSearch.trim() && (
                      <p className="px-4 pt-3 pb-1 text-xs font-medium text-text-subtle uppercase tracking-wide">Recientes</p>
                    )}
                    {isLoadingCustomers && customers.length === 0 ? (
                      <div className="p-4 text-center">
                        <p className="text-sm text-text-subtle">Buscando...</p>
                      </div>
                    ) : customers.length === 0 ? (
                      <div className="p-4 text-center">
                        <p className="text-sm text-text-subtle">No se encontraron clientes</p>
                      </div>
                    ) : (
                      <div className="py-2">
                        {customers.map((customer, index) => (
                          <button
                            key={`${customer.Rut || ''}-${index}`}
                            data-kbd-item
                            onClick={() => handleCustomerSelect(customer)}
                            className={`w-full px-4 py-3 text-left transition-colors border-b border-border last:border-b-0 ${activeCustomerIndex === index ? 'bg-muted' : 'hover:bg-muted'}`}
                          >
                            <div className="flex flex-col gap-1">
                              <p className="font-sans font-medium text-base text-foreground">
                                {customer.RazonSocial || 'Cliente'}
                              </p>
                              <div className="flex items-center gap-2 flex-wrap">
                                {customer.Rut && (
                                  <>
                                    <p className="font-sans font-normal text-xs text-muted-foreground">
                                      RUT: {customer.Rut}
                                    </p>
                                    {customer.Telefono && <span className="text-muted-foreground">•</span>}
                                  </>
                                )}
                                {customer.Telefono && (
                                  <p className="font-sans font-normal text-xs text-muted-foreground">
                                    {customer.Telefono.replace(/^\+{2,}/, '+')}
                                  </p>
                                )}
                              </div>
                              {customer.Email && (
                                <p className="font-sans font-normal text-xs text-muted-foreground">
                                  {customer.Email}
                                </p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            <BottomSheetFooter>
              <Button
                variant="primary"
                onClick={() => {
                  setShowCustomerDialog(false);
                  resetCustomerDialog();
                }}
                disabled={!selectedCustomer}
              >
                Seleccionar
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowCustomerDialog(false);
                  resetCustomerDialog();
                }}
              >
                Cancelar
              </Button>
            </BottomSheetFooter>
          </BottomSheetContent>
        </BottomSheet>
      ) : (
        <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
          <DialogContent className="px-6 py-6">
            <DialogClose onClose={() => {
              setShowCustomerDialog(false);
              resetCustomerDialog();
            }} />
            <DialogHeader>
              <DialogTitle>Seleccionar Cliente</DialogTitle>
            </DialogHeader>
            
            <div className="flex flex-col gap-4 my-6">
              <div className="relative">
                <label className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px] mb-2 block">
                  Cliente
                </label>
                <div className="relative">
                  {!selectedCustomer ? (
                    <div ref={customerSearchFieldRef}>
                      <SearchField
                        autoFocus
                        placeholder="Buscar por RUT o razón social..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        onClear={() => { setCustomerSearch(''); setCustomers([]); }}
                        onKeyDown={(e) => handleCustomerSearchKeyDown(e)}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 rounded-lg border border-border">
                      <div className="flex-1 flex flex-col gap-1">
                        <p className="font-sans font-medium text-base text-foreground">
                          {selectedCustomer.RazonSocial || 'Cliente'}
                        </p>
                        {selectedCustomer.Rut && (
                          <p className="font-sans font-normal text-xs text-text-subtle">
                            RUT: {selectedCustomer.Rut}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleClearSelectedCustomer}
                        aria-label="Eliminar selección"
                      >
                        <X size={18} weight="bold" />
                      </Button>
                    </div>
                  )}

                  <AnimatePresence>
                    {!selectedCustomer && customers.length > 0 && (
                      <motion.div
                        ref={customerListPopoverRef}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="bg-background border border-border rounded-lg shadow-lg max-h-[300px] flex flex-col overflow-hidden"
                      >
                        <div className="overflow-y-auto flex-1">
                          {!customerSearch.trim() && (
                            <p className="px-4 pt-3 pb-1 text-xs font-medium text-text-subtle uppercase tracking-wide">Recientes</p>
                          )}
                          {isLoadingCustomers && customers.length === 0 ? (
                            <div className="p-4 text-center">
                              <p className="text-sm text-text-subtle">Buscando...</p>
                            </div>
                          ) : customers.length === 0 ? (
                            <div className="p-4 text-center">
                              <p className="text-sm text-text-subtle">No se encontraron clientes</p>
                            </div>
                          ) : (
                            <div className="py-2">
                              {customers.map((customer, index) => (
                                <button
                                  key={`${customer.Rut || ''}-${index}`}
                                  data-kbd-item
                                  onClick={() => handleCustomerSelect(customer)}
                                  className={`w-full px-4 py-3 text-left transition-colors ${activeCustomerIndex === index ? 'bg-muted' : 'hover:bg-muted'}`}
                                >
                                  <div className="flex flex-col gap-1">
                                    <p className="font-sans font-medium text-base text-foreground">
                                      {customer.RazonSocial || 'Cliente'}
                                    </p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      {customer.Rut && (
                                        <>
                                          <p className="font-sans font-normal text-xs text-muted-foreground">
                                            RUT: {customer.Rut}
                                          </p>
                                          {customer.Telefono && <span className="text-muted-foreground">•</span>}
                                        </>
                                      )}
                                      {customer.Telefono && (
                                        <p className="font-sans font-normal text-xs text-muted-foreground">
                                          {customer.Telefono.replace(/^\+{2,}/, '+')}
                                        </p>
                                      )}
                                    </div>
                                    {customer.Email && (
                                      <p className="font-sans font-normal text-xs text-muted-foreground">
                                        {customer.Email}
                                      </p>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowCustomerDialog(false);
                  resetCustomerDialog();
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowCustomerDialog(false);
                  resetCustomerDialog();
                }}
                disabled={!selectedCustomer}
              >
                Seleccionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Overlay de facturación (similar a OrderDetail) */}
      {showInvoiceOverlay && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-card"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {!kameResponse ? (
            // Loading state
            <div className="flex flex-col gap-4 items-center">
              <div className="relative" style={{ width: '48px', height: '48px' }}>
                <Loader />
              </div>
              <p className="font-sans font-semibold text-base text-text text-center">
                Sincronizando pedido con KAME...
              </p>
              <p className="font-sans font-normal text-sm text-text-subtle max-w-[300px] text-center">
                Estamos creando el pedido en el sistema KAME. Esto puede tomar unos segundos.
              </p>
            </div>
          ) : !kameResponse.success ? (
            // Error de KAME
            (() => {
              const rawMsg = kameResponse.error?.message || kameResponse.error?.userFriendlyMessage || '';
              const rateLimit = parseRateLimitError(rawMsg);
              if (rateLimit) {
                return (
                  <div className="flex flex-col gap-4 items-center text-center px-4 max-w-[420px]">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: '32px', height: '32px', display: 'block', flexShrink: 0 }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex flex-col gap-2">
                      <h3 className="font-sans font-semibold text-xl text-text">Kame limitó temporalmente el acceso</h3>
                      <p className="font-sans font-normal text-sm text-text-subtle">No pudimos completar la consulta porque Kame bloqueó temporalmente nuevas solicitudes a su API por límite de uso.</p>
                      <p className="font-sans font-normal text-sm text-text-subtle">La integración está funcionando correctamente. Este límite es definido por Kame y afecta el acceso al servicio que ellos entregan.</p>
                      <p className="font-sans font-normal text-sm text-text-subtle">Puedes volver a intentarlo a las <span className="font-semibold text-text">{rateLimit.retryAt}</span>.</p>
                    </div>
                  </div>
                );
              }
              return (
            <div className="flex flex-col gap-6 items-center text-center px-4 max-w-[400px]">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: '32px', height: '32px', display: 'block', flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div className="flex flex-col gap-2 w-full">
                <h3 className="font-sans font-semibold text-xl text-text">
                  Error al sincronizar con KAME
                </h3>

                {(() => {
                  const friendlyMsg = kameResponse.error?.userFriendlyMessage || kameResponse.error?.message || '';

                  // UnidadNegocio error — dedicated message with link to Productos
                  if (friendlyMsg.startsWith('UNIDAD_NEGOCIO_ERROR:')) {
                    const productList = friendlyMsg.replace('UNIDAD_NEGOCIO_ERROR:', '').trim();
                    const products = productList ? productList.split('|').filter(Boolean) : [];
                    return (
                      <div className="flex flex-col gap-3 text-left">
                        <p className="font-sans font-normal text-sm text-text-subtle">
                          Los siguientes productos no tienen una <span className="font-semibold text-text">Unidad de Negocio</span> asignada y KAME rechaza el pedido:
                        </p>
                        {products.length > 0 && (
                          <ul className="flex flex-col gap-1">
                            {products.map((p, i) => (
                              <li key={i} className="font-sans font-normal text-sm text-text-subtle">• {p}</li>
                            ))}
                          </ul>
                        )}
                        <p className="font-sans font-normal text-sm text-text-subtle">
                          Ve a Productos y asigna la Unidad de Negocio correcta a cada uno, luego vuelve a intentarlo.
                        </p>
                        <a
                          href="/products"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-sans font-medium text-sm text-primary hover:underline"
                        >
                          Ir a Productos
                          <ArrowSquareOut size={14} weight="bold" />
                        </a>
                      </div>
                    );
                  }

                  // Generic error — collect all messages
                  const errorMessages: string[] = [];
                  if (friendlyMsg) errorMessages.push(friendlyMsg);
                  if (kameResponse.error?.details?.errors && kameResponse.error.details.errors.length > 0) {
                    kameResponse.error.details.errors.forEach((error) => {
                      const errorText = error.userFriendlyMessage || error.errorMessage;
                      if (errorText && !errorMessages.includes(errorText)) errorMessages.push(errorText);
                    });
                  }
                  return errorMessages.length > 0 ? (
                    <div className="flex flex-col gap-2 text-left">
                      {errorMessages.map((msg, index) => (
                        <p key={index} className="font-sans font-normal text-sm text-text-subtle">• {msg}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="font-sans font-normal text-sm text-text-subtle">Error desconocido al sincronizar con KAME</p>
                  );
                })()}
                
                {/* Mostrar errores detallados con información adicional si existen */}
                {kameResponse.error?.details?.errors && kameResponse.error.details.errors.length > 0 && (
                  <div className="mt-3 p-3 bg-red-50 rounded-md text-left">
                    <p className="font-sans font-semibold text-xs text-red-800 mb-2">
                      Detalles de los errores:
                    </p>
                    <ul className="space-y-2">
                      {kameResponse.error.details.errors.map((error, index) => (
                        <li key={index} className="font-sans font-normal text-xs text-red-700">
                          <div className="flex flex-col gap-1">
                            {error.productCode && (
                              <span className="font-medium">Producto: {error.productCode}</span>
                            )}
                            <span>{error.userFriendlyMessage || error.errorMessage}</span>
                            {error.errorType && (
                              <span className="text-red-600 italic">Tipo: {error.errorType}</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Mostrar nombres de productos afectados si están disponibles */}
                {kameResponse.error?.details?.productNames && kameResponse.error.details.productNames.length > 0 && (
                  <div className="mt-3 p-3 bg-red-50 rounded-md">
                    <p className="font-sans font-semibold text-xs text-red-800 mb-2">
                      Productos afectados:
                    </p>
                    <ul className="text-left space-y-1">
                      {kameResponse.error.details.productNames.map((productName, index) => (
                        <li key={index} className="font-sans font-normal text-xs text-red-700">
                          • {productName}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Mostrar posibles causas si están disponibles */}
                {kameResponse.error?.details?.possibleCauses && kameResponse.error.details.possibleCauses.length > 0 && (
                  <div className="mt-3 p-3 bg-yellow-50 rounded-md">
                    <p className="font-sans font-semibold text-xs text-yellow-800 mb-2">
                      Posibles causas:
                    </p>
                    <ul className="text-left space-y-1">
                      {kameResponse.error.details.possibleCauses.map((cause, index) => (
                        <li key={index} className="font-sans font-normal text-xs text-yellow-700">
                          • {cause}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {kameResponse.error?.status && (
                  <p className="font-sans font-normal text-xs text-text-subtle mt-1">
                    Código de error: {kameResponse.error.status}
                  </p>
                )}
                <p className="font-sans font-normal text-xs text-text-subtle mt-2">
                  El pedido se creó pero no se pudo facturar en KAME. Puedes intentar facturarlo nuevamente desde el detalle del pedido.
                </p>
              </div>
              <div className="flex flex-col gap-3 w-full">
                <Button
                  variant="primary"
                  fullWidth
                  onClick={() => {
                    setShowInvoiceOverlay(false);
                    setKameResponse(null);
                  }}
                >
                  Entendido
                </Button>
              </div>
            </div>
              );
            })()
          ) : (
            // Éxito
            <div className="flex flex-col gap-6 items-center text-center px-4 max-w-[400px]">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: '32px', height: '32px', display: 'block', flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="font-sans font-semibold text-xl text-text">
                  Pedido sincronizado con KAME
                </h3>
                {kameResponse.kameoneFolio && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                    <p className="font-sans font-normal text-xs text-green-800 mb-1">
                      Folio en KAME
                    </p>
                    <p className="font-sans font-bold text-lg text-green-900">
                      {kameResponse.kameoneFolio}
                    </p>
                    <a
                      href={`https://www.kameone.cl/Documento/Index?tipoDoc=V&folio=${kameResponse.kameoneFolio}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 flex items-center justify-center gap-2 w-full rounded-md border border-green-600 text-green-700 font-sans font-medium text-sm py-2 hover:bg-green-100 transition-colors"
                    >
                      Abrir orden en Kame
                    </a>
                  </div>
                )}
                {kameResponse.folioRetried && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-2 text-left">
                    <p className="font-sans font-normal text-sm text-yellow-800">
                      La orden fue creada con el folio <span className="font-semibold">{kameResponse.kameoneFolio}</span>, porque el folio con el que intentamos antes ya estaba tomado en Kame.
                    </p>
                  </div>
                )}
                {!kameResponse.kameoneFolio && (
                  <p className="font-sans font-normal text-sm text-text-subtle">
                    El pedido ha sido sincronizado exitosamente con el sistema KAME.
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-3 w-full">
                <Button
                  variant="primary"
                  fullWidth
                  onClick={() => {
                    setShowInvoiceOverlay(false);
                    setKameResponse(null);
                    closeAndAdvanceAfterSave();
                  }}
                >
                  Entendido
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      )}


      {/* Dialog de confirmación para cerrar modal de crear pedido */}
      <Dialog open={showCloseConfirmDialog} onOpenChange={setShowCloseConfirmDialog}>
        <DialogContent className="px-6 py-6">
          <DialogClose onClose={() => setShowCloseConfirmDialog(false)} />
          <DialogHeader>
            <DialogTitle>Salir sin guardar el pedido</DialogTitle>
          </DialogHeader>
          
          <div className="my-6">
            <p className="font-sans font-normal text-base text-foreground">
              Se perderán todos los datos ingresados.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowCloseConfirmDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="outline"
              className="border-destructive text-destructive hover:bg-transparent"
              onClick={confirmCloseCreateOrderModal}
            >
              Salir sin guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog de confirmación para descartar pedido */}
      <Dialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <DialogContent className="px-6 py-6">
          <DialogClose onClose={() => setShowDiscardDialog(false)} />
          <DialogHeader>
            <DialogTitle>Descartar pedido</DialogTitle>
          </DialogHeader>

          <div className="my-6">
            <p className="font-sans font-normal text-base text-foreground">
              ¿Está seguro que desea descartar este pedido? Esta acción no se puede deshacer.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowDiscardDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="outline"
              className="border-destructive text-destructive hover:bg-transparent"
              onClick={async () => {
                if (orderToDiscard) {
                  await handleDeleteOrder(orderToDiscard);
                  setShowSingleOrderModal(false);
                  setShowDiscardDialog(false);
                  setConsultingOrderId(null);
                  // Clear the :orderId from the URL so we land back on the list
                  // (and the urlOrderId effect doesn't try to reopen the deleted order).
                  if (urlOrderId) navigate('/orders');
                }
              }}
            >
              Descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden file input for "Cargar varias OCs" */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.xlsx,.xls,.csv"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0) startMultiPdfProcessing(files);
          e.target.value = '';
        }}
      />
    </div>
  );
};

export default OrdersTray;

